import React, { useState, useCallback, useEffect, useRef, useMemo, useContext } from 'react';
import { createPortal } from 'react-dom';
import { EmailBlockData } from '../ModernEmailBuilder';
import { SOCIAL_PLATFORMS, getPlatformData } from '../utils/socialIcons';
import ContentManagementModal from '../../ContentManagementModal';
import StockImageModal from '../../StockImageModal';
import { ImageLibraryModal } from '../ImageLibraryModal';
import KonvaImageEditor from '../KonvaImageEditor';
import { StorageFile } from '../../../types/storage';
import { useAuth } from '../../../contexts/AuthContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { storageService } from '../../../services/storageService';
import { brandingService, contactFieldService, ContactField } from '../../../services/firestore';
import {
  ChevronRightIcon,
  ChevronDownIcon,
  PaintBrushIcon,
  QueueListIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  PlusIcon,
  SparklesIcon,
  TrashIcon,
  ListBulletIcon,
  Bars3BottomLeftIcon,
  Bars3BottomRightIcon,
  Bars3Icon,
  ArrowUpIcon,
  ArrowDownIcon,
  MinusIcon,
  PhotoIcon,
  GifIcon,
  PlayIcon,
  ArrowsUpDownIcon,
  ArrowUpOnSquareIcon,
} from '@heroicons/react/24/outline';
import { giphyService, type GiphyImage } from '../utils/giphyService';
import { stickerService, type GiphySticker } from '../utils/stickerService';
import { resolveVideoThumbnailUrl } from '../utils/mediaBlockUtils';
import {
  FEATURES_GRID_LAYOUT_OPTIONS,
  getDefaultFeaturesForLayout,
  isLegacyIcon2x2FeaturesContent,
  normalizeFeaturesGridContent,
  resolveFeaturesGridVariant,
  usesPackIconLayout,
} from '../utils/featuresGridUtils';
import { FeatureIconPicker } from './FeatureIconPicker';
import { escapeHtml } from '../utils/helpers';
import {
  formatLetterSpacingPx,
  formatHeroOverlayLineHeight,
  formatLineHeightPercent,
  parseHeroOverlayLineHeight,
  parseLetterSpacingPx,
  parseLineHeightPercent,
  isEffectivelyZeroPadding,
} from '../utils/textBlockTypography';
import { CanvaImportGuideContext } from '../../../contexts/CanvaImportGuideContext';
import {
  type EditorialPropertyContext,
  HeroEditorialPropertiesPanel,
  HeroQuotePropertiesPanel,
  HeroCardExtraColorFields,
  ImageContentEditorialExtras,
  ProductEditorialPropertiesPanel,
} from './editorialBlockProperties';
import { HeaderPropertiesPanel } from './headerBlockProperties';
import { MenuPropertiesPanel } from './menuBlockProperties';
import { FooterPropertiesPanel } from './footerBlockProperties';
import {
  HeroContentPropertiesPanel,
  shouldUseHeroContentPropertiesPanel,
} from './heroBlockProperties';

/// <reference types="vite/client" />

const ImageSourceFromCanvaRow: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const ctx = useContext(CanvaImportGuideContext);
  if (!ctx) return null;
  return (
    <button
      type="button"
      onClick={() => {
        ctx.openGuide();
        onClose();
      }}
      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100"
    >
      <PaintBrushIcon className="w-4 h-4 text-slate-400" />
      <span className="font-medium">Use a design from Canva</span>
    </button>
  );
};

interface PropertiesPanelProps {
  block: EmailBlockData;
  onUpdate: (updates: Partial<EmailBlockData>) => void;
  onDone?: () => void;
  allBlocks?: EmailBlockData[];
}

type ContentManagerTab = 'image' | 'template' | 'file' | 'graphic' | 'media';

const DEFAULT_IMAGE_HEIGHT = '300px';
const PADDING_SIDE_KEYS = [
  { key: 'top', label: 'Top' },
  { key: 'right', label: 'Right' },
  { key: 'bottom', label: 'Bottom' },
  { key: 'left', label: 'Left' },
] as const;

// Default color palette - 66 colors in 6 rows x 11 columns
const DEFAULT_COLOR_PALETTE = [
  '#FFFFFF', '#F8FAFC', '#E2E8F0', '#94A3B8', '#475569', '#1E293B', '#000000', // Grayscale
  '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16', '#22C55E', // Warm & Green
  '#06B6D4', '#0F172A', '#3B82F6', '#6366F1', '#8B5CF6', '#EC4899', // Cool
];

const DIVIDER_STYLE_OPTIONS = [
  { value: 'solid', label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
  { value: 'double', label: 'Double' },
  { value: 'inset', label: 'Inset' },
  { value: 'outset', label: 'Outset' },
];

// Comprehensive font families organized by category
export const FONT_FAMILIES: { value: string; label: string; category: string }[] = [
  // Popular fonts - shown at the top
  { value: 'Inter', label: 'Inter', category: 'Popular' },
  { value: 'Roboto', label: 'Roboto', category: 'Popular' },
  { value: 'Open Sans', label: 'Open Sans', category: 'Popular' },
  { value: 'Lato', label: 'Lato', category: 'Popular' },
  { value: 'Poppins', label: 'Poppins', category: 'Popular' },
  { value: 'Montserrat', label: 'Montserrat', category: 'Popular' },
  { value: 'Arial', label: 'Arial', category: 'Popular' },
  { value: 'Helvetica', label: 'Helvetica', category: 'Popular' },

  // Sans-Serif fonts
  { value: 'Nunito', label: 'Nunito', category: 'Sans-Serif' },
  { value: 'Nunito Sans', label: 'Nunito Sans', category: 'Sans-Serif' },
  { value: 'Raleway', label: 'Raleway', category: 'Sans-Serif' },
  { value: 'Work Sans', label: 'Work Sans', category: 'Sans-Serif' },
  { value: 'DM Sans', label: 'DM Sans', category: 'Sans-Serif' },
  { value: 'Manrope', label: 'Manrope', category: 'Sans-Serif' },
  { value: 'Plus Jakarta Sans', label: 'Plus Jakarta Sans', category: 'Sans-Serif' },
  { value: 'Source Sans Pro', label: 'Source Sans Pro', category: 'Sans-Serif' },
  { value: 'Source Sans 3', label: 'Source Sans 3', category: 'Sans-Serif' },
  { value: 'PT Sans', label: 'PT Sans', category: 'Sans-Serif' },
  { value: 'Ubuntu', label: 'Ubuntu', category: 'Sans-Serif' },
  { value: 'Fira Sans', label: 'Fira Sans', category: 'Sans-Serif' },
  { value: 'Oxygen', label: 'Oxygen', category: 'Sans-Serif' },
  { value: 'Cantarell', label: 'Cantarell', category: 'Sans-Serif' },
  { value: 'Verdana', label: 'Verdana', category: 'Sans-Serif' },
  { value: 'Tahoma', label: 'Tahoma', category: 'Sans-Serif' },
  { value: 'Trebuchet MS', label: 'Trebuchet MS', category: 'Sans-Serif' },
  { value: 'Century Gothic', label: 'Century Gothic', category: 'Sans-Serif' },
  { value: 'Quicksand', label: 'Quicksand', category: 'Sans-Serif' },
  { value: 'Outfit', label: 'Outfit', category: 'Sans-Serif' },
  { value: 'Figtree', label: 'Figtree', category: 'Sans-Serif' },
  { value: 'Karla', label: 'Karla', category: 'Sans-Serif' },
  { value: 'Mulish', label: 'Mulish', category: 'Sans-Serif' },
  { value: 'Rubik', label: 'Rubik', category: 'Sans-Serif' },
  { value: 'Barlow', label: 'Barlow', category: 'Sans-Serif' },
  { value: 'Josefin Sans', label: 'Josefin Sans', category: 'Sans-Serif' },
  { value: 'Cabin', label: 'Cabin', category: 'Sans-Serif' },
  { value: 'Exo 2', label: 'Exo 2', category: 'Sans-Serif' },
  { value: 'Lexend', label: 'Lexend', category: 'Sans-Serif' },
  { value: 'Albert Sans', label: 'Albert Sans', category: 'Sans-Serif' },
  { value: 'Space Grotesk', label: 'Space Grotesk', category: 'Sans-Serif' },
  { value: 'Sora', label: 'Sora', category: 'Sans-Serif' },
  { value: 'Urbanist', label: 'Urbanist', category: 'Sans-Serif' },

  // Serif fonts
  { value: 'Merriweather', label: 'Merriweather', category: 'Serif' },
  { value: 'Playfair Display', label: 'Playfair Display', category: 'Serif' },
  { value: 'Lora', label: 'Lora', category: 'Serif' },
  { value: 'PT Serif', label: 'PT Serif', category: 'Serif' },
  { value: 'Source Serif Pro', label: 'Source Serif Pro', category: 'Serif' },
  { value: 'Libre Baskerville', label: 'Libre Baskerville', category: 'Serif' },
  { value: 'EB Garamond', label: 'EB Garamond', category: 'Serif' },
  { value: 'Crimson Text', label: 'Crimson Text', category: 'Serif' },
  { value: 'Cormorant Garamond', label: 'Cormorant Garamond', category: 'Serif' },
  { value: 'Times New Roman', label: 'Times New Roman', category: 'Serif' },
  { value: 'Georgia', label: 'Georgia', category: 'Serif' },
  { value: 'Palatino', label: 'Palatino', category: 'Serif' },
  { value: 'Garamond', label: 'Garamond', category: 'Serif' },
  { value: 'Bookman', label: 'Bookman', category: 'Serif' },
  { value: 'Bitter', label: 'Bitter', category: 'Serif' },
  { value: 'Spectral', label: 'Spectral', category: 'Serif' },
  { value: 'Noto Serif', label: 'Noto Serif', category: 'Serif' },
  { value: 'Vollkorn', label: 'Vollkorn', category: 'Serif' },
  { value: 'Cardo', label: 'Cardo', category: 'Serif' },
  { value: 'Literata', label: 'Literata', category: 'Serif' },

  // Display fonts
  { value: 'Bebas Neue', label: 'Bebas Neue', category: 'Display' },
  { value: 'Oswald', label: 'Oswald', category: 'Display' },
  { value: 'Righteous', label: 'Righteous', category: 'Display' },
  { value: 'Anton', label: 'Anton', category: 'Display' },
  { value: 'Impact', label: 'Impact', category: 'Display' },
  { value: 'Fredoka One', label: 'Fredoka One', category: 'Display' },
  { value: 'Titan One', label: 'Titan One', category: 'Display' },
  { value: 'Bangers', label: 'Bangers', category: 'Display' },
  { value: 'Alfa Slab One', label: 'Alfa Slab One', category: 'Display' },
  { value: 'Bungee', label: 'Bungee', category: 'Display' },
  { value: 'Shrikhand', label: 'Shrikhand', category: 'Display' },
  { value: 'Fugaz One', label: 'Fugaz One', category: 'Display' },

  // Handwriting fonts
  { value: 'Permanent Marker', label: 'Permanent Marker', category: 'Handwriting' },
  { value: 'Comic Sans MS', label: 'Comic Sans MS', category: 'Handwriting' },
  { value: 'Patrick Hand', label: 'Patrick Hand', category: 'Handwriting' },
  { value: 'Caveat', label: 'Caveat', category: 'Handwriting' },
  { value: 'Indie Flower', label: 'Indie Flower', category: 'Handwriting' },
  { value: 'Kalam', label: 'Kalam', category: 'Handwriting' },
  { value: 'Shadows Into Light', label: 'Shadows Into Light', category: 'Handwriting' },
  { value: 'Architects Daughter', label: 'Architects Daughter', category: 'Handwriting' },
  { value: 'Handlee', label: 'Handlee', category: 'Handwriting' },
  { value: 'Gloria Hallelujah', label: 'Gloria Hallelujah', category: 'Handwriting' },
  { value: 'Neucha', label: 'Neucha', category: 'Handwriting' },
  { value: 'Rock Salt', label: 'Rock Salt', category: 'Handwriting' },
  { value: 'Reenie Beanie', label: 'Reenie Beanie', category: 'Handwriting' },
  { value: 'Coming Soon', label: 'Coming Soon', category: 'Handwriting' },

  // Cursive fonts
  { value: 'Dancing Script', label: 'Dancing Script', category: 'Cursive' },
  { value: 'Great Vibes', label: 'Great Vibes', category: 'Cursive' },
  { value: 'Pacifico', label: 'Pacifico', category: 'Cursive' },
  { value: 'Satisfy', label: 'Satisfy', category: 'Cursive' },
  { value: 'Sacramento', label: 'Sacramento', category: 'Cursive' },
  { value: 'Lobster', label: 'Lobster', category: 'Cursive' },
  { value: 'Allura', label: 'Allura', category: 'Cursive' },
  { value: 'Alex Brush', label: 'Alex Brush', category: 'Cursive' },
  { value: 'Tangerine', label: 'Tangerine', category: 'Cursive' },
  { value: 'Pinyon Script', label: 'Pinyon Script', category: 'Cursive' },
  { value: 'Rouge Script', label: 'Rouge Script', category: 'Cursive' },
  { value: 'Petit Formal Script', label: 'Petit Formal Script', category: 'Cursive' },
  { value: 'Italianno', label: 'Italianno', category: 'Cursive' },
  { value: 'Mrs Saint Delafield', label: 'Mrs Saint Delafield', category: 'Cursive' },
  { value: 'Marck Script', label: 'Marck Script', category: 'Cursive' },

  // Luxury fonts
  { value: 'Abril Fatface', label: 'Abril Fatface', category: 'Luxury' },
  { value: 'Didact Gothic', label: 'Didact Gothic', category: 'Luxury' },
  { value: 'Cinzel', label: 'Cinzel', category: 'Luxury' },
  { value: 'Cinzel Decorative', label: 'Cinzel Decorative', category: 'Luxury' },
  { value: 'Yeseva One', label: 'Yeseva One', category: 'Luxury' },
  { value: 'Bodoni Moda', label: 'Bodoni Moda', category: 'Luxury' },
  { value: 'Tenor Sans', label: 'Tenor Sans', category: 'Luxury' },
  { value: 'Forum', label: 'Forum', category: 'Luxury' },
  { value: 'Poiret One', label: 'Poiret One', category: 'Luxury' },
  { value: 'Cormorant', label: 'Cormorant', category: 'Luxury' },
  { value: 'Cormorant Upright', label: 'Cormorant Upright', category: 'Luxury' },
  { value: 'Antic Didone', label: 'Antic Didone', category: 'Luxury' },
  { value: 'Oranienbaum', label: 'Oranienbaum', category: 'Luxury' },
  { value: 'Marcellus', label: 'Marcellus', category: 'Luxury' },

  // Decorative fonts
  { value: 'Press Start 2P', label: 'Press Start 2P', category: 'Decorative' },
  { value: 'Monoton', label: 'Monoton', category: 'Decorative' },
  { value: 'Rubik Mono One', label: 'Rubik Mono One', category: 'Decorative' },
  { value: 'Faster One', label: 'Faster One', category: 'Decorative' },
  { value: 'Bungee Shade', label: 'Bungee Shade', category: 'Decorative' },
  { value: 'Silkscreen', label: 'Silkscreen', category: 'Decorative' },
  { value: 'Rubik Glitch', label: 'Rubik Glitch', category: 'Decorative' },
  { value: 'Rubik Wet Paint', label: 'Rubik Wet Paint', category: 'Decorative' },
  { value: 'Bungee Inline', label: 'Bungee Inline', category: 'Decorative' },
  { value: 'Fascinate', label: 'Fascinate', category: 'Decorative' },
  { value: 'Creepster', label: 'Creepster', category: 'Decorative' },
  { value: 'Nosifer', label: 'Nosifer', category: 'Decorative' },

  // Monospace fonts
  { value: 'Fira Code', label: 'Fira Code', category: 'Monospace' },
  { value: 'JetBrains Mono', label: 'JetBrains Mono', category: 'Monospace' },
  { value: 'Source Code Pro', label: 'Source Code Pro', category: 'Monospace' },
  { value: 'Roboto Mono', label: 'Roboto Mono', category: 'Monospace' },
  { value: 'IBM Plex Mono', label: 'IBM Plex Mono', category: 'Monospace' },
  { value: 'Space Mono', label: 'Space Mono', category: 'Monospace' },
  { value: 'Courier New', label: 'Courier New', category: 'Monospace' },
  { value: 'Lucida Console', label: 'Lucida Console', category: 'Monospace' },
  { value: 'Monaco', label: 'Monaco', category: 'Monospace' },
  { value: 'Consolas', label: 'Consolas', category: 'Monospace' },

  // System fonts
  { value: 'system-ui', label: 'System UI', category: 'System' },
  { value: '-apple-system', label: 'Apple System', category: 'System' },
  { value: 'BlinkMacSystemFont', label: 'BlinkMacSystemFont', category: 'System' },
  { value: 'Segoe UI', label: 'Segoe UI', category: 'System' },
];

const extractYouTubeId = (url: string): string | null => {
  try {
    const patterns = [
      /youtu\.be\/([A-Za-z0-9_-]{11})/,
      /youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/,
      /youtube\.com\/watch\?v=([A-Za-z0-9_-]{11})/,
      /youtube\.com\/embed\/([A-Za-z0-9_-]{11})/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) return match[1];
    }
    const urlObj = new URL(url);
    const v = urlObj.searchParams.get('v');
    if (v && v.length === 11) return v;
  } catch (_) {
    // Ignore invalid URLs
  }
  return null;
};

const DEFAULT_MERGE_TAGS: { label: string; mergeTag: string }[] = [
  { label: 'First Name', mergeTag: '{{first_name}}' },
  { label: 'Last Name', mergeTag: '{{last_name}}' },
  { label: 'Email', mergeTag: '{{email}}' },
  { label: 'Company Name', mergeTag: '{{company_name}}' },
  { label: 'Website URL', mergeTag: '{{website_url}}' },
  { label: 'Unsubscribe Link', mergeTag: '{{unsubscribe_url}}' },
];

// Social Media Brand Colors
// Social Media Icon Component
const SocialMediaIcon: React.FC<{ 
  platform: string; 
  className?: string;
  styleOverride?: string;
  colorOverride?: string;
}> = ({ platform, className = 'w-5 h-5', styleOverride, colorOverride }) => {
  const platformData = getPlatformData(platform);
  const brandColor = platformData?.color || '#64748B';
  const effectiveStyle = styleOverride || 'solid';
  const isOutline = effectiveStyle.includes('outline');
  const isBrand = effectiveStyle.includes('brand');
  const isCircle = effectiveStyle.includes('circle');
  const isSquare = effectiveStyle.includes('square');

  const iconColor = colorOverride || (isBrand ? brandColor : 'currentColor');
  
  const iconProps = { 
    viewBox: platformData?.viewBox || '0 0 24 24', 
    fill: isOutline ? 'none' : iconColor,
    stroke: isOutline ? iconColor : 'none',
    strokeWidth: isOutline ? 2 : 0,
    xmlns: "http://www.w3.org/2000/svg"
  };

  const renderIconPath = () => {
    if (platformData) {
      return <path d={platformData.path} />;
    }
    return <circle cx="12" cy="12" r="10" />;
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    backgroundColor: isCircle || isSquare ? (isOutline ? 'transparent' : iconColor) : 'transparent',
    borderRadius: isCircle ? '50%' : isSquare ? '4px' : '0',
    border: isOutline && (isCircle || isSquare) ? `2.5px solid ${iconColor}` : 'none',
    overflow: 'hidden'
  };

  if (isCircle || isSquare) {
    const shapeIconColor = isOutline ? iconColor : (platform === 'snapchat' ? '#000000' : '#FFFFFF');
    return (
      <div className={className} style={containerStyle}>
        <svg 
          viewBox={platformData?.viewBox || "0 0 24 24"} 
          fill={isOutline ? 'none' : shapeIconColor} 
          stroke={isOutline ? shapeIconColor : 'none'}
          strokeWidth={isOutline ? 2 : 0}
          className="w-full h-full p-[18%]"
          xmlns="http://www.w3.org/2000/svg"
        >
          {renderIconPath()}
        </svg>
      </div>
    );
  }

  return (
    <svg {...iconProps} className={className}>
      {renderIconPath()}
    </svg>
  );
};

export interface PaddingControlProps {
  label?: string;
  value?: string; // Standard CSS padding string
  values?: {
    top?: string | number;
    right?: string | number;
    bottom?: string | number;
    left?: string | number;
    padding?: string | number;
  };
  onChange?: (a: string, b?: string) => void; // Polymorphic: (val) => void OR (key, val) => void
  onMultiChange?: (updates: Record<string, string>) => void;
}

export const PaddingControl: React.FC<PaddingControlProps> = ({
  label = "Padding",
  value: singleValue,
  values: objectValues,
  onChange,
  onMultiChange
}) => {
  const [isAdvanced, setIsAdvanced] = useState(false);

  // Helper to parse CSS padding string into 4 components
  const parsePaddingString = (str: string) => {
    if (!str || typeof str !== 'string') return { top: 0, right: 0, bottom: 0, left: 0 };
    const parts = str.trim().split(/\s+/).map(p => parseInt(p.replace('px', ''), 10) || 0);
    
    if (parts.length === 1) return { top: parts[0], right: parts[0], bottom: parts[0], left: parts[0] };
    if (parts.length === 2) return { top: parts[0], right: parts[1], bottom: parts[0], left: parts[1] };
    if (parts.length === 3) return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[1] };
    if (parts.length === 4) return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[3] };
    return { top: 0, right: 0, bottom: 0, left: 0 };
  };

  // Helper to serialize 4 components into CSS padding string
  const serializePadding = (t: number, r: number, b: number, l: number) => {
    if (t === r && r === b && b === l) return `${t}px`;
    if (t === b && r === l) return `${t}px ${r}px`;
    return `${t}px ${r}px ${b}px ${l}px`;
  };

  // Initial values based on either singleValue or objectValues
  const initialValues = useMemo(() => {
    if (singleValue !== undefined) {
      return parsePaddingString(singleValue);
    }
    if (objectValues) {
      const parseVal = (v: any) => {
        if (typeof v === 'number') return v;
        if (typeof v === 'string') return parseInt(v.replace('px', ''), 10) || 0;
        return 0;
      };
      const shorthandVal = parseVal(objectValues.padding);
      return {
        top: objectValues.top !== undefined ? parseVal(objectValues.top) : shorthandVal,
        right: objectValues.right !== undefined ? parseVal(objectValues.right) : shorthandVal,
        bottom: objectValues.bottom !== undefined ? parseVal(objectValues.bottom) : shorthandVal,
        left: objectValues.left !== undefined ? parseVal(objectValues.left) : shorthandVal
      };
    }
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }, [singleValue, objectValues]);

  const { top, right, bottom, left } = initialValues;
  const horizontal = left === right ? left : left;
  const vertical = top === bottom ? top : top;

  const performUpdate = (updates: { top?: number; right?: number; bottom?: number; left?: number }) => {
    const next = { top, right, bottom, left, ...updates };
    
    // If we have a singleValue, we update as a string
    if (singleValue !== undefined) {
      const serialized = serializePadding(next.top, next.right, next.bottom, next.left);
      if (typeof onChange === 'function') {
        onChange(serialized);
      }
      return;
    }

    // Otherwise, we update as discrete keys (paddingTop, etc.)
    // IMPORTANT: We must also clear any existing shorthand 'padding' property to avoid conflicts
    if (onMultiChange) {
      const multiUpdates: Record<string, any> = { padding: undefined };
      if (updates.top !== undefined || (objectValues && objectValues.top === undefined)) multiUpdates.paddingTop = `${next.top}px`;
      if (updates.right !== undefined || (objectValues && objectValues.right === undefined)) multiUpdates.paddingRight = `${next.right}px`;
      if (updates.bottom !== undefined || (objectValues && objectValues.bottom === undefined)) multiUpdates.paddingBottom = `${next.bottom}px`;
      if (updates.left !== undefined || (objectValues && objectValues.left === undefined)) multiUpdates.paddingLeft = `${next.left}px`;
      onMultiChange(multiUpdates);
    } else if (typeof onChange === 'function') {
      // Clear shorthand first if it's the first time we're doing discrete updates
      if (objectValues?.padding) onChange('padding', undefined as any);
      
      if (updates.top !== undefined) onChange('paddingTop', `${updates.top}px`);
      if (updates.right !== undefined) onChange('paddingRight', `${updates.right}px`);
      if (updates.bottom !== undefined) onChange('paddingBottom', `${updates.bottom}px`);
      if (updates.left !== undefined) onChange('paddingLeft', `${updates.left}px`);
    }
  };

  return (
    <div className="space-y-3.5">
      <style>{`
        /* Hide number input arrows */
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
      <div className="flex items-center justify-between">
        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{label}</label>
        <button
          type="button"
          onClick={() => setIsAdvanced(!isAdvanced)}
          className={`p-1.5 rounded-lg transition-all duration-200 ${isAdvanced ? 'bg-slate-900 text-white shadow-md shadow-slate-200' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
          title={isAdvanced ? "Simple mode" : "Individual sides"}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            {isAdvanced ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            ) : (
              <rect x="5" y="5" width="14" height="14" rx="2" strokeDasharray="4 3" />
            )}
          </svg>
        </button>
      </div>

      {!isAdvanced ? (
        <div className="grid grid-cols-2 gap-2.5">
          {/* Horizontal Padding */}
          <div className="group relative flex items-center bg-white border border-slate-100 rounded-xl px-3 py-2.5 transition-all hover:border-slate-200 focus-within:ring-4 focus-within:ring-slate-900/5 focus-within:border-slate-400 shadow-sm overflow-hidden min-h-[44px]">
            <div className="flex items-center gap-1 mr-3 opacity-20 group-focus-within:opacity-100 transition-opacity">
              <div className="w-[1.5px] h-3.5 bg-slate-900" />
              <div className="w-[1.5px] h-3.5 bg-slate-900" />
            </div>
            <div className="flex-1 min-w-0">
              <input
                type="number"
                value={horizontal}
                onChange={(e) => performUpdate({ left: parseInt(e.target.value, 10) || 0, right: parseInt(e.target.value, 10) || 0 })}
                className="w-full bg-transparent text-[14px] font-bold text-slate-800 outline-none tabular-nums border-none ring-0 p-0 text-center"
                placeholder="0"
              />
            </div>
            <span className="text-[10px] font-bold text-slate-300 ml-1.5 uppercase">px</span>
          </div>

          {/* Vertical Padding */}
          <div className="group relative flex items-center bg-white border border-slate-100 rounded-xl px-3 py-2.5 transition-all hover:border-slate-200 focus-within:ring-4 focus-within:ring-slate-900/5 focus-within:border-slate-400 shadow-sm overflow-hidden min-h-[44px]">
            <div className="flex flex-col gap-1 mr-3 opacity-20 group-focus-within:opacity-100 transition-opacity">
              <div className="h-[1.5px] w-3.5 bg-slate-900" />
              <div className="h-[1.5px] w-3.5 bg-slate-900" />
            </div>
            <div className="flex-1 min-w-0">
              <input
                type="number"
                value={vertical}
                onChange={(e) => performUpdate({ top: parseInt(e.target.value, 10) || 0, bottom: parseInt(e.target.value, 10) || 0 })}
                className="w-full bg-transparent text-[14px] font-bold text-slate-800 outline-none tabular-nums border-none ring-0 p-0 text-center"
                placeholder="0"
              />
            </div>
            <span className="text-[10px] font-bold text-slate-300 ml-1.5 uppercase">px</span>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {[
            { key: 'top', icon: <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" /> },
            { key: 'bottom', icon: <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /> },
            { key: 'left', icon: <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /> },
            { key: 'right', icon: <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /> }
          ].map((side) => {
            const sideVal = side.key === 'top' ? top : side.key === 'right' ? right : side.key === 'bottom' ? bottom : left;
            return (
              <div key={side.key} className="group relative flex items-center bg-white border border-slate-100 rounded-xl px-2.5 py-2 transition-all hover:border-slate-200 focus-within:ring-4 focus-within:ring-slate-900/5 focus-within:border-slate-400 shadow-sm overflow-hidden">
                <div className="mr-2 text-slate-200 group-focus-within:text-slate-900 transition-colors">
                  <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={4}>
                    {side.icon}
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <input
                    type="number"
                    value={sideVal}
                    onChange={(e) => performUpdate({ [side.key]: parseInt(e.target.value, 10) || 0 })}
                    className="w-full bg-transparent text-[13px] font-bold text-slate-800 outline-none tabular-nums border-none ring-0 p-0 text-center"
                    placeholder="0"
                  />
                </div>
                <span className="text-[9px] font-bold text-slate-200 uppercase ml-1">px</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  action?: React.ReactNode;
  subtitle?: string;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, children, defaultOpen = true, action, subtitle }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="mb-0 last:mb-0 transition-all duration-200">
      <div
        className="flex items-center justify-between py-2.5 cursor-pointer select-none hover:bg-black/[0.02] px-1 transition-colors duration-150"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-col min-w-0">
          <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-[0.07em] truncate">{title}</span>
          {subtitle && <span className="text-[11px] text-slate-400 truncate font-normal mt-0.5">{subtitle}</span>}
        </div>
        <div className="flex items-center gap-3">
          {action && (
            <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
              {action}
            </div>
          )}
          <ChevronRightIcon
            className={`w-3 h-3 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
          />
        </div>
      </div>
      {isOpen && (
        <div className="pt-3 pb-5 px-1 animate-in slide-in-from-top-1 duration-200">
          {children}
        </div>
      )}
    </div>
  );
};

export interface CustomColorPickerProps {
  currentColor: string;
  onChange: (color: string) => void;
  pickerType: string;
  emailColors: string[];
  brandColors: { primary: string; secondary: string[] };
  showAdvancedPicker: string | null;
  setShowAdvancedPicker: (type: string | null) => void;
  setAdvancedPickerPosition: (pos: { top: number; left: number } | null) => void;
}

export const CustomColorPicker: React.FC<CustomColorPickerProps> = ({
  currentColor,
  onChange,
  pickerType,
  emailColors,
  brandColors,
  showAdvancedPicker,
  setShowAdvancedPicker,
  setAdvancedPickerPosition
}) => {
  const allBrandColors = [brandColors.primary, ...brandColors.secondary].filter(Boolean);
  const isSelected = (color: string) => {
    const normalized = color.toLowerCase().trim();
    const current = currentColor?.toLowerCase().trim();
    return normalized === current ||
      (normalized === 'transparent' && (!current || current === 'transparent' || current === 'rgba(0,0,0,0)'));
  };

  const [activeTab, setActiveTab] = useState<'solid' | 'gradient'>('solid');
  const [gradientAngle, setGradientAngle] = useState(180);
  const [gradientStop1, setGradientStop1] = useState('#ffffff');
  const [gradientStop2, setGradientStop2] = useState('#000000');
  const [hexInput, setHexInput] = useState('');
  const [opacity, setOpacity] = useState(100);

  // Helpers ──────────────────────────────────────────────────────────────────
  const colorToHex = (c: string): string => {
    if (!c || c === 'transparent') return '#000000';
    if (c.startsWith('#')) return c.slice(0, 7);
    const m = c.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (m) {
      return '#' + [m[1], m[2], m[3]].map(n => parseInt(n).toString(16).padStart(2, '0')).join('');
    }
    return '#000000';
  };

  const colorToOpacity = (c: string): number => {
    const m = c.match(/rgba\(\d+,\s*\d+,\s*\d+,\s*([\d.]+)\)/);
    if (m) return Math.round(parseFloat(m[1]) * 100);
    return 100;
  };

  const hexAndOpacityToColor = (hex: string, op: number): string => {
    if (op >= 100) return hex;
    const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    if (!m) return hex;
    return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${(op / 100).toFixed(2)})`;
  };

  // Sync state when currentColor changes ────────────────────────────────────
  useEffect(() => {
    if (!currentColor || currentColor === 'transparent') {
      setHexInput('#000000');
      setOpacity(100);
      setActiveTab('solid');
      return;
    }
    if (currentColor.includes('gradient')) {
      setActiveTab('gradient');
      const match = currentColor.match(/(\d+)deg/);
      if (match) setGradientAngle(parseInt(match[1]));
      const colors = currentColor.match(/#[a-fA-F0-9]{6}|#[a-fA-F0-9]{3}|rgba?\([^)]+\)/g);
      if (colors && colors.length >= 2) { setGradientStop1(colors[0]); setGradientStop2(colors[1]); }
      return;
    }
    setActiveTab('solid');
    setHexInput(colorToHex(currentColor));
    setOpacity(colorToOpacity(currentColor));
  }, [currentColor]);

  const handleHexChange = (raw: string) => {
    setHexInput(raw);
    const clean = raw.startsWith('#') ? raw : `#${raw}`;
    if (/^#[0-9a-fA-F]{6}$/.test(clean)) {
      onChange(hexAndOpacityToColor(clean, opacity));
    }
  };

  const handleOpacityChange = (op: number) => {
    setOpacity(op);
    const hex = colorToHex(currentColor);
    onChange(hexAndOpacityToColor(hex, op));
  };

  const updateGradient = (angle: number, s1: string, s2: string) => {
    onChange(`linear-gradient(${angle}deg, ${s1} 0%, ${s2} 100%)`);
  };

  const currentHex = colorToHex(currentColor);
  const allSwatches = Array.from(new Set([...emailColors, ...allBrandColors])).filter(Boolean).slice(0, 14);

  return (
    <div className="bg-white border border-black/[0.1] rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.12)] overflow-hidden w-[220px]">
      {/* Tabs */}
      <div className="flex border-b border-black/[0.07]">
        {(['solid', 'gradient'] as const).map(tab => (
          <button
            key={tab}
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2.5 text-[11px] font-semibold tracking-wide uppercase transition-colors ${
              activeTab === tab
                ? 'text-slate-900 bg-white'
                : 'text-slate-400 hover:text-slate-600 bg-slate-50/60'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="p-3 space-y-3">
        {activeTab === 'solid' && (
          <>
            {/* Native colour canvas */}
            <div className="relative">
              <input
                type="color"
                value={currentHex}
                onChange={e => {
                  setHexInput(e.target.value);
                  onChange(hexAndOpacityToColor(e.target.value, opacity));
                }}
                className="w-full h-24 rounded-lg cursor-pointer border border-black/[0.08] p-0 block"
                style={{ appearance: 'none', WebkitAppearance: 'none' }}
              />
            </div>

            {/* Hex + Opacity row */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 flex-1 bg-slate-50 border border-black/[0.08] rounded-md px-2 py-1.5">
                <span className="text-[10px] text-slate-400 font-bold">#</span>
                <input
                  type="text"
                  value={hexInput.replace('#', '')}
                  onChange={e => handleHexChange('#' + e.target.value)}
                  maxLength={6}
                  className="flex-1 text-[11px] font-mono text-slate-800 bg-transparent focus:outline-none min-w-0"
                  spellCheck={false}
                />
              </div>
              <div className="flex items-center gap-1.5 w-16 bg-slate-50 border border-black/[0.08] rounded-md px-2 py-1.5">
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={opacity}
                  onChange={e => handleOpacityChange(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                  className="w-full text-[11px] font-mono text-slate-800 bg-transparent focus:outline-none"
                />
                <span className="text-[10px] text-slate-400">%</span>
              </div>
            </div>

            {/* Opacity slider */}
            <div className="relative h-2.5">
              {/* Checkerboard behind */}
              <div className="absolute inset-0 rounded-full overflow-hidden"
                style={{ backgroundImage: 'repeating-conic-gradient(#ddd 0% 25%, #fff 0% 50%)', backgroundSize: '6px 6px' }} />
              {/* Colour gradient overlay */}
              <div className="absolute inset-0 rounded-full" style={{ background: `linear-gradient(to right, transparent, ${currentHex})` }} />
              <input
                type="range" min={0} max={100} value={opacity}
                onChange={e => handleOpacityChange(parseInt(e.target.value))}
                className="absolute inset-0 w-full opacity-0 cursor-pointer h-full"
              />
              {/* Thumb indicator */}
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border-2 border-white shadow-sm pointer-events-none"
                style={{ left: `calc(${opacity}% - 7px)`, backgroundColor: currentHex }}
              />
            </div>
          </>
        )}

        {activeTab === 'gradient' && (
          <div className="space-y-2.5">
            {/* Preview */}
            <div className="h-10 rounded-lg w-full border border-black/[0.08]"
              style={{ background: `linear-gradient(${gradientAngle}deg, ${gradientStop1} 0%, ${gradientStop2} 100%)` }} />
            {/* Angle */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-500 w-10 flex-shrink-0">Angle</span>
              <input type="range" min={0} max={360} value={gradientAngle}
                onChange={e => { const v = parseInt(e.target.value); setGradientAngle(v); updateGradient(v, gradientStop1, gradientStop2); }}
                className="v2-slider flex-1 h-1"
                style={{ background: `linear-gradient(90deg, #0F172A ${(gradientAngle/360)*100}%, #E2E8F0 ${(gradientAngle/360)*100}%)` }}
              />
              <span className="text-[10px] text-slate-500 w-7 text-right">{gradientAngle}°</span>
            </div>
            {/* Stops */}
            {[{ label: 'Start', val: gradientStop1, set: (v: string) => { setGradientStop1(v); updateGradient(gradientAngle, v, gradientStop2); } },
              { label: 'End',   val: gradientStop2, set: (v: string) => { setGradientStop2(v); updateGradient(gradientAngle, gradientStop1, v); } }
            ].map(({ label, val, set }) => (
              <div key={label} className="flex items-center gap-2">
                <span className="text-[10px] text-slate-500 w-10 flex-shrink-0">{label}</span>
                <input type="color" value={val} onChange={e => set(e.target.value)}
                  className="w-7 h-7 rounded-md border border-black/[0.08] cursor-pointer p-0.5 flex-shrink-0" />
                <input type="text" value={val} onChange={e => set(e.target.value)}
                  className="flex-1 text-[11px] font-mono text-slate-700 bg-slate-50 border border-black/[0.08] rounded-md px-2 py-1 focus:outline-none" />
              </div>
            ))}
          </div>
        )}

        {/* Transparent + colour swatches */}
        <div className="pt-2 border-t border-black/[0.06]">
          <div className="flex flex-wrap gap-1.5">
            {/* Transparent */}
            <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => onChange('transparent')}
              className={`w-5 h-5 rounded border transition-all flex-shrink-0 ${isSelected('transparent') ? 'ring-2 ring-slate-700 ring-offset-1' : 'border-black/[0.12] hover:border-black/30'}`}
              style={{ backgroundImage: 'repeating-conic-gradient(#ddd 0% 25%, #fff 0% 50%)', backgroundSize: '6px 6px' }}
              title="Transparent" />
            {/* Swatches */}
            {allSwatches.map((color, i) => (
              <button key={i} type="button" onMouseDown={e => e.preventDefault()} onClick={() => onChange(color)}
                className={`w-5 h-5 rounded border transition-all flex-shrink-0 ${isSelected(color) ? 'ring-2 ring-slate-700 ring-offset-1' : 'border-black/[0.08] hover:border-black/30 hover:scale-110'}`}
                style={{ backgroundColor: color }} title={color} />
            ))}
            {/* Default palette */}
            {DEFAULT_COLOR_PALETTE.map((color) => (
              <button key={color} type="button" onMouseDown={e => e.preventDefault()} onClick={() => onChange(color)}
                className={`w-5 h-5 rounded border transition-all flex-shrink-0 ${isSelected(color) ? 'ring-2 ring-slate-700 ring-offset-1' : 'border-black/[0.08] hover:border-black/30 hover:scale-110'}`}
                style={{ backgroundColor: color }} title={color} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({ block, onUpdate, onDone, allBlocks = [] }) => {
  const [showColorPicker, setShowColorPicker] = useState<string | null>(null);
  const [showAdvancedPicker, setShowAdvancedPicker] = useState<string | null>(null);
  const [advancedPickerPosition, setAdvancedPickerPosition] = useState<{ top: number; left: number } | null>(null);
  const [imageActionOpen, setImageActionOpen] = useState(true);
  const [logoActionOpen, setLogoActionOpen] = useState(true);
  const [showFontFamilyDropdown, setShowFontFamilyDropdown] = useState(false);
  const [showProductFontFamilyDropdown, setShowProductFontFamilyDropdown] = useState(false);
  const [showMergeTagDropdown, setShowMergeTagDropdown] = useState(false);
  const [mergeTags, setMergeTags] = useState<{ label: string; mergeTag: string }[]>(DEFAULT_MERGE_TAGS);
  const [imageLayoutOpen, setImageLayoutOpen] = useState(true);
  const [imagePaddingAdvanced, setImagePaddingAdvanced] = useState(false);
  const [isContentManagerOpen, setIsContentManagerOpen] = useState(false);
  const [isStockImageModalOpen, setIsStockImageModalOpen] = useState(false);
  const [isImageLibraryOpen, setIsImageLibraryOpen] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingButtonFile, setIsUploadingButtonFile] = useState(false);
  const [isImageEditorOpen, setIsImageEditorOpen] = useState(false);
  const [imageEditorInitialSrc, setImageEditorInitialSrc] = useState<string | null>(null);
  const [isSavingEditedImage, setIsSavingEditedImage] = useState(false);
  const [isImageSourceDropOpen, setIsImageSourceDropOpen] = useState(false);
  const imageSourceDropRef = useRef<HTMLDivElement | null>(null);
  const [contentManagerTab, setContentManagerTab] = useState<ContentManagerTab>('image');
  const [showDividerStyleDropdown, setShowDividerStyleDropdown] = useState(false);
  const [videoPreviewState, setVideoPreviewState] = useState<{ url: string | null; status: 'idle' | 'loading' | 'success' | 'error' }>({
    url: null,
    status: 'idle',
  });
  const [showPlatformPicker, setShowPlatformPicker] = useState<number | null>(null);
  const [expandedStyleIndex, setExpandedStyleIndex] = useState<number | null>(null);
  const [giphySearchTerm, setGiphySearchTerm] = useState('');
  const [showGiphyResults, setShowGiphyResults] = useState(false);
  const [giphyCategory, setGiphyCategory] = useState<'all' | 'trending' | 'reactions' | 'memes'>('all');
  const [giphyGifs, setGiphyGifs] = useState<GiphyImage[]>([]);
  const [giphyLoading, setGiphyLoading] = useState(false);
  const [stickerGiphySearch, setStickerGiphySearch] = useState('');
  const [stickerGiphyResults, setStickerGiphyResults] = useState<GiphySticker[]>([]);
  const [stickerGiphyLoading, setStickerGiphyLoading] = useState(false);
  const [carouselUploadIndex, setCarouselUploadIndex] = useState<number | null>(null);
  const [carouselEditingIndex, setCarouselEditingIndex] = useState<number | null>(null);
  const [openProductImageIndex, setOpenProductImageIndex] = useState<number | null>(null);
  const productImageDropRef = useRef<HTMLDivElement | null>(null);

  // Fetch GIFs for giphy block only
  useEffect(() => {
    if (block.type !== 'giphy') return;

    const fetchGifs = async () => {
      setGiphyLoading(true);
      try {
        let results: GiphyImage[] = [];

        if (giphySearchTerm.trim()) {
          results = await giphyService.search(giphySearchTerm);
        } else if (giphyCategory === 'trending' || giphyCategory === 'all') {
          results = await giphyService.getTrending();
        } else {
          results = await giphyService.search(giphyCategory);
        }

        setGiphyGifs(results);
      } catch (error) {
        console.error('Failed to fetch GIFs:', error);
      } finally {
        setGiphyLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchGifs, 500);
    return () => clearTimeout(timeoutId);
  }, [block.type, giphySearchTerm, giphyCategory]);

  // Fetch GIPHY stickers for sticker block animated tab
  useEffect(() => {
    if (block.type !== 'sticker' || block.content.stickerType !== 'giphy') return;

    const fetchStickers = async () => {
      setStickerGiphyLoading(true);
      try {
        const results = stickerGiphySearch.trim()
          ? await stickerService.searchStickers(stickerGiphySearch)
          : await stickerService.getTrendingStickers();
        setStickerGiphyResults(results);
      } catch (error) {
        console.error('Failed to fetch stickers:', error);
        setStickerGiphyResults([]);
      } finally {
        setStickerGiphyLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchStickers, 500);
    return () => clearTimeout(timeoutId);
  }, [block.type, block.content.stickerType, stickerGiphySearch]);
  const [isGenerating, setIsGenerating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const carouselFileInputRef = useRef<HTMLInputElement | null>(null);
  const buttonFileInputRef = useRef<HTMLInputElement | null>(null);

  const colorPickerRef = useRef<HTMLDivElement | null>(null);
  const fontFamilyDropdownRef = useRef<HTMLDivElement | null>(null);
  const productFontFamilyDropdownRef = useRef<HTMLDivElement | null>(null);
  const mergeTagDropdownRef = useRef<HTMLDivElement | null>(null);
  const mergeTagTriggerRef = useRef<HTMLDivElement | null>(null);
  const dividerStyleDropdownRef = useRef<HTMLDivElement | null>(null);
  const richTextInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const richTextSelectionRef = useRef<{ start: number; end: number } | null>(null);
  const setRichTextInputRef = useCallback((node: HTMLInputElement | HTMLTextAreaElement | null) => {
    richTextInputRef.current = node;
  }, []);
  const { currentUser } = useAuth();
  const { currentWorkspace } = useWorkspace();


  const [autoWidthEnabled, setAutoWidthEnabled] = useState(block.content.autoWidth !== false);
  const deriveWidthValue = useCallback(() => {
    const raw = block.content.width?.toString().replace('%', '') || '100';
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return 100;
    return Math.max(10, Math.min(100, parsed));
  }, [block.content.width]);
  const [manualWidth, setManualWidth] = useState(deriveWidthValue());
  const normalizedBlockType = block.type === 'paragraph' ? 'text' : block.type;

  // When selecting images from the library/stock for non-image blocks (hero-content, etc.),
  // remember which content field should receive the selected image URL.
  const imageTargetFieldRef = useRef<string | null>(null);


  // Extract unique background colors from all blocks (excluding current block and transparent)
  const getEmailBackgroundColors = useCallback(() => {
    const colors = new Set<string>();
    allBlocks.forEach((b) => {
      if (b.id !== block.id && b.styles?.backgroundColor) {
        const bgColor = b.styles.backgroundColor;
        // Exclude transparent and default values
        if (bgColor && bgColor !== 'transparent' && bgColor !== 'rgba(0,0,0,0)') {
          colors.add(bgColor);
        }
      }
    });
    return Array.from(colors);
  }, [allBlocks, block.id]);

  // Get brand colors
  const [brandColors, setBrandColors] = useState<{ primary: string; secondary: string[] }>({
    primary: '#E91E63',
    secondary: ['#2196F3', '#9C27B0']
  });

  useEffect(() => {
    const loadBrandColors = async () => {
      if (!currentUser) return;
      try {
        const branding = await brandingService.getBrandingSettings(currentUser.uid);
        if (branding?.colors) {
          setBrandColors({
            primary: branding.colors.primary || '#E91E63',
            secondary: branding.colors.secondary || []
          });
        }
      } catch (error) {
        console.error('Error loading brand colors:', error);
      }
    };
    loadBrandColors();
  }, [currentUser]);

  const sanitizeMergeTag = useCallback((tag?: string) => {
    if (!tag) return '';
    const trimmed = tag.trim();
    if (trimmed.startsWith('{{') && trimmed.endsWith('}}')) {
      return trimmed;
    }
    const stripped = trimmed.replace(/[{}]/g, '');
    return `{{${stripped}}}`;
  }, []);

  useEffect(() => {
    const loadMergeTags = async () => {
      if (!currentUser) {
        setMergeTags(DEFAULT_MERGE_TAGS);
        return;
      }

      try {
        const fields: ContactField[] = await contactFieldService.getContactFields(currentUser.uid);
        const fieldTags = fields
          .filter((field: ContactField) => Boolean(field.mergeTag))
          .map((field: ContactField) => ({
            label: field.label,
            mergeTag: sanitizeMergeTag(field.mergeTag),
          }));

        const merged = [...DEFAULT_MERGE_TAGS];
        fieldTags.forEach((tag) => {
          if (!merged.some((existing) => existing.mergeTag === tag.mergeTag)) {
            merged.push(tag);
          }
        });

        setMergeTags(merged);
      } catch (error) {
        console.error('Error loading merge tags:', error);
        setMergeTags(DEFAULT_MERGE_TAGS);
      }
    };

    loadMergeTags();
  }, [currentUser, sanitizeMergeTag]);

  useEffect(() => {
    if (!showMergeTagDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const dropdown = mergeTagDropdownRef.current;
      const trigger = mergeTagTriggerRef.current;

      if (
        dropdown &&
        !dropdown.contains(target) &&
        (!trigger || !trigger.contains(target))
      ) {
        setShowMergeTagDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMergeTagDropdown]);


  const applyContentUpdate = useCallback(
    (updates: Record<string, any>) => {
      onUpdate({
        content: {
          ...block.content,
          ...updates,
        },
      });
    },
    [block.content, onUpdate]
  );

  // Replace old Webflow/WXP sample copy with plain generic text when a block is opened.
  useEffect(() => {
    if (block.type !== 'features-grid') return;
    if (!isLegacyIcon2x2FeaturesContent(block.content)) return;
    const normalized = normalizeFeaturesGridContent(block.content);
    onUpdate({ content: normalized });
  }, [block.id, block.type, block.content, onUpdate]);

  const handleContentChange = useCallback(
    (key: string, value: any) => {
      if (block.type === 'columns' && key === 'columns') {
        const newCount = Math.max(1, Number(value) || 1);
        const existingContent = Array.isArray(block.content.columnContent) ? block.content.columnContent : [];
        const newColumnContent = Array.from({ length: newCount }, (_, index) => existingContent[index] ?? '');
        const equalWidth = `${(100 / newCount).toFixed(2)}%`;
        const newColumnWidths = Array.from({ length: newCount }, () => equalWidth);

        applyContentUpdate({
          columns: newCount,
          columnContent: newColumnContent,
          columnWidths: newColumnWidths,
        });
        return;
      }

      applyContentUpdate({
        [key]: value,
      });
    },
    [block.content, block.type, applyContentUpdate]
  );

  useEffect(() => {
    if (block.type !== 'video') return;
    const rawUrl = block.content.videoUrl || '';
    const videoUrl = rawUrl.trim();
    const manualThumb = (block.content.thumbnailUrl || '').trim();

    if (!videoUrl) {
      if (block.content.thumbnailUrl || block.content.thumbnail) {
        applyContentUpdate({ thumbnailUrl: '', thumbnail: '' });
      }
      setVideoPreviewState({ url: null, status: 'idle' });
      return;
    }

    // Do not overwrite a manually set thumbnail URL
    if (manualThumb) {
      if (videoPreviewState.url !== videoUrl || videoPreviewState.status !== 'success') {
        setVideoPreviewState({ url: videoUrl, status: 'success' });
      }
      return;
    }

    const youtubeId = extractYouTubeId(videoUrl);
    if (youtubeId) {
      const youtubeThumb = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
      const current = (block.content.thumbnailUrl || block.content.thumbnail || '').trim();
      if (current !== youtubeThumb) {
        applyContentUpdate({ thumbnailUrl: youtubeThumb, thumbnail: youtubeThumb });
      }
      if (videoPreviewState.url !== videoUrl || videoPreviewState.status !== 'success') {
        setVideoPreviewState({ url: videoUrl, status: 'success' });
      }
      return;
    }

    if (videoUrl === videoPreviewState.url && videoPreviewState.status === 'success') {
      return;
    }

    let cancelled = false;
    setVideoPreviewState({ url: videoUrl, status: 'loading' });

    const fetchThumbnail = async () => {
      try {
        const response = await fetch(`https://noembed.com/embed?url=${encodeURIComponent(videoUrl)}`);
        if (!response.ok) throw new Error('Noembed request failed');
        const data = await response.json();
        if (!cancelled && data?.thumbnail_url) {
          const current = (block.content.thumbnailUrl || block.content.thumbnail || '').trim();
          if (current !== data.thumbnail_url) {
            applyContentUpdate({ thumbnailUrl: data.thumbnail_url, thumbnail: data.thumbnail_url });
          }
          setVideoPreviewState({ url: videoUrl, status: 'success' });
        } else if (!cancelled) {
          setVideoPreviewState({ url: videoUrl, status: 'error' });
        }
      } catch (error) {
        if (!cancelled) {
          setVideoPreviewState({ url: videoUrl, status: 'error' });
        }
      }
    };

    fetchThumbnail();
    return () => {
      cancelled = true;
    };
  }, [
    block.type,
    block.content.videoUrl,
    block.content.thumbnailUrl,
    block.content.thumbnail,
    applyContentUpdate,
    videoPreviewState.url,
    videoPreviewState.status,
  ]);

  const getExistingFileId = useCallback(() => {
    if (block.content.fileId) return block.content.fileId;
    if (block.content.src) {
      const match = block.content.src.match(/\/o\/([^?]+)/);
      if (match) {
        return decodeURIComponent(match[1]).split('/').pop()?.split('.')[0] || '';
      }
    }
    return '';
  }, [block.content.fileId, block.content.src]);

  const dataUrlToFile = useCallback(async (dataUrl: string, fileName: string) => {
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const extension = blob.type.split('/').pop() || 'png';
    const normalizedName = fileName.endsWith(`.${extension}`) ? fileName : `${fileName}.${extension}`;
    return new File([blob], normalizedName, { type: blob.type || 'image/png' });
  }, []);

  const deriveFileName = (src?: string) => {
    if (!src) return '';
    try {
      const sanitized = src.split('?')[0];
      const parts = sanitized.split('/');
      const lastPart = parts[parts.length - 1] || '';
      return decodeURIComponent(lastPart);
    } catch (error) {
      console.warn('Unable to derive image filename', error);
      return src;
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes || Number.isNaN(bytes) || bytes <= 0) {
      return 'Size unknown';
    }
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    const formatted = unitIndex === 0 ? size.toFixed(0) : size.toFixed(2);
    return `${formatted} ${units[unitIndex]}`;
  };

  const parsePxValue = (value: unknown, fallback: number | string) => {
    if (value === '') return '';
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseInt(value.replace('px', ''), 10);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
    return fallback;
  };

  const paddingValues = useMemo(() => {
    const base = parsePxValue(block.styles.padding, 20);
    return {
      top: parsePxValue(block.styles.paddingTop, base),
      right: parsePxValue(block.styles.paddingRight, base),
      bottom: parsePxValue(block.styles.paddingBottom, base),
      left: parsePxValue(block.styles.paddingLeft, base),
    };
  }, [block.styles]);
  const isUniformPadding =
    paddingValues.top === paddingValues.right &&
    paddingValues.top === paddingValues.bottom &&
    paddingValues.top === paddingValues.left;
  const paddingAllValue = isUniformPadding ? paddingValues.top : '';

  const applyPaddingValues = useCallback(
    (values: Record<(typeof PADDING_SIDE_KEYS)[number]['key'], number | string>) => {
      const px = (val: number | string) => {
        if (val === '') return '';
        const num = typeof val === 'string' ? parseInt(val, 10) : val;
        return isNaN(num) ? '' : `${Math.max(0, num)}px`;
      };

      onUpdate({
        styles: {
          ...block.styles,
          padding: `${px(values.top) || '0px'} ${px(values.right) || '0px'} ${px(values.bottom) || '0px'} ${px(values.left) || '0px'}`,
          paddingTop: px(values.top),
          paddingRight: px(values.right),
          paddingBottom: px(values.bottom),
          paddingLeft: px(values.left),
        },
      });
    },
    [block.styles, onUpdate]
  );

  const handlePaddingAllSidesChange = (value: number | string) => {
    const safeValue = value === '' ? '' : Math.max(0, typeof value === 'string' ? parseInt(value, 10) : value);
    if (typeof safeValue === 'number' && isNaN(safeValue)) return;
    applyPaddingValues({
      top: safeValue,
      right: safeValue,
      bottom: safeValue,
      left: safeValue,
    });
  };

  const handlePaddingSideChange = (side: (typeof PADDING_SIDE_KEYS)[number]['key'], value: number | string) => {
    const safeValue = value === '' ? '' : Math.max(0, typeof value === 'string' ? parseInt(value, 10) : value);
    if (typeof safeValue === 'number' && isNaN(safeValue)) return;
    applyPaddingValues({
      ...paddingValues,
      [side]: safeValue,
    });
  };

  const selectedDividerStyle = block.styles.borderStyle || 'solid';
  const selectedDividerOption =
    DIVIDER_STYLE_OPTIONS.find((option) => option.value === selectedDividerStyle) || DIVIDER_STYLE_OPTIONS[0];

  useEffect(() => {
    setShowColorPicker(null);
    setImageActionOpen(true);
    setImageLayoutOpen(true);
    setImagePaddingAdvanced(false);
    setIsImageSourceDropOpen(false);
    setShowDividerStyleDropdown(false);
    setVideoPreviewState({
      url: (block.content.videoUrl || '').trim() || null,
      status: 'idle',
    });
  }, [block.id, block.content.videoUrl]);

  useEffect(() => {
    setAutoWidthEnabled(block.content.autoWidth !== false);
  }, [block.content.autoWidth]);

  useEffect(() => {
    setManualWidth(deriveWidthValue());
  }, [deriveWidthValue]);

  useEffect(() => {
    if (!isImageSourceDropOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (imageSourceDropRef.current && !imageSourceDropRef.current.contains(event.target as Node)) {
        setIsImageSourceDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isImageSourceDropOpen]);

  useEffect(() => {
    if (!showDividerStyleDropdown) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dividerStyleDropdownRef.current &&
        !dividerStyleDropdownRef.current.contains(event.target as Node)
      ) {
        setShowDividerStyleDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDividerStyleDropdown]);

  // Close color picker when clicking outside
  useEffect(() => {
    if (!showColorPicker && !showAdvancedPicker) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (colorPickerRef.current && !colorPickerRef.current.contains(target)) {
        // Check if click is on the SketchPicker chrome (react-color adds elements outside the ref)
        const sketchPicker = (target as HTMLElement).closest('.sketch-picker');
        if (sketchPicker) return;

        setShowColorPicker(null);
        setShowAdvancedPicker(null);
        setAdvancedPickerPosition(null);
      }
    };
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColorPicker, showAdvancedPicker]);
  const handleOpenContentManager = (tab: ContentManagerTab) => {
    setContentManagerTab(tab);
    setIsContentManagerOpen(true);
    setIsImageSourceDropOpen(false);
  };

  const handleOpenStockImages = () => {
    setIsStockImageModalOpen(true);
    setIsImageSourceDropOpen(false);
  };

  useEffect(() => {
    if (openProductImageIndex === null) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (productImageDropRef.current && !productImageDropRef.current.contains(event.target as Node)) {
        setOpenProductImageIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openProductImageIndex]);

  useEffect(() => {
    if (!showProductFontFamilyDropdown) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (productFontFamilyDropdownRef.current && !productFontFamilyDropdownRef.current.contains(event.target as Node)) {
        setShowProductFontFamilyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProductFontFamilyDropdown]);

  useEffect(() => {
    if (!showProductFontFamilyDropdown) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (productFontFamilyDropdownRef.current && !productFontFamilyDropdownRef.current.contains(event.target as Node)) {
        setShowProductFontFamilyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProductFontFamilyDropdown]);

  const handleOpenImageEditor = useCallback(() => {
    if (!block.content.src) return;
    setImageEditorInitialSrc(block.content.src);
    setIsImageEditorOpen(true);
  }, [block.content.src]);

  const handleCloseImageEditor = useCallback(() => {
    setIsImageEditorOpen(false);
    setImageEditorInitialSrc(null);
    setCarouselEditingIndex(null);
  }, []);

  const handleImageEditorSave = useCallback(
    async (editedDataUrl: string) => {
      if (!currentUser) {
        console.warn('User must be signed in to save edited images.');
        return;
      }

      setIsSavingEditedImage(true);

      try {
        const editedFile = await dataUrlToFile(
          editedDataUrl,
          block.content.imageName ? `${block.content.imageName}-edited` : `edited-image-${Date.now()}`
        );

        const uploadedFile = await storageService.uploadFile(
          editedFile,
          currentUser.uid,
          currentWorkspace?.id
        );

        // Check if we are editing a carousel image
        if (carouselEditingIndex !== null && block.type === 'image-carousel') {
          const images = (block.content.images as any[]) || [];
          const newImages = [...images];
          if (newImages[carouselEditingIndex]) {
            newImages[carouselEditingIndex] = {
              ...newImages[carouselEditingIndex],
              src: uploadedFile.url,
              fileId: uploadedFile.id || null,
              fileSize: uploadedFile.size
            };
            handleContentChange('images', newImages);
          }
        } else {
          // Standard image block update
          // Capture original snapshot if not present
          const hasOriginal = Boolean(block.content.originalSrc) || Boolean(block.content.originalFileId);
          const originalSnapshot = hasOriginal ? {} : {
            originalSrc: block.content.src,
            originalFileId: block.content.fileId || getExistingFileId(),
            originalFileSize: block.content.fileSize,
            originalImageName: block.content.imageName,
            originalImageMimeType: block.content.imageMimeType,
            originalMetadata: block.content.metadata,
          };

          applyContentUpdate({
            ...originalSnapshot,
            src: uploadedFile.url,
            fileId: uploadedFile.id || null,
            fileSize: uploadedFile.size,
            imageName: uploadedFile.originalName || 'Edited Image',
            imageMimeType: uploadedFile.mimeType,
            // Update dimensions if needed
            metadata: {
              ...block.content.metadata,
              width: uploadedFile.metadata?.width,
              height: uploadedFile.metadata?.height,
            }
          });
        }

        handleCloseImageEditor();
      } catch (error) {
        console.error('Failed to save edited image:', error);
        alert('Failed to save image. Please try again.');
      } finally {
        setIsSavingEditedImage(false);
      }
    },
    [
      applyContentUpdate,
      block.content.fileId,
      block.content.imageName,
      block.content.metadata,
      block.content.fileSize,
      block.content.imageMimeType,
      block.content.src,
      block.content.originalSrc,
      block.content.originalFileId,
      block.type,
      block.content.images,
      currentUser,
      currentWorkspace?.id,
      getExistingFileId,
      carouselEditingIndex,
      handleContentChange,
      handleCloseImageEditor
    ]
  );

  const handleRevertImageEdits = useCallback(() => {
    const originalSrc = block.content.originalSrc as string | undefined;
    if (!originalSrc) {
      return;
    }

    applyContentUpdate({
      src: originalSrc,
      fileId: (block.content.originalFileId as string | undefined) || block.content.fileId,
      fileSize: block.content.originalFileSize ?? block.content.fileSize,
      imageName: (block.content.originalImageName as string | undefined) || block.content.imageName,
      imageMimeType:
        (block.content.originalImageMimeType as string | undefined) || block.content.imageMimeType,
      metadata: (block.content.originalMetadata as any) || block.content.metadata,
    });
  }, [applyContentUpdate, block.content]);

  const insertRichTextMergeTag = useCallback(
    (mergeTag: string) => {
      const input = richTextInputRef.current;
      const currentValue = block.content.text || '';
      const tag = sanitizeMergeTag(mergeTag);

      if (!input) {
        handleContentChange('text', `${currentValue}${tag}`);
        setShowMergeTagDropdown(false);
        return;
      }

      const start = input.selectionStart ?? currentValue.length;
      const end = input.selectionEnd ?? currentValue.length;
      const newValue = `${currentValue.slice(0, start)}${tag}${currentValue.slice(end)}`;

      handleContentChange('text', newValue);
      requestAnimationFrame(() => {
        input.focus();
        const cursor = start + tag.length;
        input.setSelectionRange(cursor, cursor);
      });
      setShowMergeTagDropdown(false);
    },
    [block.content.text, handleContentChange, sanitizeMergeTag]
  );

  const wrapRichTextSelectionWithHtml = useCallback(
    (tagName: 'strong' | 'em' | 'u') => {
      const input = richTextInputRef.current;
      const currentValue = String(block.content.text || '');
      const openTag = `<${tagName}>`;
      const closeTag = `</${tagName}>`;

      if (!input) {
        handleContentChange('text', `${currentValue}${openTag}${closeTag}`);
        handleContentChange('isHtml', true);
        return;
      }

      // Clicking toolbar buttons can steal focus and collapse selection.
      // Prefer a cached selection if the live one is empty.
      const liveStart = input.selectionStart ?? 0;
      const liveEnd = input.selectionEnd ?? 0;
      const cached = richTextSelectionRef.current;
      const start = liveEnd > liveStart ? liveStart : (cached?.start ?? liveStart);
      const end = liveEnd > liveStart ? liveEnd : (cached?.end ?? liveEnd);
      if (end <= start) {
        // No selection: insert an empty tag pair and place cursor between them
        const newValue = `${currentValue.slice(0, start)}${openTag}${closeTag}${currentValue.slice(end)}`;
        handleContentChange('text', newValue);
        handleContentChange('isHtml', true);
        requestAnimationFrame(() => {
          input.focus();
          const cursor = start + openTag.length;
          input.setSelectionRange(cursor, cursor);
          richTextSelectionRef.current = { start: cursor, end: cursor };
        });
        return;
      }

      const selected = currentValue.slice(start, end);
      const newValue = `${currentValue.slice(0, start)}${openTag}${selected}${closeTag}${currentValue.slice(end)}`;
      handleContentChange('text', newValue);
      handleContentChange('isHtml', true);
      requestAnimationFrame(() => {
        input.focus();
        // Keep selection on the formatted content
        const newStart = start + openTag.length;
        const newEnd = newStart + selected.length;
        input.setSelectionRange(newStart, newEnd);
        richTextSelectionRef.current = { start: newStart, end: newEnd };
      });
    },
    [block.content.text, handleContentChange]
  );

  const clearRichTextFormattingInSelection = useCallback(() => {
    const input = richTextInputRef.current;
    const currentValue = String(block.content.text || '');

    if (!input) {
      const stripped = currentValue
        .replace(/<\/?(strong|em|u)\b[^>]*>/gi, '')
        .replace(/<\/?span\b[^>]*>/gi, '');
      handleContentChange('text', stripped);
      handleContentChange('isHtml', true);
      return;
    }

    const liveStart = input.selectionStart ?? 0;
    const liveEnd = input.selectionEnd ?? 0;
    const cached = richTextSelectionRef.current;
    const start = liveEnd > liveStart ? liveStart : (cached?.start ?? liveStart);
    const end = liveEnd > liveStart ? liveEnd : (cached?.end ?? liveEnd);
    if (end <= start) return;

    const selected = currentValue.slice(start, end);
    const strippedSelected = selected
      .replace(/<\/?(strong|em|u)\b[^>]*>/gi, '')
      .replace(/<\/?span\b[^>]*>/gi, '');
    const newValue = `${currentValue.slice(0, start)}${strippedSelected}${currentValue.slice(end)}`;
    handleContentChange('text', newValue);
    handleContentChange('isHtml', true);
    requestAnimationFrame(() => {
      input.focus();
      const newEnd = start + strippedSelected.length;
      input.setSelectionRange(start, newEnd);
      richTextSelectionRef.current = { start, end: newEnd };
    });
  }, [block.content.text, handleContentChange]);

  const handleMagicGenerate = useCallback(() => {
    if (!block.content.magicTopic) return;

    setIsGenerating(true);

    // Simulate AI delay
    setTimeout(() => {
      let generatedText = '';
      const topic = block.content.magicTopic?.toLowerCase() || '';
      const tone = block.content.magicTone || 'professional';

      // Simple mock generation logic
      if (topic.includes('welcome')) {
        generatedText = `Welcome to the family! We are thrilled to have you on board. Get ready for exclusive updates and special offers coming your way.`;
      } else if (topic.includes('sale') || topic.includes('offer')) {
        generatedText = `Don't miss out! Our biggest sale of the year is here. Shop now and save up to 50% on your favorite items. Limited time only!`;
      } else if (topic.includes('update') || topic.includes('news')) {
        generatedText = `We've got some exciting news to share! Our latest update brings new features designed to help you achieve more. Read on to learn more.`;
      } else {
        generatedText = `Here is some ${tone} content about "${block.content.magicTopic}". Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.`;
      }

      onUpdate({
        content: {
          ...block.content,
          text: generatedText
        }
      });
      setIsGenerating(false);
    }, 1500);
  }, [block.content, onUpdate]);

  const handleStyleChange = useCallback(
    (key: string, value: any) => {
      // Dynamically load Google Font when fontFamily changes
      if ((key === 'fontFamily' || key === 'productNameFontFamily') && typeof value === 'string' && value) {
        const fontName = value;
        const systemFonts = ['Arial', 'Helvetica', 'Verdana', 'Tahoma', 'Trebuchet MS', 'Century Gothic', 'Times New Roman', 'Georgia', 'Palatino', 'Garamond', 'Bookman', 'Impact', 'Comic Sans MS', 'Courier New', 'Lucida Console', 'Monaco', 'Consolas', 'system-ui', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI'];
        if (!systemFonts.includes(fontName)) {
          const linkId = `google-font-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
          if (!document.getElementById(linkId)) {
            const link = document.createElement('link');
            link.id = linkId;
            link.rel = 'stylesheet';
            link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;500;600;700&display=swap`;
            document.head.appendChild(link);
          }
        }
      }
      onUpdate({
        styles: {
          ...block.styles,
          [key]: value,
        },
      });
    },
    [block.styles, onUpdate]
  );

  useEffect(() => {
    if (showFontFamilyDropdown) {
      const systemFonts = ['Arial', 'Helvetica', 'Verdana', 'Tahoma', 'Trebuchet MS', 'Century Gothic', 'Times New Roman', 'Georgia', 'Palatino', 'Garamond', 'Bookman', 'Impact', 'Comic Sans MS', 'Courier New', 'Lucida Console', 'Monaco', 'Consolas', 'system-ui', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI'];
      const fontsToLoad = FONT_FAMILIES.map(f => f.value).filter(f => !systemFonts.includes(f));

      const chunkSize = 20;
      for (let i = 0; i < fontsToLoad.length; i += chunkSize) {
        const chunk = fontsToLoad.slice(i, i + chunkSize);
        const chunkId = `google-fonts-preview-chunk-${i}`;
        if (!document.getElementById(chunkId)) {
          const familyString = chunk.map(f => `family=${encodeURIComponent(f)}:wght@400;500;600;700`).join('&');
          const link = document.createElement('link');
          link.id = chunkId;
          link.rel = 'stylesheet';
          link.href = `https://fonts.googleapis.com/css2?${familyString}&display=swap`;
          document.head.appendChild(link);
        }
      }
    }
  }, [showFontFamilyDropdown]);

  const handleFontFamilyKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      // Ensure we have a clean font name to match
      const currentFontFamily = block.styles?.fontFamily || 'Inter';
      const cleanFontFamily = currentFontFamily.split(',')[0].replace(/['"]/g, '').trim();

      const currentIndex = FONT_FAMILIES.findIndex(f => f.value === cleanFontFamily);
      let nextIndex = currentIndex !== -1 ? currentIndex : 0;

      if (e.key === 'ArrowUp') {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : FONT_FAMILIES.length - 1;
      } else if (e.key === 'ArrowDown') {
        nextIndex = currentIndex < FONT_FAMILIES.length - 1 ? currentIndex + 1 : 0;
      }

      if (nextIndex >= 0 && nextIndex < FONT_FAMILIES.length) {
        const nextFont = FONT_FAMILIES[nextIndex];
        handleStyleChange('fontFamily', nextFont.value);

        if (showFontFamilyDropdown && fontFamilyDropdownRef.current) {
          const targetButton = fontFamilyDropdownRef.current.querySelector(
            `button[data-font="${nextFont.value}"]`
          ) as HTMLButtonElement | null;
          if (targetButton) {
            targetButton.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        }
      }
    }
  }, [block.styles?.fontFamily, showFontFamilyDropdown, handleStyleChange]);

  const handleButtonStyleChange = useCallback(
    (key: string, value: any) => {
      onUpdate({
        styles: {
          ...block.styles,
          buttonStyle: {
            ...block.styles.buttonStyle,
            [key]: value,
          },
        },
      });
    },
    [block.styles, onUpdate]
  );

  const renderLogoProperties = () => {
    // Similar to renderImageProperties but without "More images" button
    const widthValue = block.content.width
      ? parseInt(block.content.width.toString().replace('%', ''), 10)
      : 200;

    const handleWidthChange = (newWidth: number) => {
      setManualWidth(newWidth);
      applyContentUpdate({ width: `${newWidth}px` });
    };

    const previousDerivedName = deriveFileName(block.content.src);
    const imageDisplayName = block.content.imageName || previousDerivedName;
    const fileSizeLabel = formatFileSize(block.content.fileSize);
    const currentBackground = block.styles.backgroundColor || 'transparent';

    const handleImageUrlInput = (value: string) => {
      const derivedName = deriveFileName(value);
      const shouldUpdateName =
        !block.content.imageName ||
        !block.content.src ||
        block.content.imageName === previousDerivedName;

      applyContentUpdate({
        src: value,
        fileId: null,
        fileSize: null,
        ...(shouldUpdateName ? { imageName: derivedName } : {}),
      });
    };

    // Get dimensions
    const dimensions = block.content.metadata?.width && block.content.metadata?.height
      ? `${block.content.metadata.width} × ${block.content.metadata.height}`
      : '';

    return (
      <div className="space-y-6">
        <div className="border border-slate-200 rounded-2xl bg-white shadow-sm p-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">Logo source</p>
              <p className="text-xs text-slate-500">Upload a file or paste a direct link</p>
            </div>
            <div className="w-full">
              <button
                className={`text-sm font-medium px-4 py-2.5 transition w-full ${isUploadingImage
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  : 'bg-slate-900 text-white hover:bg-black'
                  }`}
                onClick={() => {
                  if (isUploadingImage) return;
                  fileInputRef.current?.click();
                }}
              >
                {isUploadingImage ? 'Uploading...' : 'Upload Logo'}
              </button>
            </div>
          </div>

          {block.content.src && (
            <div className="flex items-start gap-3 rounded-xl border border-slate-200 p-3 bg-slate-50">
              <div className="w-16 h-16 rounded-lg overflow-hidden border border-slate-200 bg-white flex items-center justify-center">
                <img
                  src={block.content.src}
                  alt={block.content.alt || 'Selected logo'}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {imageDisplayName || 'Selected logo'}
                </p>
                <p className="text-xs text-slate-500">
                  {block.content.fileSize ? fileSizeLabel : (block.content.stockProvider || block.content.src?.includes('unsplash.com') ? 'From stock image' : 'From library')}
                  {dimensions ? ` • ${dimensions}` : ''}
                </p>
                {/* Edit button hidden for now, but preserved for future use */}
                {false && (
                  <button
                    className="mt-2 inline-flex items-center justify-center text-sm font-semibold text-white bg-slate-900 rounded-lg px-5 py-2 shadow-sm hover:bg-black disabled:opacity-50"
                    onClick={handleOpenImageEditor}
                    disabled={isUploadingImage || !block.content.src}
                  >
                    {isSavingEditedImage ? 'Processing...' : 'Edit logo'}
                  </button>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Image URL</label>
            <input
              type="text"
              value={block.content.src || ''}
              onChange={(e) => handleImageUrlInput(e.target.value)}
              placeholder="https://example.com/logo.png"
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
          </div>

        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-800">Width</p>
              <p className="text-xs text-slate-500">{widthValue}px</p>
            </div>
          </div>
          <div className="relative">
            <input
              type="range"
              min={50}
              max={400}
              value={widthValue}
              onChange={(e) => handleWidthChange(parseInt(e.target.value, 10))}
              className="v2-slider w-full h-1 opacity-100"
              style={{
                background: `linear-gradient(90deg, #0F172A ${((widthValue - 50) / 350) * 100}%, #E2E8F0 ${((widthValue - 50) / 350) * 100}%)`,
              }}
            />
          </div>
          <div className="mt-8 pt-4 border-t border-slate-100 space-y-4">
            <div>
              <p className="text-sm font-medium text-slate-800 mb-2">Align</p>
              <div className="flex items-center gap-1.5 mt-1.5">
                {(['left', 'center', 'right'] as const).map((align) => (
                  <button
                    key={align}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                      (block.styles.textAlign || 'center') === align
                        ? 'bg-slate-100 text-slate-900 shadow-sm'
                        : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                    }`}
                    onClick={() => handleStyleChange('textAlign', align)}
                    title={align.charAt(0).toUpperCase() + align.slice(1)}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {align === 'left' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h13M4 12h9M4 18h13" />}
                      {align === 'center' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 6h10M4 12h16M7 18h10" />}
                      {align === 'right' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 6h13M11 12h9M7 18h13" />}
                    </svg>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-slate-800">Background color</p>
                {currentBackground !== 'transparent' && (
                  <button
                    type="button"
                    onClick={() => handleStyleChange('backgroundColor', 'transparent')}
                    className="text-xs font-semibold text-slate-500 hover:text-slate-800"
                  >
                    Reset
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'logoBg' ? null : 'logoBg')}
                  className="w-full flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 transition bg-white"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-5 h-5 rounded-full border border-slate-200 shadow-inner flex-shrink-0"
                      style={{
                        background:
                          currentBackground === 'transparent'
                            ? 'repeating-conic-gradient(#E5E7EB 0% 25%, #F3F4F6 0% 50%)'
                            : currentBackground,
                        backgroundSize: '8px 8px',
                      }}
                    />
                    <span className="truncate">
                      {currentBackground === 'transparent' ? 'Transparent' : currentBackground}
                    </span>
                  </div>
                  <ChevronDownIcon
                    className={`w-4 h-4 text-slate-500 flex-shrink-0 transition-transform ${showColorPicker === 'logoBg' ? 'rotate-180' : ''
                      }`}
                  />
                </button>
                <input
                  type="text"
                  value={currentBackground === 'transparent' ? '' : currentBackground}
                  onChange={(e) => handleStyleChange('backgroundColor', e.target.value || 'transparent')}
                  placeholder="e.g. #F8FAFC"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
              </div>
              {showColorPicker === 'logoBg' && (
                <div className="mt-3" ref={colorPickerRef}>
                  <CustomColorPicker
                    currentColor={currentBackground === 'transparent' ? '' : currentBackground}
                    onChange={(color) => handleStyleChange('backgroundColor', color)}
                    pickerType="logoBg"
                    emailColors={getEmailBackgroundColors()}
                    brandColors={brandColors}
                    showAdvancedPicker={showAdvancedPicker}
                    setShowAdvancedPicker={setShowAdvancedPicker}
                    setAdvancedPickerPosition={setAdvancedPickerPosition}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <button
              className="w-full flex items-center justify-between text-sm font-medium text-slate-800"
              onClick={() => setLogoActionOpen((prev) => !prev)}
            >
              Action
              <svg className={`w-4 h-4 transition ${logoActionOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.061l-4.24 4.243a.75.75 0 01-1.06 0L5.25 8.27a.75.75 0 01-.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            {logoActionOpen && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">URL</label>
                  <input
                    type="text"
                    value={block.content.linkUrl || ''}
                    onChange={(e) => handleContentChange('linkUrl', e.target.value)}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                    placeholder={
                      'https://example.com'
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Target</label>
                  <select
                    value={block.content.linkTarget || '_blank'}
                    onChange={(e) => handleContentChange('linkTarget', e.target.value)}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  >
                    <option value="_blank">New tab</option>
                    <option value="_self">Same tab</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-800 mb-1">Alternate text</label>
            <input
              type="text"
              value={block.content.alt || ''}
              onChange={(e) => handleContentChange('alt', e.target.value)}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              placeholder="Describe the logo"
            />
          </div>
        </div>

        <div className="border-t border-slate-200 pt-4">
          <button
            className="w-full flex items-center justify-between text-sm font-medium text-slate-800"
            onClick={() => setImageLayoutOpen((prev) => !prev)}
          >
            Layout
            <svg className={`w-4 h-4 transition ${imageLayoutOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.061l-4.24 4.243a.75.75 0 01-1.06 0L5.25 8.27a.75.75 0 01-.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          {imageLayoutOpen && (
            <div className="mt-3 space-y-3 min-w-0">
          <PaddingControl
            values={{
              top: block.styles.paddingTop,
              bottom: block.styles.paddingBottom,
              left: block.styles.paddingLeft,
              right: block.styles.paddingRight,
              padding: block.styles.padding
            }}
            onChange={handleStyleChange}
            onMultiChange={(updates) => {
              onUpdate({
                styles: {
                  ...block.styles,
                  ...updates
                }
              });
            }}
          />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderImageProperties = () => {
    const autoWidth = autoWidthEnabled;
    const widthValue = manualWidth;
    const currentBackground = (block.styles.backgroundColor as string) || 'transparent';

    const handleWidthChange = (value: number) => {
      const switchingFromAuto = autoWidthEnabled;
      const currentHeight = block.content.height;
      const shouldResetHeight =
        switchingFromAuto ||
        !currentHeight ||
        currentHeight === 'auto' ||
        currentHeight === DEFAULT_IMAGE_HEIGHT;

      const nextContentUpdates: Record<string, any> = {
        width: `${value}%`,
      };

      if (switchingFromAuto) {
        nextContentUpdates.autoWidth = false;
        setAutoWidthEnabled(false);
        handleStyleChange('textAlign', 'center');
      }

      if (shouldResetHeight) {
        nextContentUpdates.height = 'auto';
      }

      setManualWidth(value);
      applyContentUpdate(nextContentUpdates);
    };

    const handlePaddingInput = (value: number | string) => {
      if (value === '') return handleStyleChange('padding', '');
      const num = typeof value === 'string' ? parseInt(value, 10) : value;
      if (!isNaN(num)) handleStyleChange('padding', `${Math.max(0, num)}px`);
    };

    const paddingSides = {
      top: (block.styles.paddingTop === 0 || block.styles.paddingTop === '0' || block.styles.paddingTop === '0px') ? '0px' : (block.styles.paddingTop || block.styles.padding || '20px'),
      bottom: (block.styles.paddingBottom === 0 || block.styles.paddingBottom === '0' || block.styles.paddingBottom === '0px') ? '0px' : (block.styles.paddingBottom || block.styles.padding || '20px'),
      left: (block.styles.paddingLeft === 0 || block.styles.paddingLeft === '0' || block.styles.paddingLeft === '0px') ? '0px' : (block.styles.paddingLeft || block.styles.padding || '20px'),
      right: (block.styles.paddingRight === 0 || block.styles.paddingRight === '0' || block.styles.paddingRight === '0px') ? '0px' : (block.styles.paddingRight || block.styles.padding || '20px'),
    };

    const setPaddingSide = (side: keyof typeof paddingSides, value: number | string) => {
      const property = `padding${side.charAt(0).toUpperCase()}${side.slice(1)}`;
      if (value === '') return handleStyleChange(property, '');
      const num = typeof value === 'string' ? parseInt(value, 10) : value;
      if (!isNaN(num)) handleStyleChange(property, `${Math.max(0, num)}px`);
    };

    const previousDerivedName = deriveFileName(block.content.src);
    const imageDisplayName = block.content.imageName || previousDerivedName;
    const fileSizeLabel = formatFileSize(block.content.fileSize);

    const handleImageUrlInput = (value: string) => {
      const derivedName = deriveFileName(value);
      const shouldUpdateName =
        !block.content.imageName ||
        !block.content.src ||
        block.content.imageName === previousDerivedName;

      applyContentUpdate({
        src: value,
        fileId: null,
        fileSize: null,
        ...(shouldUpdateName ? { imageName: derivedName } : {}),
      });
    };

    // Get dimensions
    const dimensions = block.content.metadata?.width && block.content.metadata?.height
      ? `${block.content.metadata.width} × ${block.content.metadata.height}`
      : '';
    const overlayText = block.content.overlayText || {};
    const ensureGoogleFontLoaded = (fontName?: string) => {
      if (!fontName) return;
      const systemFonts = ['Arial', 'Helvetica', 'Verdana', 'Tahoma', 'Trebuchet MS', 'Century Gothic', 'Times New Roman', 'Georgia', 'Palatino', 'Garamond', 'Bookman', 'Impact', 'Comic Sans MS', 'Courier New', 'Lucida Console', 'Monaco', 'Consolas', 'system-ui', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI'];
      if (systemFonts.includes(fontName)) return;
      const fontSlug = fontName.replace(/\s+/g, '-').toLowerCase();
      const overlayLinkId = `overlay-google-font-${fontSlug}`;
      const desiredHref = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}&display=swap`;
      const existingOverlayLink = document.getElementById(overlayLinkId) as HTMLLinkElement | null;

      if (existingOverlayLink) {
        if (existingOverlayLink.href !== desiredHref) {
          existingOverlayLink.href = desiredHref;
        }
        return;
      }

      const link = document.createElement('link');
      link.id = overlayLinkId;
      link.rel = 'stylesheet';
      // Use a safe family-only request: some decorative/display fonts
      // do not support multi-weight requests and can fail to load.
      link.href = desiredHref;
      document.head.appendChild(link);
    };
    const updateOverlayText = (updates: Record<string, any>) => {
      if (typeof updates.fontFamily === 'string' && updates.fontFamily) {
        ensureGoogleFontLoaded(updates.fontFamily);
      }
      handleContentChange('overlayText', {
        ...overlayText,
        ...updates,
      });
    };

    return (
      <div className="space-y-6">
        <CollapsibleSection title="Image">
        <div className="space-y-6 pt-2">
          {/* Image Source & Background Card */}
          <div className="p-1 space-y-5">
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-slate-900">Image source</p>
                <p className="text-xs text-slate-500">Upload a file or paste a direct link</p>
              </div>

              <div className="space-y-2 relative" ref={imageSourceDropRef}>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">Add image from...</label>
                <div className="relative">
                  <button
                    onClick={() => setIsImageSourceDropOpen(!isImageSourceDropOpen)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50/30 transition-all group shadow-sm"
                  >
                    <div className="flex items-center gap-2">
                      <PhotoIcon className="w-4 h-4 text-slate-400 group-hover:text-slate-500" />
                      <span>{block.content.src ? 'Change Image Assets' : 'Select Image Assets'}</span>
                    </div>
                    <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform ${isImageSourceDropOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isImageSourceDropOpen && (
                    <div className="absolute top-full left-0 right-0 z-[60] mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                      <button
                        onClick={() => {
                          if (isUploadingImage) return;
                          fileInputRef.current?.click();
                          setIsImageSourceDropOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Upload from computer</span>
                      </button>
                      <button
                        onClick={() => {
                          imageTargetFieldRef.current = 'src';
                          setIsStockImageModalOpen(true);
                          setIsImageSourceDropOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <SparklesIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Stock image</span>
                      </button>
                      <button
                        onClick={() => {
                          imageTargetFieldRef.current = 'src';
                          setIsContentManagerOpen(true);
                          setContentManagerTab('image');
                          setIsImageSourceDropOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <QueueListIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Library</span>
                      </button>
                      <ImageSourceFromCanvaRow onClose={() => setIsImageSourceDropOpen(false)} />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Image URL</label>
                <input
                  type="text"
                  value={block.content.src || ''}
                  onChange={(e) => handleImageUrlInput(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500"
                />
              </div>

              {block.content.src && (
                <div className="flex items-start gap-3 rounded-xl border border-slate-200 p-3 bg-slate-50/50">
                  <div className="w-14 h-14 rounded-lg overflow-hidden border border-slate-200 bg-white flex items-center justify-center shrink-0 shadow-sm">
                    <img
                      src={block.content.src}
                      alt={block.content.alt || 'Selected image'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-900 truncate">
                      {imageDisplayName || 'Selected image'}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {block.content.fileSize ? fileSizeLabel : (block.content.stockProvider || block.content.src?.includes('unsplash.com') ? 'From stock image' : 'From library')}
                      {dimensions ? ` • ${dimensions}` : ''}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      {/* Edit button hidden for now, but preserved for future use */}
                      {false && (
                        <button
                          className="text-[10px] font-bold text-slate-900 bg-white border border-slate-200 rounded-md px-2 py-1 shadow-sm hover:bg-slate-50 transition-colors"
                          onClick={handleOpenImageEditor}
                          disabled={isUploadingImage || !block.content.src}
                        >
                          Edit
                        </button>
                      )}
                      <button
                        onClick={() => handleContentChange('src', '')}
                        className="text-[10px] font-bold text-rose-600 bg-white border border-rose-100 rounded-md px-2 py-1 shadow-sm hover:bg-rose-50 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>


          </div>

          {/* Width Control */}
          <div className="space-y-4 px-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-slate-900 uppercase tracking-tight">Width</p>
                <p className="text-xs text-slate-500">{autoWidthEnabled ? 'Full scale' : `${widthValue}%`}</p>
              </div>
              <div className="flex bg-slate-100/80 p-0.5 rounded-lg border border-slate-200/60 w-[110px]">
                <button
                  type="button"
                  onClick={() => {
                    if (autoWidthEnabled) return;
                    setAutoWidthEnabled(true);
                    applyContentUpdate({ autoWidth: true, width: '100%', height: 'auto' });
                  }}
                  className={`flex-1 text-[11px] font-bold py-1 rounded-md transition-all duration-200 ${autoWidthEnabled ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-900/5' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  Auto
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!autoWidthEnabled) return;
                    setAutoWidthEnabled(false);
                    setManualWidth(80);
                    applyContentUpdate({ autoWidth: false, width: '80%', height: 'auto' });
                  }}
                  className={`flex-1 text-[11px] font-bold py-1 rounded-md transition-all duration-200 ${!autoWidthEnabled ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-900/5' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  Custom
                </button>
              </div>
            </div>
            <div className="relative pt-1">
              <input
                type="range"
                min={10}
                max={100}
                value={widthValue}
                onChange={(e) => handleWidthChange(parseInt(e.target.value, 10))}
                className={`v2-slider w-full h-1.5 ${autoWidthEnabled ? 'opacity-40 cursor-not-allowed' : 'opacity-100'}`}
                style={{
                  background: autoWidthEnabled
                    ? '#E2E8F0'
                    : `linear-gradient(90deg, #0F172A ${((widthValue - 10) / 90) * 100}%, #E2E8F0 ${((widthValue - 10) / 90) * 100}%)`,
                }}
                disabled={autoWidthEnabled}
              />
            </div>
          </div>

          {/* Height Control */}
          <div className="space-y-4 px-1 pt-4 border-t border-slate-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-slate-900 uppercase tracking-tight">Height</p>
                <p className="text-xs text-slate-500">
                  {(!block.content.height || block.content.height === 'auto') ? 'Original aspect' : block.content.height}
                </p>
              </div>
              <div className="flex bg-slate-100/80 p-0.5 rounded-lg border border-slate-200/60 w-[110px]">
                <button
                  type="button"
                  onClick={() => applyContentUpdate({ height: 'auto' })}
                  className={`flex-1 text-[11px] font-bold py-1 rounded-md transition-all duration-200 ${(!block.content.height || block.content.height === 'auto') ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-900/5' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  Auto
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!block.content.height || block.content.height === 'auto') {
                      applyContentUpdate({ height: '300px' });
                    }
                  }}
                  className={`flex-1 text-[11px] font-bold py-1 rounded-md transition-all duration-200 ${!(!block.content.height || block.content.height === 'auto') ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-900/5' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  Custom
                </button>
              </div>
            </div>
            {block.content.height && block.content.height !== 'auto' && (
              <div className="flex items-center gap-2">
                <div className="flex items-center border border-slate-200 rounded-xl flex-1 bg-white shadow-sm overflow-hidden min-h-[40px]">
                  <button
                    type="button"
                    className="w-10 h-full flex items-center justify-center text-slate-500 hover:bg-slate-50 transition-colors border-r border-slate-100 font-bold"
                    onClick={() => {
                      const currentHeight = parseInt(block.content.height?.replace('px', '') || '300', 10);
                      applyContentUpdate({ height: `${Math.max(50, currentHeight - 10)}px` });
                    }}
                  >
                    -
                  </button>
                  <div className="flex-1 flex items-center justify-center relative">
                    <input
                      type="number"
                      value={block.content.height?.replace('px', '') ?? '300'}
                      onChange={(e) => {
                        const num = parseInt(e.target.value, 10);
                        if (!isNaN(num)) applyContentUpdate({ height: `${Math.max(50, num)}px` });
                      }}
                      className="w-full text-center py-2 text-sm font-bold text-slate-800 focus:outline-none bg-transparent"
                    />
                    <span className="absolute right-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">px</span>
                  </div>
                  <button
                    type="button"
                    className="w-10 h-full flex items-center justify-center text-slate-500 hover:bg-slate-50 transition-colors border-l border-slate-100 font-bold"
                    onClick={() => {
                      const currentHeight = parseInt(block.content.height?.replace('px', '') || '300', 10);
                      applyContentUpdate({ height: `${currentHeight + 10}px` });
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Align */}
          <div className="space-y-3 px-1 pt-4 border-t border-slate-50">
            <p className="text-sm font-bold text-slate-900 uppercase tracking-tight">Align</p>
            <div className="flex items-center gap-2">
              {(['left', 'center', 'right'] as const).map((align) => (
                <button
                  key={align}
                  className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all ${
                    (block.styles.textAlign || 'left') === align
                      ? 'bg-slate-100 text-slate-900 shadow-sm ring-1 ring-slate-200'
                      : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                  }`}
                  onClick={() => handleStyleChange('textAlign', align)}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {align === 'left' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h13M4 12h9M4 18h13" />}
                    {align === 'center' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 6h10M4 12h16M7 18h10" />}
                    {align === 'right' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 6h13M11 12h9M7 18h13" />}
                  </svg>
                </button>
              ))}
            </div>
          </div>

          {/* Shape */}
          <div className="space-y-4 px-1 pt-4 border-t border-slate-50">
            <p className="text-sm font-bold text-slate-900 uppercase tracking-tight">Shape</p>
            <div className="grid grid-cols-6 gap-2">
              {[
                { id: 'square', path: 'M4 4h16v16H4V4z' },
                { id: 'rounded', path: 'M6 4h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2z' },
                { id: 'leaf', path: 'M4 12c0-4.4 3.6-8 8-8h8v8c0 4.4-3.6 8-8 8H4v-8z' },
                { id: 'pill', path: 'M7 7h10a5 5 0 010 10H7a5 5 0 010-10z' },
                { id: 'arch', path: 'M4 20V12c0-4.4 3.6-8 8-8s8 3.6 8 8v8H4z' },
                { id: 'shield', path: 'M4 4v8c0 4.4 3.6 8 8 8s8-3.6 8-8V4H4z' },
                { id: 'clover', path: 'M12 2c2.2 0 4 1.8 4 4 0 1.2-.5 2.3-1.4 3.1.9.8 1.4 1.9 1.4 3.1 0 2.2-1.8 4-4 4s-4-1.8-4-4c0-1.2.5-2.3 1.4-3.1-.9-.8-1.4-1.9-1.4-3.1 0-2.2 1.8-4 4-4z' },
                { id: 'star-8', path: 'M12 2l2 6 6 2-6 2-2 6-2-6-6-2 6-2 2-6z' },
                { id: 'sun', path: 'M12 7a5 5 0 100 10 5 5 0 000-10zM12 1v3M12 20v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M1 12h3M20 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1' },
                { id: 'blob', path: 'M12 2C8.5 2 4 5.5 4 10c0 5 4 12 8 12s8-7 8-12c0-4.5-4.5-8-8-8z' },
                { id: 'circle', path: 'M12 4a8 8 0 100 16 8 8 0 000-16z' },
                { id: 'badge', path: 'M6 4h12a2 2 0 012 2v10l-4 4H6a2 2 0 01-2-2V6a2 2 0 012-2z' },
              ].map((shape) => (
                <button
                  key={shape.id}
                  onClick={() => handleContentChange('imageShape', shape.id)}
                  className={`w-10 h-10 flex items-center justify-center border rounded-xl transition-all ${(block.content.imageShape || 'square') === shape.id
                    ? 'border-slate-800 bg-slate-900 text-white shadow-md ring-2 ring-slate-100'
                    : 'border-slate-200 hover:border-slate-300 bg-white text-slate-400 hover:text-slate-600'
                    }`}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d={shape.path} />
                  </svg>
                </button>
              ))}
            </div>
          </div>

          {/* Alternate text */}
          <div className="space-y-2 px-1 pt-4 border-t border-slate-50">
            <label className="block text-sm font-bold text-slate-900 uppercase tracking-tight">Alternate text</label>
            <input
              type="text"
              value={block.content.alt || ''}
              onChange={(e) => handleContentChange('alt', e.target.value)}
              className="w-full text-sm font-medium bg-slate-50/50 border border-slate-200 rounded-xl hover:border-slate-300 focus:bg-white focus:outline-none focus:border-slate-400 transition-all px-4 py-3 shadow-sm"
              placeholder="Ex: Minimalist office desk"
            />
          </div>

          {/* Text Overlay */}
          <div className="space-y-3 px-1 pt-4 border-t border-slate-50">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-bold text-slate-900 uppercase tracking-tight">Text Overlay</label>
              <button
                type="button"
                onClick={() => updateOverlayText({ enabled: !overlayText.enabled })}
                className={`px-2.5 py-1 text-[11px] font-bold rounded-md border transition ${
                  overlayText.enabled ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'
                }`}
              >
                {overlayText.enabled ? 'On' : 'Off'}
              </button>
            </div>

            {overlayText.enabled && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Headline</label>
                  <textarea
                    value={overlayText.headline || ''}
                    onChange={(e) => updateOverlayText({ headline: e.target.value })}
                    rows={3}
                    className="w-full text-sm bg-slate-50 border border-slate-200 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 px-3 py-2"
                    placeholder="New year&#10;new You"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Subtext</label>
                  <textarea
                    value={overlayText.subtext || ''}
                    onChange={(e) => updateOverlayText({ subtext: e.target.value })}
                    rows={3}
                    className="w-full text-sm bg-slate-50 border border-slate-200 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 px-3 py-2"
                    placeholder="Optional supporting line"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Position</label>
                    <select
                      value={overlayText.position || 'center'}
                      onChange={(e) => updateOverlayText({ position: e.target.value })}
                      className="w-full text-sm bg-slate-50 border border-slate-200 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 px-3 py-2"
                    >
                      <option value="top">Top</option>
                      <option value="center">Center</option>
                      <option value="bottom">Bottom</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Align</label>
                    <select
                      value={overlayText.align || 'center'}
                      onChange={(e) => updateOverlayText({ align: e.target.value })}
                      className="w-full text-sm bg-slate-50 border border-slate-200 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 px-3 py-2"
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Font style</label>
                  <div className="relative" ref={fontFamilyDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setShowFontFamilyDropdown(!showFontFamilyDropdown)}
                      className="w-full flex items-center justify-between px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all text-slate-700"
                      style={{ fontFamily: overlayText.fontFamily || 'Inter' }}
                      onKeyDown={handleFontFamilyKeyDown}
                    >
                      <span className="truncate">{overlayText.fontFamily || 'Inter'}</span>
                      <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                    {showFontFamilyDropdown && (
                      <div className="absolute z-[100] mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-100 max-h-80 overflow-y-auto custom-scrollbar">
                        {['Popular', 'Sans-Serif', 'Serif', 'Display', 'Handwriting', 'Cursive', 'Luxury', 'Decorative', 'Monospace', 'System'].map((category) => {
                          const fontsInCategory = FONT_FAMILIES.filter(f => f.category === category);
                          if (fontsInCategory.length === 0) return null;
                          return (
                            <div key={category}>
                              <div className="sticky top-0 px-3 py-1.5 bg-slate-50 border-y border-slate-100 first:border-t-0 z-10">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{category}</span>
                              </div>
                              {fontsInCategory.map((font) => (
                                <button
                                  key={font.value}
                                  type="button"
                                  onClick={() => {
                                    updateOverlayText({ fontFamily: font.value });
                                    setShowFontFamilyDropdown(false);
                                  }}
                                  className={`w-full px-3 py-2 text-left hover:bg-slate-100 transition-colors ${(overlayText.fontFamily || 'Inter') === font.value ? 'bg-slate-50 text-slate-900 font-bold' : 'text-slate-600'}`}
                                  style={{ fontFamily: font.value }}
                                >
                                  {font.label}
                                </button>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Headline color</label>
                    <div className="relative">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'overlayHeadlineColor' ? null : 'overlayHeadlineColor')}
                        className="w-full flex items-center justify-between border border-slate-200 rounded-xl px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 transition bg-white shadow-sm"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="w-5 h-5 rounded-full border border-slate-200 shadow-inner flex-shrink-0"
                            style={{ backgroundColor: overlayText.headlineColor || '#D9FF4A' }}
                          />
                          <span className="truncate text-xs">{overlayText.headlineColor || '#D9FF4A'}</span>
                        </div>
                        <ChevronDownIcon
                          className={`w-4 h-4 text-slate-500 flex-shrink-0 transition-transform ${showColorPicker === 'overlayHeadlineColor' ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {showColorPicker === 'overlayHeadlineColor' && (
                        <div className="absolute top-full left-0 mt-2 z-20" ref={colorPickerRef}>
                          <CustomColorPicker
                            currentColor={overlayText.headlineColor || '#D9FF4A'}
                            onChange={(color) => updateOverlayText({ headlineColor: color })}
                            pickerType="solid"
                            emailColors={getEmailBackgroundColors()}
                            brandColors={brandColors}
                            showAdvancedPicker={showAdvancedPicker}
                            setShowAdvancedPicker={setShowAdvancedPicker}
                            setAdvancedPickerPosition={setAdvancedPickerPosition}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Subtext color</label>
                    <div className="relative">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'overlaySubtextColor' ? null : 'overlaySubtextColor')}
                        className="w-full flex items-center justify-between border border-slate-200 rounded-xl px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 transition bg-white shadow-sm"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="w-5 h-5 rounded-full border border-slate-200 shadow-inner flex-shrink-0"
                            style={{ backgroundColor: overlayText.subtextColor || '#FFFFFF' }}
                          />
                          <span className="truncate text-xs">{overlayText.subtextColor || '#FFFFFF'}</span>
                        </div>
                        <ChevronDownIcon
                          className={`w-4 h-4 text-slate-500 flex-shrink-0 transition-transform ${showColorPicker === 'overlaySubtextColor' ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {showColorPicker === 'overlaySubtextColor' && (
                        <div className="absolute top-full right-0 mt-2 z-20" ref={colorPickerRef}>
                          <CustomColorPicker
                            currentColor={overlayText.subtextColor || '#FFFFFF'}
                            onChange={(color) => updateOverlayText({ subtextColor: color })}
                            pickerType="solid"
                            emailColors={getEmailBackgroundColors()}
                            brandColors={brandColors}
                            showAdvancedPicker={showAdvancedPicker}
                            setShowAdvancedPicker={setShowAdvancedPicker}
                            setAdvancedPickerPosition={setAdvancedPickerPosition}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                      Headline size ({overlayText.headlineSize || 56}px)
                    </label>
                    <input
                      type="range"
                      min={24}
                      max={96}
                      value={overlayText.headlineSize || 56}
                      onChange={(e) => updateOverlayText({ headlineSize: parseInt(e.target.value, 10) })}
                      className="v2-slider w-full h-1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                      Subtext size ({overlayText.subtextSize || 18}px)
                    </label>
                    <input
                      type="range"
                      min={12}
                      max={36}
                      value={overlayText.subtextSize || 18}
                      onChange={(e) => updateOverlayText({ subtextSize: parseInt(e.target.value, 10) })}
                      className="v2-slider w-full h-1"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Style">
          <div className="p-1 space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Background color</p>
                  <p className="text-xs text-slate-500">Set a color behind the image block</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'imageBg' ? null : 'imageBg')}
                  className="w-full flex items-center justify-between border border-slate-200 rounded-xl px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 transition bg-white shadow-sm"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-5 h-5 rounded-full border border-slate-200 shadow-inner flex-shrink-0"
                      style={{
                        background:
                          currentBackground === 'transparent'
                            ? 'repeating-conic-gradient(#E5E7EB 0% 25%, #F3F4F6 0% 50%)'
                            : currentBackground,
                        backgroundSize: '8px 8px',
                      }}
                    />
                    <span className="truncate text-xs">
                      {currentBackground === 'transparent' ? 'Transparent' : currentBackground}
                    </span>
                  </div>
                  <ChevronDownIcon
                    className={`w-4 h-4 text-slate-500 flex-shrink-0 transition-transform ${showColorPicker === 'imageBg' ? 'rotate-180' : ''}`}
                  />
                </button>
                <div className="relative">
                  <input
                    type="text"
                    value={currentBackground === 'transparent' ? '' : currentBackground}
                    onChange={(e) => handleStyleChange('backgroundColor', e.target.value || 'transparent')}
                    placeholder="None"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-slate-400 bg-slate-50/30 shadow-sm"
                  />
                </div>
              </div>
              {showColorPicker === 'imageBg' && (
                <div className="relative z-10" ref={colorPickerRef}>
                  <div className="absolute top-2 left-0">
                    <CustomColorPicker
                      currentColor={currentBackground === 'transparent' ? '' : currentBackground}
                      onChange={(color) => handleStyleChange('backgroundColor', color)}
                      pickerType="imageBg"
                      emailColors={getEmailBackgroundColors()}
                      brandColors={brandColors}
                      showAdvancedPicker={showAdvancedPicker}
                      setShowAdvancedPicker={setShowAdvancedPicker}
                      setAdvancedPickerPosition={setAdvancedPickerPosition}
                    />
                  </div>
                </div>
              )}
            </div>

          </div>
        </CollapsibleSection>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={async (event) => {
            const file = event.target.files?.[0];
            if (!file || !currentUser) {
              event.target.value = '';
              return;
            }
            setIsUploadingImage(true);
            try {
              // Get image dimensions before upload
              const imageDimensions = await new Promise<{ width: number; height: number }>((resolve) => {
                const img = new Image();
                const objectUrl = URL.createObjectURL(file);
                img.onload = () => {
                  resolve({ width: img.width, height: img.height });
                  URL.revokeObjectURL(objectUrl);
                };
                img.onerror = () => {
                  resolve({ width: 0, height: 0 });
                  URL.revokeObjectURL(objectUrl);
                };
                img.src = objectUrl;
              });

              const uploadedFile = await storageService.uploadFile(
                file,
                currentUser.uid,
                currentWorkspace?.id
              );
              applyContentUpdate({
                src: uploadedFile.url,
                alt: uploadedFile.originalName || '',
                autoWidth: true,
                fileId: uploadedFile.id || getExistingFileId(),
                fileSize: uploadedFile.size,
                imageName: uploadedFile.originalName || uploadedFile.name,
                imageMimeType: uploadedFile.mimeType,
                metadata: {
                  ...block.content.metadata,
                  width: imageDimensions.width || uploadedFile.metadata?.width,
                  height: imageDimensions.height || uploadedFile.metadata?.height,
                },
              });
            } catch (error) {
              console.error('Image upload failed:', error);
            } finally {
              setIsUploadingImage(false);
              event.target.value = '';
            }
          }}
        />

        <div className="border-t border-slate-200 pt-4">
          <button
            className="w-full flex items-center justify-between text-sm font-medium text-slate-800"
            onClick={() => setImageActionOpen((prev) => !prev)}
          >
            Action
            <svg className={`w-4 h-4 transition ${imageActionOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.061l-4.24 4.243a.75.75 0 01-1.06 0L5.25 8.27a.75.75 0 01-.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          {imageActionOpen && (
            <div className="mt-3 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Image link</label>
                <select
                  value={block.content.linkType || 'none'}
                  onChange={(e) => handleContentChange('linkType', e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <option value="none">Do nothing</option>
                  <option value="open-website">Open website</option>
                  <option value="mailto">Send email</option>
                  <option value="tel">Dial phone</option>
                </select>
              </div>
              {(block.content.linkType && block.content.linkType !== 'none') && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                      {block.content.linkType === 'mailto' ? 'Email address' : block.content.linkType === 'tel' ? 'Phone number' : 'URL'}
                    </label>
                    <input
                      type="text"
                      value={block.content.linkUrl || ''}
                      onChange={(e) => handleContentChange('linkUrl', e.target.value)}
                      className="w-full text-sm bg-slate-50 border border-slate-200 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-500/10 transition-all duration-200 px-3 py-2"
                      placeholder={block.content.linkType === 'mailto' ? 'email@example.com' : block.content.linkType === 'tel' ? '+1 (555) 123-4567' : 'https://example.com'}
                    />
                  </div>
                  {block.content.linkType === 'open-website' && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Target</label>
                      <select
                        value={block.content.linkTarget || '_blank'}
                        onChange={(e) => handleContentChange('linkTarget', e.target.value)}
                        className="w-full text-sm bg-slate-50 border border-slate-200 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-500/10 transition-all duration-200 px-3 py-2 cursor-pointer"
                      >
                        <option value="_blank">New tab</option>
                        <option value="_self">Same tab</option>
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 pt-4">
          <button
            className="w-full flex items-center justify-between text-sm font-medium text-slate-800"
            onClick={() => setImageLayoutOpen((prev) => !prev)}
          >
            Layout
            <svg className={`w-4 h-4 transition ${imageLayoutOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.061l-4.24 4.243a.75.75 0 01-1.06 0L5.25 8.27a.75.75 0 01-.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          {imageLayoutOpen && (
            <div className="mt-3 space-y-3 min-w-0">
          <PaddingControl
            values={{
              top: block.styles.paddingTop,
              bottom: block.styles.paddingBottom,
              left: block.styles.paddingLeft,
              right: block.styles.paddingRight,
              padding: block.styles.padding
            }}
            onChange={handleStyleChange}
            onMultiChange={(updates) => {
              onUpdate({
                styles: {
                  ...block.styles,
                  ...updates
                }
              });
            }}
          />
            </div>
          )}
        </div>

        {/* ── Sticker Overlay ── */}
        <div className="border-t border-slate-200 pt-4">
          <button
            type="button"
            className="w-full flex items-center justify-between text-sm font-medium text-slate-800"
            onClick={() => {
              const el = document.getElementById('img-sticker-overlay');
              if (el) el.classList.toggle('hidden');
            }}
          >
            <span className="flex items-center gap-2">
              Sticker Overlay
              {block.content.overlaySticker?.src && <span className="w-2 h-2 rounded-full bg-slate-500 inline-block" />}
            </span>
            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.061l-4.24 4.243a.75.75 0 01-1.06 0L5.25 8.27a.75.75 0 01-.02-1.06z" clipRule="evenodd" />
            </svg>
          </button>
          <div id="img-sticker-overlay" className={block.content.overlaySticker?.src ? 'mt-3 space-y-3' : 'mt-3 space-y-3 hidden'}>
            {(() => {
              const _sm = require('../utils/stickerService');
              const _allSt = _sm.stickerService.getAll();
              const _cats = _sm.STICKER_CATEGORIES as { id: string; label: string; icon: string }[];
              const _ovr = block.content.overlaySticker || {} as any;
              const _bcat = _ovr.browseCategory || 'celebrations';
              return (
                <>
                  <select
                    value={_bcat}
                    onChange={(e) => handleContentChange('overlaySticker', { ..._ovr, browseCategory: e.target.value })}
                    className="w-full text-sm bg-slate-50 border border-slate-200 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-500/10 transition-all duration-200 px-3 py-2 cursor-pointer mb-2"
                  >
                    {_cats.map((c: { id: string; label: string; icon: string }) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                  <div className="grid grid-cols-5 gap-1.5" style={{ maxHeight: '140px', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'thin', scrollbarColor: '#CBD5E1 transparent' }}>
                    {_allSt.filter((s: any) => s.category === _bcat).map((st: any) => (
                      <button key={st.id} type="button" title={st.name}
                        onClick={() => handleContentChange('overlaySticker', { ..._ovr, src: st.src, stickerName: st.name, stickerType: 'builtin', position: _ovr.position || 'top-right', size: _ovr.size || 25, browseCategory: _bcat })}
                        className={`aspect-square rounded-lg border-2 transition-all p-1.5 flex items-center justify-center bg-white hover:shadow-sm ${_ovr.stickerName === st.name ? 'border-slate-400 shadow-md bg-slate-50' : 'border-transparent hover:border-slate-200'}`}>
                        <div className="w-full h-full" dangerouslySetInnerHTML={{ __html: st.src }} />
                      </button>
                    ))}
                  </div>
                  {_ovr.src && (
                    <div className="bg-slate-50 rounded-xl p-3 space-y-3">
                      <div className="flex gap-3 items-start">
                        <div>
                          <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Position</label>
                          <div className="grid grid-cols-3 gap-0.5 bg-slate-200 rounded-lg p-1">
                            {[{ p: 'top-left', l: '↖' }, { p: 'top-center', l: '↑' }, { p: 'top-right', l: '↗' }, { p: 'center-left', l: '←' }, { p: 'center', l: '·' }, { p: 'center-right', l: '→' }, { p: 'bottom-left', l: '↙' }, { p: 'bottom-center', l: '↓' }, { p: 'bottom-right', l: '↘' }].map(({ p, l }) => (
                              <button key={p} type="button" title={p} onClick={() => handleContentChange('overlaySticker', { ..._ovr, position: p })}
                                className={`w-7 h-7 rounded-md text-[11px] font-bold flex items-center justify-center transition-all ${(_ovr.position || 'top-right') === p ? 'bg-slate-500 text-white shadow-sm' : 'bg-white text-slate-400 hover:text-slate-600'}`}>
                                {l}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="flex-1 space-y-2">
                          <div>
                            <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Size: {_ovr.size || 25}%</label>
                            <input type="range" min={10} max={60} value={_ovr.size || 25}
                              onChange={(e) => handleContentChange('overlaySticker', { ..._ovr, size: parseInt(e.target.value, 10) })}
                              className="v2-slider w-full h-1"
                              style={{ background: `linear-gradient(90deg, #0F172A ${(((_ovr.size || 25) - 10) / 50) * 100}%, #E2E8F0 ${(((_ovr.size || 25) - 10) / 50) * 100}%)` }} />
                          </div>
                          <div>
                            <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Rotate: {_ovr.rotation || 0}°</label>
                            <input type="range" min={0} max={360} step={5} value={_ovr.rotation || 0}
                              onChange={(e) => handleContentChange('overlaySticker', { ..._ovr, rotation: parseInt(e.target.value, 10) })}
                              className="v2-slider w-full h-1"
                              style={{ background: `linear-gradient(90deg, #0F172A ${((_ovr.rotation || 0) / 360) * 100}%, #E2E8F0 ${((_ovr.rotation || 0) / 360) * 100}%)` }} />
                          </div>
                          <button type="button" onClick={() => handleContentChange('overlaySticker', { src: '', stickerType: '', stickerName: '', position: 'top-right', size: 25, rotation: 0, browseCategory: _bcat })}
                            className="w-full py-1.5 text-[11px] font-medium text-red-500 hover:text-red-600 rounded-md border border-red-100 hover:bg-red-50 transition-colors">
                            Remove
                          </button>
                        </div>
                      </div >
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>
    );
  };

  const handleButtonFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !currentUser) {
        event.target.value = '';
        return;
      }
      setIsUploadingButtonFile(true);
      try {
        const uploadedFile = await storageService.uploadFile(
          file,
          currentUser.uid,
          currentWorkspace?.id
        );
        applyContentUpdate({
          actionType: 'download-file',
          downloadFileUrl: uploadedFile.url,
          downloadFileName: uploadedFile.originalName || uploadedFile.name,
          downloadFileSize: uploadedFile.size,
          downloadFileId: uploadedFile.id || getExistingFileId(),
          downloadAs: uploadedFile.originalName || uploadedFile.name,
        });
      } catch (error) {
        console.error('Button file upload failed:', error);
      } finally {
        setIsUploadingButtonFile(false);
        event.target.value = '';
      }
    },
    [applyContentUpdate, currentUser, currentWorkspace?.id, getExistingFileId]
  );

  const handleButtonFileRemove = useCallback(() => {
    applyContentUpdate({
      downloadFileUrl: '',
      downloadFileName: '',
      downloadFileSize: null,
      downloadFileId: null,
      downloadAs: '',
    });
  }, [applyContentUpdate]);

  const setButtonActionType = useCallback(
    (action: 'open-url' | 'download-file') => {
      const update: Record<string, any> = { actionType: action };
      if (action === 'open-url' && (!block.content.url || !block.content.url.trim())) {
        update.url = 'https://example.com';
      }
      applyContentUpdate(update);
    },
    [applyContentUpdate, block.content.url]
  );

  const isImageBlock = block.type === 'image';
  const isLogoBlock = block.type === 'logo';
  const isTextBlock = ['text', 'paragraph', 'caption', 'preheader', 'quote', 'list', 'title', 'subtitle', 'heading'].includes(normalizedBlockType);

  const handleImageSelection = (files: StorageFile[]) => {
    const selected = files[0];
    if (!selected) return;

    const targetField = imageTargetFieldRef.current;
    if (targetField) {
      if (targetField.startsWith('products.')) {
        const parts = targetField.split('.');
        const index = parseInt(parts[1]);
        const field = parts[2];
        const nextProducts = [...(block.content.products || [])];
        if (nextProducts[index]) {
          nextProducts[index] = { ...nextProducts[index], [field]: selected.url };
          onUpdate({
            content: {
              ...block.content,
              products: nextProducts,
            },
          });
        }
      } else {
        const libraryUpdates: Record<string, unknown> = { [targetField]: selected.url };
        if (targetField === 'logoUrl') {
          libraryUpdates.imageName = selected.originalName || selected.name;
        }
        onUpdate({
          content: {
            ...block.content,
            ...libraryUpdates,
          },
        });
      }
      imageTargetFieldRef.current = null;
      setIsContentManagerOpen(false);
      return;
    }

    applyContentUpdate({
      src: selected.url,
      alt: selected.originalName || '',
      autoWidth: true,
      fileId: selected.id || selected.name,
      fileSize: selected.size,
      imageName: selected.originalName || selected.name,
      imageMimeType: selected.mimeType,
      metadata: {
        ...(block.content.metadata || {}),
        width: selected.metadata?.width ?? block.content.metadata?.width,
        height: selected.metadata?.height ?? block.content.metadata?.height,
      },
    });
    setIsContentManagerOpen(false);
  };

  const handleStockImageSelection = (image: { full: string; alt: string; provider: string; attribution: string }) => {
    const targetField = imageTargetFieldRef.current;
    if (targetField) {
      if (targetField.startsWith('products.')) {
        const parts = targetField.split('.');
        const index = parseInt(parts[1]);
        const field = parts[2];
        const nextProducts = [...(block.content.products || [])];
        if (nextProducts[index]) {
          nextProducts[index] = { ...nextProducts[index], [field]: image.full };
          onUpdate({
            content: {
              ...block.content,
              products: nextProducts,
            },
          });
        }
      } else {
        const stockUpdates: Record<string, unknown> = { [targetField]: image.full };
        if (targetField === 'logoUrl') {
          stockUpdates.imageName = image.alt || 'stock-logo';
        }
        onUpdate({
          content: {
            ...block.content,
            ...stockUpdates,
          },
        });
      }
      imageTargetFieldRef.current = null;
      setIsStockImageModalOpen(false);
      return;
    }

    applyContentUpdate({
      src: image.full,
      alt: '',
      autoWidth: true,
      imageName: image.alt,
      fileId: null,
      fileSize: null,
      imageMimeType: 'image/jpeg',
      stockProvider: image.provider,
      stockAttribution: image.attribution,
    });
    setIsStockImageModalOpen(false);
  };

  const mobileStyles = block.mobileStyles || {};
  const updateMobileStyle = (key: string, value?: string) => {
    const current = { ...mobileStyles };
    if (value === undefined || value === '') {
      delete current[key];
    } else {
      current[key] = value;
    }
    const next = Object.keys(current).length > 0 ? current : undefined;
    onUpdate({
      mobileStyles: next,
    });
  };
  const resetMobileStyles = () => onUpdate({ mobileStyles: undefined });
  const mobileHasOverrides = Boolean(block.mobileStyles && Object.keys(block.mobileStyles).length > 0);
  const isHiddenOnMobile = mobileStyles.display === 'none';

  // Render style tab content
  const renderStyleContent = () => (
    <div className="space-y-4">
      {/* Background Color */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Background Color</label>
        <div className="relative">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'bgColor' ? null : 'bgColor')}
            className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400 flex items-center gap-3 bg-white hover:border-slate-300 transition-colors"
          >
            <div
              className="w-8 h-8 rounded-lg border border-slate-200 shadow-inner"
              style={{
                backgroundColor: block.styles.backgroundColor || 'transparent',
                backgroundImage: !block.styles.backgroundColor || block.styles.backgroundColor === 'transparent'
                  ? 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%), linear-gradient(-45deg, #e5e7eb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e7eb 75%), linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)'
                  : undefined,
                backgroundSize: '8px 8px',
                backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
              }}
            />
            <span className="text-sm text-slate-700 font-medium">
              {block.styles.backgroundColor || 'Transparent'}
            </span>
          </button>
          {showColorPicker === 'bgColor' && (
            <div ref={colorPickerRef} className="absolute z-10 mt-1 mb-4">
              <CustomColorPicker
                currentColor={block.styles.backgroundColor || 'transparent'}
                onChange={(color) => handleStyleChange('backgroundColor', color)}
                pickerType="bgColor"
                emailColors={getEmailBackgroundColors()}
                brandColors={brandColors}
                showAdvancedPicker={showAdvancedPicker}
                setShowAdvancedPicker={setShowAdvancedPicker}
                setAdvancedPickerPosition={setAdvancedPickerPosition}
              />
            </div>
          )}
        </div>
      </div>


      {/* Divider-specific styles */}
      {block.type === 'divider' && (
        <>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Border Color</label>
            <div className="relative">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'borderColor' ? null : 'borderColor')}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400 flex items-center gap-3 bg-white hover:border-slate-300 transition-colors"
              >
                <div
                  className="w-8 h-8 rounded-lg border border-slate-200 shadow-inner"
                  style={{
                    backgroundColor: block.styles.borderColor || '#E5E7EB',
                  }}
                />
                <span className="text-sm text-slate-700 font-medium">
                  {block.styles.borderColor || '#E5E7EB'}
                </span>
              </button>
              {showColorPicker === 'borderColor' && (
                <div ref={colorPickerRef} className="absolute z-10 mt-1 mb-4">
                  <CustomColorPicker
                    currentColor={block.styles.borderColor || '#E5E7EB'}
                    onChange={(color) => handleStyleChange('borderColor', color)}
                    pickerType="borderColor"
                    emailColors={getEmailBackgroundColors()}
                    brandColors={brandColors}
                    showAdvancedPicker={showAdvancedPicker}
                    setShowAdvancedPicker={setShowAdvancedPicker}
                    setAdvancedPickerPosition={setAdvancedPickerPosition}
                  />
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Border Width</label>
            <input
              type="text"
              value={block.styles.borderWidth || '1px'}
              onChange={(e) => handleStyleChange('borderWidth', e.target.value)}
              className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-500/10 transition-all duration-200"
              placeholder="1px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Line Style</label>
            <div className="relative" ref={dividerStyleDropdownRef}>
              <button
                type="button"
                onClick={() => setShowDividerStyleDropdown((prev) => !prev)}
                className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-500/10 transition-all duration-200 flex items-center justify-between gap-3"
              >
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-700">{selectedDividerOption.label}</div>
                  <div className="mt-2">
                    <div
                      style={{
                        borderTop: `${block.styles.borderWidth || '2px'} ${selectedDividerOption.value} ${block.styles.borderColor || '#475569'}`,
                      }}
                    />
                  </div>
                </div>
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showDividerStyleDropdown && (
                <div className="absolute mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl z-20 overflow-hidden">
                  {DIVIDER_STYLE_OPTIONS.map((option) => {
                    const isActive = (block.styles.borderStyle || 'solid') === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          handleStyleChange('borderStyle', option.value);
                          setShowDividerStyleDropdown(false);
                        }}
                        className={`w-full px-3 py-2.5 text-left flex flex-col gap-1 ${isActive ? 'bg-slate-50' : 'hover:bg-slate-50'}`}
                      >
                        <span className="text-sm font-medium text-slate-700">{option.label}</span>
                        <span
                          style={{
                            borderTop: `${block.styles.borderWidth || '2px'} ${option.value} ${block.styles.borderColor || '#475569'}`,
                            display: 'block',
                          }}
                        />
                      </button>
                    );
                  })}
                 </div>
              )}
            </div>
          </div>
        </>
      )}


    </div>
  );

  // Render spacing content (padding)
  const renderSpacingContent = () => (
      <PaddingControl
        values={{
          top: block.styles.paddingTop,
          bottom: block.styles.paddingBottom,
          left: block.styles.paddingLeft,
          right: block.styles.paddingRight,
          padding: block.styles.padding
        }}
        onChange={handleStyleChange}
        onMultiChange={(updates) => {
          onUpdate({
            styles: {
              ...block.styles,
              ...updates
            }
          });
        }}
      />
  );

  // Render mobile responsive content
  const renderMobileContent = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1.5">Font Size</label>
          <input
            type="text"
            value={mobileStyles.fontSize || ''}
            onChange={(e) => updateMobileStyle('fontSize', e.target.value)}
            placeholder="e.g. 16px"
            className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-500/10 transition-all duration-200"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1.5">Line Height</label>
          <input
            type="text"
            value={mobileStyles.lineHeight || ''}
            onChange={(e) => updateMobileStyle('lineHeight', e.target.value)}
            placeholder="e.g. 150%"
            className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-500/10 transition-all duration-200"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Mobile Padding</label>
        <input
          type="text"
          value={mobileStyles.padding || ''}
          onChange={(e) => updateMobileStyle('padding', e.target.value)}
          placeholder="e.g. 16px 12px"
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
        />
      </div>

      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
        <input
          type="checkbox"
          id={`mobile-hide-${block.id}`}
          checked={isHiddenOnMobile}
          onChange={(e) => updateMobileStyle('display', e.target.checked ? 'none' : undefined)}
          className="w-4 h-4 text-slate-500 border-slate-300 rounded focus:ring-slate-500 cursor-pointer"
        />
        <label htmlFor={`mobile-hide-${block.id}`} className="text-sm text-slate-700 cursor-pointer font-medium select-none">
          Hide this block on mobile
        </label>
      </div>
    </div>
  );

  // Render typography content
  const renderTypographyContent = () => {
    const fontFamily = block.styles.fontFamily || 'Arial';
    const fontWeight = block.styles.fontWeight || '400';
    const rawFontSize = block.styles.fontSize?.toString().replace('px', '');
    const fontSizeValue = rawFontSize !== undefined ? rawFontSize : '16';
    const fontSizeNum = parseInt(fontSizeValue, 10) || 16;

    const lineHeightDefault =
      normalizedBlockType === 'title' ? 110 : normalizedBlockType === 'subtitle' ? 150 : 140;
    const lineHeightNum = block.styles.lineHeight
      ? parseLineHeightPercent(block.styles.lineHeight)
      : lineHeightDefault;

    const letterSpacingPx = parseLetterSpacingPx(block.styles.letterSpacing);
    const letterSpacingTenths = Math.round(letterSpacingPx * 10);

    const handleFontSizeChangeLocal = (value: number | string) => {
      if (value === '') return handleStyleChange('fontSize', '');
      const safeValue = typeof value === 'string' ? parseInt(value, 10) : value;
      if (!isNaN(safeValue)) handleStyleChange('fontSize', `${safeValue}px`);
    };

    const handleLineHeightChangeLocal = (value: number | string) => {
      if (value === '') return handleStyleChange('lineHeight', '');
      const num = typeof value === 'string' ? parseInt(value, 10) : value;
      if (!isNaN(num)) handleStyleChange('lineHeight', formatLineHeightPercent(num));
    };

    const handleLetterSpacingChangeLocal = (px: number) => {
      handleStyleChange('letterSpacing', formatLetterSpacingPx(px));
    };

    return (
      <div className="space-y-6">
        {/* Font Selectors */}
        <div className="grid grid-cols-2 gap-3">
          <div className="relative" ref={fontFamilyDropdownRef}>
            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Font</label>
            <button
              type="button"
              onClick={() => setShowFontFamilyDropdown(!showFontFamilyDropdown)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all text-slate-700"
              style={{ fontFamily }}
              onKeyDown={handleFontFamilyKeyDown}
            >
              <span className="truncate">{fontFamily}</span>
              <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400" />
            </button>
            {showFontFamilyDropdown && (
              <div className="absolute z-[100] mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-100 max-h-80 overflow-y-auto custom-scrollbar">
                {['Popular', 'Sans-Serif', 'Serif', 'Display', 'Handwriting', 'Cursive', 'Luxury', 'Decorative', 'Monospace', 'System'].map((category) => {
                  const fontsInCategory = FONT_FAMILIES.filter(f => f.category === category);
                  if (fontsInCategory.length === 0) return null;
                  return (
                    <div key={category}>
                      <div className="sticky top-0 px-3 py-1.5 bg-slate-50 border-y border-slate-100 first:border-t-0 z-10">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{category}</span>
                      </div>
                      {fontsInCategory.map((font) => (
                        <button
                          key={font.value}
                          type="button"
                          onClick={() => {
                            handleStyleChange('fontFamily', font.value);
                            setShowFontFamilyDropdown(false);
                          }}
                          className={`w-full px-3 py-2 text-left hover:bg-slate-100 transition-colors ${fontFamily === font.value ? 'bg-slate-50 text-slate-900 font-bold' : 'text-slate-600'}`}
                          style={{ fontFamily: font.value }}
                        >
                          {font.label}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Weight</label>
            <select
              value={fontWeight}
              onChange={(e) => handleStyleChange('fontWeight', e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all text-slate-700 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke%3D%22%2394a3b8%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%222%22%20d%3D%22M19%209l-7%207-7-7%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat pr-10"
            >
              <option value="100">Thin</option>
              <option value="200">Extra Light</option>
              <option value="300">Light</option>
              <option value="400">Regular</option>
              <option value="500">Medium</option>
              <option value="600">Semi Bold</option>
              <option value="700">Bold</option>
              <option value="800">Extra Bold</option>
              <option value="900">Black</option>
            </select>
          </div>
        </div>

        {/* Size Slider */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Size <span className="text-slate-500 font-normal ml-1 lowercase font-mono">{fontSizeValue}</span>
            </label>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => handleFontSizeChangeLocal(Math.max(8, fontSizeNum - 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors shadow-sm"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" />
              </svg>
            </button>
            <div className="flex-1 relative flex items-center h-8">
              <input
                type="range"
                min="8"
                max="80"
                value={fontSizeNum}
                onChange={(e) => handleFontSizeChangeLocal(e.target.value)}
                className="v2-slider w-full h-1"
                style={{ background: `linear-gradient(90deg, #0F172A ${((fontSizeNum - 8) / 72) * 100}%, #E2E8F0 ${((fontSizeNum - 8) / 72) * 100}%)` }}
              />
            </div>
            <button
              type="button"
              onClick={() => handleFontSizeChangeLocal(Math.min(80, fontSizeNum + 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors shadow-sm"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>

        {/* Rich text formatting (selection-based) */}
        {['text', 'paragraph', 'caption', 'preheader', 'quote', 'list', 'magic-text'].includes(normalizedBlockType) && (
          <div className="flex items-center justify-between pt-2">
            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Text style <span className="text-slate-500 font-normal ml-1 lowercase font-mono">selection</span>
            </label>
            <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => wrapRichTextSelectionWithHtml('strong')}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-slate-50 text-slate-700 font-bold"
                title="Bold"
              >
                B
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => wrapRichTextSelectionWithHtml('em')}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-slate-50 text-slate-700 italic"
                title="Italic"
              >
                I
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => wrapRichTextSelectionWithHtml('u')}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-slate-50 text-slate-700 underline"
                title="Underline"
              >
                U
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={clearRichTextFormattingInSelection}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-slate-50 text-slate-500"
                title="Clear formatting"
              >
                ⨯
              </button>
            </div>
          </div>
        )}

        {/* Font Color */}
        <div className="space-y-4">
          <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">
            Font color
          </label>
          <div className="relative">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'textColor' ? null : 'textColor')}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400 flex items-center gap-3 bg-white hover:border-slate-300 transition-colors"
            >
              <div
                className="w-8 h-8 rounded-lg border border-slate-200 shadow-inner"
                style={{ backgroundColor: block.styles.color || '#1e293b' }}
              />
              <span className="text-sm text-slate-700 font-medium">
                {block.styles.color || '#1e293b'}
              </span>
            </button>
            {showColorPicker === 'textColor' && (
              <div
                ref={colorPickerRef}
                className="absolute z-50 mt-1 left-0 shadow-2xl animate-in fade-in zoom-in-95 duration-100"
                style={{
                  width: '280px'
                }}
              >
                <CustomColorPicker
                  currentColor={block.styles.color || '#1e293b'}
                  onChange={(color) => handleStyleChange('color', color)}
                  pickerType="textColor"
                  emailColors={getEmailBackgroundColors()}
                  brandColors={brandColors}
                  showAdvancedPicker={showAdvancedPicker}
                  setShowAdvancedPicker={setShowAdvancedPicker}
                  setAdvancedPickerPosition={setAdvancedPickerPosition}
                />
              </div>
            )}
          </div>
        </div>

        {/* Alignment */}
        <div className="space-y-3">
          <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">Align</label>
          <div className="flex items-center gap-1.5 p-1 bg-slate-50 rounded-xl border border-slate-100 w-fit shadow-inner">
            {(['left', 'center', 'right', 'justify'] as const).map((align) => {
              const effectiveAlign =
                block.styles.textAlign ?? (block.type === 'footer' ? 'center' : 'left');
              return (
              <button
                key={align}
                type="button"
                onClick={() => handleStyleChange('textAlign', align)}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  effectiveAlign === align
                    ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-900/5'
                    : 'text-slate-400 hover:text-slate-600 hover:bg-white/50'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {align === 'left' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h13M4 12h9M4 18h13" />}
                  {align === 'center' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 6h10M4 12h16M7 18h10" />}
                  {align === 'right' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 6h13M11 12h9M7 18h13" />}
                  {align === 'justify' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
                </svg>
              </button>
            );
            })}
          </div>
        </div>

        {/* Line Height Slider */}
        <div className="space-y-3 pt-4 border-t border-slate-100">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Line height <span className="text-slate-500 font-normal ml-1 lowercase font-mono">{(lineHeightNum / 100).toFixed(1)}</span>
            </label>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => handleLineHeightChangeLocal(Math.max(100, lineHeightNum - 10))}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors shadow-sm"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" />
              </svg>
            </button>
            <div className="flex-1 relative flex items-center h-8">
              <input
                type="range"
                min="100"
                max="300"
                step="10"
                value={lineHeightNum}
                onChange={(e) => handleLineHeightChangeLocal(parseInt(e.target.value, 10))}
                className="v2-slider w-full h-1"
                style={{ background: `linear-gradient(90deg, #0F172A ${((lineHeightNum - 100) / 200) * 100}%, #E2E8F0 ${((lineHeightNum - 100) / 200) * 100}%)` }}
              />
            </div>
            <button
              type="button"
              onClick={() => handleLineHeightChangeLocal(Math.min(300, lineHeightNum + 10))}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors shadow-sm"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>

        {/* Letter Spacing Slider */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Letter spacing <span className="text-slate-500 font-normal ml-1 lowercase font-mono">{letterSpacingPx.toFixed(1)}</span>
            </label>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => handleLetterSpacingChangeLocal(Math.max(0, letterSpacingPx - 0.1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors shadow-sm"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" />
              </svg>
            </button>
            <div className="flex-1 relative flex items-center h-8">
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={letterSpacingTenths}
                onChange={(e) => handleLetterSpacingChangeLocal(parseInt(e.target.value, 10) / 10)}
                className="v2-slider w-full h-1"
                style={{ background: `linear-gradient(90deg, #0F172A ${(letterSpacingTenths / 100) * 100}%, #E2E8F0 ${(letterSpacingTenths / 100) * 100}%)` }}
              />
            </div>
            <button
              type="button"
              onClick={() => handleLetterSpacingChangeLocal(Math.min(10, letterSpacingPx + 0.1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors shadow-sm"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderHeroOverlayTypography = () => {
    const fontFamily = block.styles.fontFamily || 'Arial';
    const fontWeight = block.styles.fontWeight || '900';
    const rawFontSize = block.styles.fontSize?.toString().replace('px', '');
    const fontSizeNum = parseInt(rawFontSize || '72', 10) || 72;
    const lineHeightNum = parseHeroOverlayLineHeight(block.styles.lineHeight);

    const handleFontSizeChange = (value: number) => {
      handleStyleChange('fontSize', `${Math.max(32, Math.min(120, value))}px`);
    };

    const handleLineHeightChange = (value: number) => {
      handleStyleChange('lineHeight', formatHeroOverlayLineHeight(value));
    };

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-3">
          <div className="relative" ref={fontFamilyDropdownRef}>
            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Font</label>
            <button
              type="button"
              onClick={() => setShowFontFamilyDropdown(!showFontFamilyDropdown)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all text-slate-700"
              style={{ fontFamily }}
            >
              <span className="truncate">{fontFamily}</span>
              <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400" />
            </button>
            {showFontFamilyDropdown && (
              <div className="absolute z-[100] mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-100 max-h-80 overflow-y-auto custom-scrollbar">
                {['Popular', 'Sans-Serif', 'Serif', 'Display', 'Handwriting', 'Cursive', 'Luxury', 'Decorative', 'Monospace', 'System'].map((category) => {
                  const fontsInCategory = FONT_FAMILIES.filter(f => f.category === category);
                  if (fontsInCategory.length === 0) return null;
                  return (
                    <div key={category}>
                      <div className="sticky top-0 px-3 py-1.5 bg-slate-50 border-y border-slate-100 first:border-t-0 z-10">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{category}</span>
                      </div>
                      {fontsInCategory.map((font) => (
                        <button
                          key={font.value}
                          type="button"
                          onClick={() => {
                            handleStyleChange('fontFamily', font.value);
                            setShowFontFamilyDropdown(false);
                          }}
                          className={`w-full px-3 py-2 text-left hover:bg-slate-100 transition-colors ${fontFamily === font.value ? 'bg-slate-50 text-slate-900 font-bold' : 'text-slate-600'}`}
                          style={{ fontFamily: font.value }}
                        >
                          {font.label}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Weight</label>
            <select
              value={fontWeight}
              onChange={(e) => handleStyleChange('fontWeight', e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all text-slate-700 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke%3D%22%2394a3b8%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%222%22%20d%3D%22M19%209l-7%207-7-7%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat pr-10"
            >
              <option value="100">Thin</option>
              <option value="200">Extra Light</option>
              <option value="300">Light</option>
              <option value="400">Regular</option>
              <option value="500">Medium</option>
              <option value="600">Semi Bold</option>
              <option value="700">Bold</option>
              <option value="800">Extra Bold</option>
              <option value="900">Black</option>
            </select>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Heading size <span className="text-slate-500 font-normal ml-1 lowercase font-mono">{fontSizeNum}px</span>
            </label>
          </div>
          <input
            type="range"
            min="32"
            max="120"
            value={fontSizeNum}
            onChange={(e) => handleFontSizeChange(parseInt(e.target.value, 10))}
            className="v2-slider w-full h-1"
            style={{ background: `linear-gradient(90deg, #0F172A ${((fontSizeNum - 32) / 88) * 100}%, #E2E8F0 ${((fontSizeNum - 32) / 88) * 100}%)` }}
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Line height <span className="text-slate-500 font-normal ml-1 lowercase font-mono">{lineHeightNum.toFixed(2)}</span>
            </label>
          </div>
          <input
            type="range"
            min="65"
            max="120"
            step="5"
            value={Math.round(lineHeightNum * 100)}
            onChange={(e) => handleLineHeightChange(parseInt(e.target.value, 10) / 100)}
            className="v2-slider w-full h-1"
            style={{ background: `linear-gradient(90deg, #0F172A ${((lineHeightNum - 0.65) / 0.55) * 100}%, #E2E8F0 ${((lineHeightNum - 0.65) / 0.55) * 100}%)` }}
          />
        </div>

        <div className="space-y-4">
          <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">Text color</label>
          <div className="relative">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'heroOverlayTextColor' ? null : 'heroOverlayTextColor')}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400 flex items-center gap-3 bg-white hover:border-slate-300 transition-colors"
            >
              <div
                className="w-8 h-8 rounded-lg border border-slate-200 shadow-inner"
                style={{ backgroundColor: block.styles.color || '#FFFFFF' }}
              />
              <span className="text-sm text-slate-700 font-medium">{block.styles.color || '#FFFFFF'}</span>
            </button>
            {showColorPicker === 'heroOverlayTextColor' && (
              <div ref={colorPickerRef} className="absolute z-50 mt-1 left-0 shadow-2xl animate-in fade-in zoom-in-95 duration-100" style={{ width: '280px' }}>
                <CustomColorPicker
                  currentColor={block.styles.color || '#FFFFFF'}
                  onChange={(color) => handleStyleChange('color', color)}
                  pickerType="heroOverlayTextColor"
                  emailColors={getEmailBackgroundColors()}
                  brandColors={brandColors}
                  showAdvancedPicker={showAdvancedPicker}
                  setShowAdvancedPicker={setShowAdvancedPicker}
                  setAdvancedPickerPosition={setAdvancedPickerPosition}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderProductTypographyContent = () => {
    const fontFamily = block.styles.productNameFontFamily || 'Arial';
    const fontWeight = block.styles.productNameFontWeight || '600';
    const rawFontSize = block.styles.productNameFontSize?.toString().replace('px', '');
    const fontSizeValue = rawFontSize !== undefined ? rawFontSize : '16';
    const fontSizeNum = parseInt(fontSizeValue, 10) || 16;
    const color = block.styles.productNameColor || '#111827';

    const handleProductFontSizeChange = (value: number | string) => {
      if (value === '') return handleStyleChange('productNameFontSize', '');
      const safeValue = typeof value === 'string' ? parseInt(value, 10) : value;
      if (!isNaN(safeValue)) handleStyleChange('productNameFontSize', `${safeValue}px`);
    };

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-3">
          <div className="relative" ref={productFontFamilyDropdownRef}>
            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Font</label>
            <button
              type="button"
              onClick={() => setShowProductFontFamilyDropdown(!showProductFontFamilyDropdown)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all text-slate-700"
              style={{ fontFamily }}
            >
              <span className="truncate">{fontFamily}</span>
              <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400" />
            </button>
            {showProductFontFamilyDropdown && (
              <div className="absolute z-[100] mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-100 max-h-80 overflow-y-auto custom-scrollbar">
                {['Popular', 'Sans-Serif', 'Serif', 'Display', 'Handwriting', 'Cursive', 'Luxury', 'Decorative', 'Monospace', 'System'].map((category) => {
                  const fontsInCategory = FONT_FAMILIES.filter(f => f.category === category);
                  if (fontsInCategory.length === 0) return null;
                  return (
                    <div key={category}>
                      <div className="sticky top-0 px-3 py-1.5 bg-slate-50 border-y border-slate-100 first:border-t-0 z-10">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{category}</span>
                      </div>
                      {fontsInCategory.map((font) => (
                        <button
                          key={font.value}
                          type="button"
                          onClick={() => {
                            handleStyleChange('productNameFontFamily', font.value);
                            setShowProductFontFamilyDropdown(false);
                          }}
                          className={`w-full px-3 py-2 text-left hover:bg-slate-100 transition-colors ${fontFamily === font.value ? 'bg-slate-50 text-slate-900 font-bold' : 'text-slate-600'}`}
                          style={{ fontFamily: font.value }}
                        >
                          {font.label}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Weight</label>
            <select
              value={fontWeight}
              onChange={(e) => handleStyleChange('productNameFontWeight', e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all text-slate-700 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke%3D%22%2394a3b8%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%222%22%20d%3D%22M19%209l-7%207-7-7%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat pr-10"
            >
              <option value="400">Regular</option>
              <option value="500">Medium</option>
              <option value="600">Semi Bold</option>
              <option value="700">Bold</option>
              <option value="800">Extra Bold</option>
              <option value="900">Black</option>
            </select>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Size <span className="text-slate-500 font-normal ml-1 lowercase font-mono">{fontSizeValue}px</span>
            </label>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => handleProductFontSizeChange(Math.max(8, fontSizeNum - 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors shadow-sm"
            >
              <MinusIcon className="w-3.5 h-3.5" />
            </button>
            <div className="flex-1 relative flex items-center h-8">
              <input
                type="range"
                min="8"
                max="48"
                value={fontSizeNum}
                onChange={(e) => handleProductFontSizeChange(e.target.value)}
                className="v2-slider w-full h-1"
                style={{ background: `linear-gradient(90deg, #0F172A ${((fontSizeNum - 8) / 40) * 100}%, #E2E8F0 ${((fontSizeNum - 8) / 40) * 100}%)` }}
              />
            </div>
            <button
              type="button"
              onClick={() => handleProductFontSizeChange(Math.min(48, fontSizeNum + 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors shadow-sm"
            >
              <PlusIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">
            Font color
          </label>
          <div className="relative">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'productNameColor' ? null : 'productNameColor')}
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all text-slate-700"
            >
              <div className="flex items-center gap-2">
                <div 
                  className="w-5 h-5 rounded-full border border-slate-200"
                  style={{ backgroundColor: color }}
                />
                <span className="font-mono uppercase">{color}</span>
              </div>
              <ChevronDownIcon className="w-4 h-4 text-slate-400" />
            </button>
            {showColorPicker === 'productNameColor' && (
              <div ref={colorPickerRef} className="absolute left-0 top-full mt-2 z-[110] animate-in fade-in slide-in-from-top-2">
                <CustomColorPicker
                  currentColor={color}
                  onChange={(c) => handleStyleChange('productNameColor', c)}
                  pickerType="productNameColor"
                  emailColors={getEmailBackgroundColors()}
                  brandColors={brandColors}
                  showAdvancedPicker={showAdvancedPicker}
                  setShowAdvancedPicker={setShowAdvancedPicker}
                  setAdvancedPickerPosition={setAdvancedPickerPosition}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderProductPriceTypographyContent = () => {
    const fontFamily = block.styles.productPriceFontFamily || 'Arial';
    const fontWeight = block.styles.productPriceFontWeight || '700';
    const rawFontSize = block.styles.productPriceFontSize?.toString().replace('px', '');
    const fontSizeValue = rawFontSize !== undefined ? rawFontSize : '15';
    const fontSizeNum = parseInt(fontSizeValue, 10) || 15;
    const color = block.styles.productPriceColor || '#6B7280';

    const handleProductPriceFontSizeChange = (value: number | string) => {
      if (value === '') return handleStyleChange('productPriceFontSize', '');
      const safeValue = typeof value === 'string' ? parseInt(value, 10) : value;
      if (!isNaN(safeValue)) handleStyleChange('productPriceFontSize', `${safeValue}px`);
    };

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-3">
          <div className="relative">
            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Weight</label>
            <select
              value={fontWeight}
              onChange={(e) => handleStyleChange('productPriceFontWeight', e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all text-slate-700 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke%3D%22%2394a3b8%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%222%22%20d%3D%22M19%209l-7%207-7-7%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat pr-10"
            >
              <option value="400">Regular</option>
              <option value="500">Medium</option>
              <option value="600">Semi Bold</option>
              <option value="700">Bold</option>
              <option value="800">Extra Bold</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Price Color</label>
            <div className="relative">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'productPriceColor' ? null : 'productPriceColor')}
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all text-slate-700"
              >
                <div className="flex items-center gap-2">
                  <div 
                    className="w-5 h-5 rounded-full border border-slate-200"
                    style={{ backgroundColor: color }}
                  />
                  <span className="font-mono uppercase">{color}</span>
                </div>
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              </button>
              {showColorPicker === 'productPriceColor' && (
                <div ref={colorPickerRef} className="absolute right-0 top-full mt-2 z-[110] animate-in fade-in slide-in-from-top-2">
                  <CustomColorPicker
                    currentColor={color}
                    onChange={(c) => handleStyleChange('productPriceColor', c)}
                    pickerType="productPriceColor"
                    emailColors={getEmailBackgroundColors()}
                    brandColors={brandColors}
                    showAdvancedPicker={showAdvancedPicker}
                    setShowAdvancedPicker={setShowAdvancedPicker}
                    setAdvancedPickerPosition={setAdvancedPickerPosition}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Price Size <span className="text-slate-500 font-normal ml-1 lowercase font-mono">{fontSizeValue}px</span>
            </label>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => handleProductPriceFontSizeChange(Math.max(8, fontSizeNum - 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors shadow-sm"
            >
              <MinusIcon className="w-3.5 h-3.5" />
            </button>
            <div className="flex-1 relative flex items-center h-8">
              <input
                type="range"
                min="8"
                max="48"
                value={fontSizeNum}
                onChange={(e) => handleProductPriceFontSizeChange(e.target.value)}
                className="v2-slider w-full h-1"
                style={{ background: `linear-gradient(90deg, #0F172A ${((fontSizeNum - 8) / 40) * 100}%, #E2E8F0 ${((fontSizeNum - 8) / 40) * 100}%)` }}
              />
            </div>
            <button
              type="button"
              onClick={() => handleProductPriceFontSizeChange(Math.min(48, fontSizeNum + 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors shadow-sm"
            >
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render layout tab content (padding)
  const renderLayoutContent = () => (
    <div className="space-y-4 p-1">
      {/* Spacing Section */}
      <div className="mb-2">
        {renderSpacingContent()}
      </div>
    </div>
  );


  const editorialPropertyCtx: EditorialPropertyContext = {
    block,
    handleContentChange,
    handleStyleChange,
    imageSourceDropRef,
    isImageSourceDropOpen,
    setIsImageSourceDropOpen,
    imageTargetFieldRef,
    fileInputRef,
    isUploadingImage,
    setIsStockImageModalOpen,
    setContentManagerTab,
    setIsContentManagerOpen,
    ImageSourceFromCanvaRow,
    showColorPicker,
    setShowColorPicker,
    colorPickerRef,
    getEmailBackgroundColors,
    brandColors,
    showAdvancedPicker,
    setShowAdvancedPicker,
    setAdvancedPickerPosition,
  };

  const renderContentFields = () => {
    switch (normalizedBlockType) {
      case 'magic-text': {
        return (
          <div className="space-y-6">
            {/* AI Writer Section */}
            <div className="bg-gradient-to-br from-slate-50 to-slate-50 rounded-xl border border-slate-100 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-4 text-slate-950">
                <div className="p-1.5 bg-white rounded-lg shadow-sm border border-slate-100">
                  <SparklesIcon className="w-4 h-4 text-slate-900" />
                </div>
                <span className="font-semibold text-sm">AI Magic Writer</span>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-950 mb-1.5">
                    What should we write about?
                  </label>
                  <textarea
                    value={block.content.magicTopic || ''}
                    onChange={(e) => handleContentChange('magicTopic', e.target.value)}
                    placeholder="e.g., Welcome email for new users..."
                    rows={3}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent bg-white shadow-sm placeholder:text-slate-300 resize-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-950 mb-1.5">
                    Tone of voice
                  </label>
                  <div className="relative">
                    <select
                      value={block.content.magicTone || 'professional'}
                      onChange={(e) => handleContentChange('magicTone', e.target.value)}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent bg-white shadow-sm appearance-none cursor-pointer text-slate-700"
                    >
                      <option value="professional">Professional</option>
                      <option value="friendly">Friendly</option>
                      <option value="witty">Witty</option>
                      <option value="excited">Excited</option>
                      <option value="urgent">Urgent</option>
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleMagicGenerate}
                  disabled={isGenerating || !block.content.magicTopic}
                  className={`w-full py-2.5 px-4 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all shadow-sm ${isGenerating || !block.content.magicTopic
                    ? 'bg-slate-200 text-white cursor-not-allowed'
                    : 'bg-slate-900 hover:bg-slate-800 text-white hover:shadow-md active:transform active:scale-[0.98]'
                    }`}
                >
                  {isGenerating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Writing magic...</span>
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="w-4 h-4" />
                      <span>Generate Content</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Separator */}
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Manual Edit
                </span>
              </div>
            </div>

            {/* Manual Edit Section */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Text Content</label>
                <textarea
                  value={block.content.text || ''}
                  onChange={(e) => handleContentChange('text', e.target.value)}
                  placeholder="Enter text..."
                  rows={6}
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-500/10 transition-all duration-200"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="text-sm font-medium text-slate-700">Size</label>
                    <span className="text-xs text-slate-400 font-medium">
                      {parseInt(block.styles?.fontSize?.toString().replace('px', '') ?? '16')}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button 
                      type="button" 
                      onClick={() => {
                        const current = parseInt(block.styles?.fontSize?.toString().replace('px', '') ?? '16');
                        handleStyleChange('fontSize', `${Math.max(8, current - 1)}px`);
                      }}
                      className="text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <MinusIcon className="w-4 h-4" />
                    </button>
                    <input
                      type="range"
                      min="8"
                      max="64"
                      step="1"
                      value={parseInt(block.styles?.fontSize?.toString().replace('px', '') ?? '16')}
                      onChange={(e) => handleStyleChange('fontSize', `${e.target.value}px`)}
                      className="v2-spacing-slider flex-1"
                      style={{ background: `linear-gradient(90deg, #64748b ${((parseInt(block.styles?.fontSize?.toString().replace('px', '') ?? '16') - 8) / 56) * 100}%, #f1f5f9 ${((parseInt(block.styles?.fontSize?.toString().replace('px', '') ?? '16') - 8) / 56) * 100}%)` }}
                    />
                    <button 
                      type="button" 
                      onClick={() => {
                        const current = parseInt(block.styles?.fontSize?.toString().replace('px', '') ?? '16');
                        handleStyleChange('fontSize', `${Math.min(64, current + 1)}px`);
                      }}
                      className="text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <PlusIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Align</label>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    {['left', 'center', 'right', 'justify'].map((align) => (
                      <button
                        key={align}
                        type="button"
                        onClick={() => handleStyleChange('textAlign', align)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                          (block.styles?.textAlign || 'left') === align
                            ? 'bg-slate-100 text-slate-900 shadow-sm'
                            : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                        }`}
                        title={align.charAt(0).toUpperCase() + align.slice(1)}
                      >
                        {align === 'left' && (
                          <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h16" />
                          </svg>
                        )}
                        {align === 'center' && (
                          <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M7 12h10M4 18h16" />
                          </svg>
                        )}
                        {align === 'right' && (
                          <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M10 12h10M4 18h16" />
                          </svg>
                        )}
                        {align === 'justify' && (
                          <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'accordion': {
        const items = (block.content.items as any[]) || [];

        const handleItemChange = (index: number, field: string, value: string) => {
          const newItems = [...items];
          newItems[index] = { ...newItems[index], [field]: value };
          handleContentChange('items', newItems);
        };

        const addItem = () => {
          handleContentChange('items', [...items, { title: 'New Item', content: 'Content goes here' }]);
        };

        const removeItem = (index: number) => {
          const newItems = items.filter((_, i) => i !== index);
          handleContentChange('items', newItems);
        };

        return (
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-2 text-slate-700">
                <ListBulletIcon className="w-4 h-4" />
                <span className="text-sm font-semibold">Accordion Items</span>
              </div>
              <p className="text-xs text-slate-500 mb-3">Add expandable sections for FAQs or details.</p>

              <div className="space-y-3">
                {items.map((item, index) => (
                  <div key={index} className="bg-white p-3 rounded-md border border-slate-200 shadow-sm">
                    <div className="flex justify-between items-center mb-2 pb-2 border-b border-slate-100">
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Item {index + 1}</span>
                      <button
                        onClick={() => removeItem(index)}
                        className="text-slate-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-slate-100"
                        title="Remove item"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Title</label>
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) => handleItemChange(index, 'title', e.target.value)}
                          placeholder="Question or Title"
                          className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-500/10 transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Content</label>
                        <textarea
                          value={item.content}
                          onChange={(e) => handleItemChange(index, 'content', e.target.value)}
                          placeholder="Answer or Content"
                          rows={2}
                          className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-500/10 transition-all duration-200"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={addItem}
                className="mt-4 w-full py-2 px-3 flex items-center justify-center gap-1.5 text-sm font-medium text-slate-800 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg transition-all shadow-sm"
              >
                <PlusIcon className="w-4 h-4" />
                Add New Item
              </button>
            </div>
          </div>
        );
      }

      case 'image-carousel': {
        const images = (block.content.images as any[]) || [];

        const handleImageChange = (index: number, field: string, value: string) => {
          const newImages = [...images];
          newImages[index] = { ...newImages[index], [field]: value };
          handleContentChange('images', newImages);
        };

        const addImage = () => {
          handleContentChange('images', [...images, { src: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=400&q=80', alt: 'New Image' }]);
        };

        const removeImage = (index: number) => {
          const newImages = images.filter((_, i) => i !== index);
          handleContentChange('images', newImages);
        };

        return (
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-2 text-slate-700">
                <PhotoIcon className="w-4 h-4" />
                <span className="text-sm font-semibold">Carousel Settings</span>
              </div>

              {/* Global Dimensions */}
              <div className="grid grid-cols-2 gap-2 mb-4 bg-white p-2 rounded border border-slate-200">
                <div>
                  <label className="text-[10px] uppercase text-slate-500 font-semibold mb-1 block">Card Width</label>
                  <input
                    type="text"
                    value={block.content.carouselImageWidth || '200px'}
                    onChange={(e) => handleContentChange('carouselImageWidth', e.target.value)}
                    className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:border-slate-500"
                    placeholder="e.g. 200px"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-500 font-semibold mb-1 block">Card Height</label>
                  <input
                    type="text"
                    value={block.content.carouselImageHeight || '150px'}
                    onChange={(e) => handleContentChange('carouselImageHeight', e.target.value)}
                    className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:border-slate-500"
                    placeholder="e.g. 150px"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 mb-2 text-slate-700">
                <span className="text-sm font-semibold">Slides</span>
              </div>

              <div className="space-y-4">
                {images.map((img, index) => (
                  <div key={index} className="bg-white p-3 rounded-md border border-slate-200 shadow-sm flex flex-col gap-3">
                    <div className="flex gap-3">
                      <div className="w-16 h-16 bg-slate-100 rounded border border-slate-200 flex-shrink-0 overflow-hidden relative group">
                        {img.src ? (
                          <img
                            src={img.src}
                            alt={img.alt}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent && !parent.querySelector('.img-fallback')) {
                                const fallback = document.createElement('div');
                                fallback.className = 'img-fallback';
                                fallback.style.cssText = 'width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#94A3B8;font-size:10px;text-align:center;padding:4px;';
                                fallback.textContent = 'Invalid URL';
                                parent.insertBefore(fallback, target);
                              }
                            }}
                            onLoad={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = '';
                              const parent = target.parentElement;
                              const fallback = parent?.querySelector('.img-fallback');
                              if (fallback) fallback.remove();
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400 text-[10px]">No Image</div>
                        )}
                        <button
                          onClick={() => {
                            setCarouselUploadIndex(index);
                            carouselFileInputRef.current?.click();
                          }}
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                          title="Upload image"
                        >
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                        </button>
                      </div>
                      <div className="flex-1 space-y-2 min-w-0">
                        <input
                          type="text"
                          value={img.src}
                          onChange={(e) => handleImageChange(index, 'src', e.target.value)}
                          placeholder="Image URL"
                          className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:border-slate-500"
                        />
                        <input
                          type="text"
                          value={img.alt}
                          onChange={(e) => handleImageChange(index, 'alt', e.target.value)}
                          placeholder="Alt text"
                          className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:border-slate-500"
                        />
                      </div>
                      <button
                        onClick={() => removeImage(index)}
                        className="text-slate-400 hover:text-red-500 transition-colors self-start"
                        title="Remove image"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Resize Controls */}
                    <div className="flex gap-4 items-start pt-2 border-t border-slate-100 mt-2">
                      <div>
                        <label className="text-[10px] uppercase text-slate-500 font-semibold mb-2 block">Position</label>
                        <div className="grid grid-cols-3 gap-1 p-1 bg-slate-50 border border-slate-200 rounded-lg w-fit shadow-sm">
                          {[
                            'left top', 'center top', 'right top',
                            'left center', 'center center', 'right center',
                            'left bottom', 'center bottom', 'right bottom'
                          ].map((pos) => {
                            const isSelected = (img.objectPosition || 'center center') === pos;
                            return (
                              <button
                                key={pos}
                                onClick={() => handleImageChange(index, 'objectPosition', pos)}
                                className={`w-6 h-6 rounded-md flex items-center justify-center transition-all ${isSelected
                                  ? 'bg-slate-800 shadow-sm ring-1 ring-slate-800 scale-105 z-10'
                                  : 'bg-white hover:bg-slate-200 border border-transparent opacity-70 hover:opacity-100'
                                  }`}
                                title={pos}
                              >
                                <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-slate-400'}`} />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex-1 pt-6">
                        {/* Edit button hidden for now, but preserved for future use */}
                        {img.src && (
                          <>
                            <button
                              onClick={() => {
                                if (!img.src) return;
                                setCarouselEditingIndex(index);
                                setImageEditorInitialSrc(img.src);
                                setIsImageEditorOpen(true);
                              }}
                              disabled={!img.src}
                              className="w-full px-3 py-2 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm transition-all group"
                            >
                              <svg className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                              Edit Image
                            </button>
                            <p className="text-[10px] text-slate-400 mt-2 text-center">
                              Crop or adjust focus
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Hidden file input for carousel */}
              <input
                ref={carouselFileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (!file || !currentUser || carouselUploadIndex === null) {
                    event.target.value = '';
                    return;
                  }

                  try {
                    const uploadedFile = await storageService.uploadFile(
                      file,
                      currentUser.uid,
                      currentWorkspace?.id
                    );

                    const newImages = [...images];
                    newImages[carouselUploadIndex] = {
                      ...newImages[carouselUploadIndex],
                      src: uploadedFile.url,
                      alt: uploadedFile.originalName || newImages[carouselUploadIndex].alt
                    };
                    handleContentChange('images', newImages);
                  } catch (error) {
                    console.error('Failed to upload carousel image:', error);
                    alert('Failed to upload image. Please try again.');
                  } finally {
                    setCarouselUploadIndex(null);
                    if (carouselFileInputRef.current) {
                      carouselFileInputRef.current.value = '';
                    }
                  }
                }}
              />

              <button
                onClick={addImage}
                className="mt-4 w-full py-2 px-3 flex items-center justify-center gap-1.5 text-sm font-medium text-slate-800 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg transition-all shadow-sm"
              >
                <PlusIcon className="w-4 h-4" />
                Add Image
              </button>
            </div>
          </div>
        );
      }

      case 'list': {
        const items = Array.isArray(block.content.items)
          ? block.content.items
          : (typeof block.content.text === 'string' && block.content.text.trim()
            ? block.content.text.split('\n').filter((line: string) => line.trim())
            : ['List item 1', 'List item 2', 'List item 3']);

        const listType = block.content.listType === 'number' || block.content.listType === 'ordered' ? 'ordered' : (block.content.listType === 'check' ? 'check' : (block.content.listType === 'none' ? 'none' : 'unordered'));
        const currentBackground = (block.styles.backgroundColor as string) || 'transparent';
        const markerColor = (block.styles.markerColor as string) || block.styles.color || '#374151';

        const handleItemChange = (index: number, value: string) => {
          const newItems = [...items];
          newItems[index] = value;
          handleContentChange('items', newItems);
        };

        const addItem = () => {
          handleContentChange('items', [...items, `List item ${items.length + 1}`]);
        };

        const removeItem = (index: number) => {
          if (items.length <= 1) return;
          const newItems = items.filter((_, i) => i !== index);
          handleContentChange('items', newItems);
        };

        return (
          <div className="space-y-6">
            <CollapsibleSection title="Content">
              <div className="space-y-4 pt-2">
                <div className="flex items-center justify-between px-1">
                  <label className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">List Items</label>
                  <span className="text-[10px] text-slate-300 font-medium font-mono">{items.length} total</span>
                </div>
                
                <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1 custom-scrollbar">
                  {items.map((item, index) => (
                    <div key={index} className="flex gap-2 group">
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => handleItemChange(index, e.target.value)}
                          placeholder={`Item ${index + 1}`}
                          className="w-full px-3 py-2.5 text-sm bg-white border border-slate-200 rounded-xl hover:border-slate-300 focus:outline-none focus:ring-4 focus:ring-slate-500/5 focus:border-slate-400 transition-all shadow-sm font-medium"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addItem();
                            }
                          }}
                        />
                      </div>
                      {items.length > 1 && (
                        <button
                          onClick={() => removeItem(index)}
                          className="p-2.5 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all border border-transparent"
                          title="Remove item"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <button
                  onClick={addItem}
                  className="w-full py-3 px-4 flex items-center justify-center gap-2 text-xs font-bold text-slate-900 bg-slate-50 hover:bg-slate-900 hover:text-white border border-slate-200 rounded-xl transition-all group"
                >
                  <PlusIcon className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
                  Add New Item
                </button>
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Typography">
              <div className="pt-2">
                {renderTypographyContent()}
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="List Style">
              <div className="space-y-6 pt-2">
                {/* Variant */}
                <div className="space-y-3">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-1">Variant</p>
                  <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200/60 shadow-inner">
                    {[
                      { id: 'unordered', icon: <ListBulletIcon className="w-4 h-4" />, label: 'Bullets' },
                      { id: 'ordered', icon: <span className="text-[10px] font-bold px-0.5">1.</span>, label: 'Numbered' },
                      { id: 'check', icon: <span className="text-xs font-bold px-0.5">✓</span>, label: 'Check' },
                      { id: 'none', icon: <span className="text-[9px] font-bold px-0.5">None</span>, label: 'Plain' },
                    ].map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => handleContentChange('listType', type.id)}
                        className={`flex-1 flex items-center justify-center py-2 rounded-md transition-all ${listType === type.id
                          ? 'bg-slate-900 text-white shadow-sm ring-1 ring-slate-900/5'
                          : 'text-slate-500 hover:text-slate-700'
                          }`}
                        title={type.label}
                      >
                        {type.icon}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Marker Styling */}
                <div className="space-y-4 border-t border-slate-100/60 pt-5">
                  <div>
                    <div className="flex items-center justify-between mb-3 px-1">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Marker Color</p>
                      <button 
                        onClick={() => handleStyleChange('markerColor', block.styles.color)}
                        className="text-[9px] font-bold text-slate-300 hover:text-slate-500 uppercase tracking-tighter"
                      >
                        Sync with text
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'markerCol' ? null : 'markerCol')}
                        className="w-full flex items-center justify-between border border-slate-200 rounded-xl px-3 py-2.5 bg-white shadow-sm hover:border-slate-300 transition-all font-medium"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="w-4 h-4 rounded-full border border-slate-100 shadow-inner"
                            style={{ backgroundColor: markerColor }}
                          />
                          <span className="text-[11px] text-slate-600 truncate">{markerColor}</span>
                        </div>
                        <ChevronDownIcon className={`w-3.5 h-3.5 text-slate-400 transition-transform ${showColorPicker === 'markerCol' ? 'rotate-180' : ''}`} />
                      </button>
                      <input
                        type="text"
                        value={markerColor}
                        onChange={(e) => handleStyleChange('markerColor', e.target.value)}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-[11px] font-bold focus:outline-none focus:ring-4 focus:ring-slate-900/5 transition-all bg-slate-50/30 text-center"
                        placeholder="#000000"
                      />
                    </div>
                    {showColorPicker === 'markerCol' && (
                      <div className="relative z-10" ref={colorPickerRef}>
                        <div className="absolute top-2 left-0">
                          <CustomColorPicker
                            currentColor={markerColor}
                            onChange={(color) => handleStyleChange('markerColor', color)}
                            pickerType="markerCol"
                            emailColors={getEmailBackgroundColors()}
                            brandColors={brandColors}
                            showAdvancedPicker={showAdvancedPicker}
                            setShowAdvancedPicker={setShowAdvancedPicker}
                            setAdvancedPickerPosition={setAdvancedPickerPosition}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 px-1">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Item Gaps</label>
                      <span className="text-[10px] text-slate-900 font-bold font-mono">{block.styles.listItemSpacing || '12px'}</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="40"
                      step="2"
                      value={parseInt((block.styles.listItemSpacing as string || '12px').replace('px', ''))}
                      onChange={(e) => handleStyleChange('listItemSpacing', `${e.target.value}px`)}
                      className="v2-slider w-full h-1.5"
                      style={{
                        background: `linear-gradient(90deg, #0F172A ${(parseInt((block.styles.listItemSpacing as string || '12px').replace('px', '')) / 40) * 100}%, #E2E8F0 ${(parseInt((block.styles.listItemSpacing as string || '12px').replace('px', '')) / 40) * 100}%)`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Layout">
              <div className="space-y-6 pt-2">
                <div className="space-y-3">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-1">Block Background</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'listBg' ? null : 'listBg')}
                      className="w-full flex items-center justify-between border border-slate-200 rounded-xl px-3 py-2.5 bg-white shadow-sm hover:border-slate-300 transition-all font-medium"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="w-4 h-4 rounded-full border border-slate-100 shadow-inner"
                          style={{ 
                            background: currentBackground === 'transparent' ? 'repeating-conic-gradient(#E5E7EB 0% 25%, #F3F4F6 0% 50%)' : currentBackground,
                            backgroundSize: '8px 8px'
                          }}
                        />
                        <span className="text-[11px] text-slate-600 truncate">{currentBackground === 'transparent' ? 'None' : currentBackground}</span>
                      </div>
                      <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                    <input
                      type="text"
                      value={currentBackground === 'transparent' ? '' : currentBackground}
                      onChange={(e) => handleStyleChange('backgroundColor', e.target.value || 'transparent')}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-[11px] font-bold focus:outline-none focus:ring-4 focus:ring-slate-900/5 transition-all text-center bg-slate-50/30"
                      placeholder="Hex code"
                    />
                  </div>
                  {showColorPicker === 'listBg' && (
                    <div className="relative z-10" ref={colorPickerRef}>
                      <div className="absolute top-2 left-0">
                        <CustomColorPicker
                          currentColor={currentBackground === 'transparent' ? '' : currentBackground}
                          onChange={(color) => handleStyleChange('backgroundColor', color)}
                          pickerType="listBg"
                          emailColors={getEmailBackgroundColors()}
                          brandColors={brandColors}
                          showAdvancedPicker={showAdvancedPicker}
                          setShowAdvancedPicker={setShowAdvancedPicker}
                          setAdvancedPickerPosition={setAdvancedPickerPosition}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-slate-100/60">
                  <PaddingControl
                    values={{
                      top: block.styles.paddingTop,
                      bottom: block.styles.paddingBottom,
                      left: block.styles.paddingLeft,
                      right: block.styles.paddingRight,
                      padding: block.styles.padding
                    }}
                    onChange={handleStyleChange}
                    onMultiChange={(updates) => {
                      onUpdate({
                        styles: {
                          ...block.styles,
                          ...updates
                        }
                      });
                    }}
                  />
                </div>
              </div>
            </CollapsibleSection>
          </div>
        );
      }

      case 'sticker': {
        const stickerSrc = block.content.src || '';
        const stickerWidth = parseInt((block.content.width || '120px').replace('px', ''), 10);
        const stickerType = block.content.stickerType || 'builtin';
        const isSvgSticker =
          (stickerType === 'builtin' || stickerType === 'badge') && stickerSrc.startsWith('<svg');

        // Lazy-import sticker service to avoid circular deps
        const stickerServiceModule = require('../utils/stickerService');
        const { stickerService, STICKER_CATEGORIES } = stickerServiceModule;
        const allBuiltinStickers = stickerService.getAll();

        return (
          <div className="space-y-4">
            {/* Tab bar */}
            <div className="flex border-b border-slate-200">
              {[
                { key: 'builtin', label: '🩹 Stickers' },
                { key: 'giphy', label: '✨ Animated' },
                { key: 'badge', label: '🏷️ Badges' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => handleContentChange('stickerType', tab.key)}
                  className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${stickerType === tab.key
                    ? 'text-slate-800 border-b-2 border-slate-500'
                    : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Built-in stickers tab */}
            {stickerType === 'builtin' && (
              <div>
                {/* Category chips */}
                <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-hide mb-3">
                  {(STICKER_CATEGORIES as { id: string; label: string; icon: string }[])
                    .filter((cat: { id: string }) => cat.id !== 'badges')
                    .map((cat: { id: string; label: string; icon: string }) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => handleContentChange('stickerCategory', cat.id)}
                        className={`px-2.5 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors flex items-center gap-1 ${(block.content.stickerCategory || 'celebrations') === cat.id
                          ? 'bg-slate-100 text-slate-700 border border-slate-200'
                          : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                          }`}
                      >
                        <span>{cat.icon}</span>
                        <span>{cat.label}</span>
                      </button>
                    ))}
                </div>

                {/* Sticker grid */}
                <div className="grid grid-cols-4 gap-2 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
                  {allBuiltinStickers
                    .filter((s: { category: string }) => s.category === (block.content.stickerCategory || 'celebrations') && s.category !== 'badges')
                    .map((sticker: { id: string; name: string; src: string }) => (
                      <button
                        key={sticker.id}
                        type="button"
                        onClick={() => {
                          applyContentUpdate({
                            src: sticker.src,
                            stickerName: sticker.name,
                            alt: sticker.name,
                            stickerType: 'builtin',
                          });
                        }}
                        className={`relative aspect-square rounded-xl border-2 transition-all p-2 flex items-center justify-center bg-white hover:shadow-md ${block.content.stickerName === sticker.name
                          ? 'border-slate-400 shadow-md ring-2 ring-slate-100'
                          : 'border-slate-100 hover:border-slate-300'
                          }`}
                        title={sticker.name}
                      >
                        <div
                          className="w-full h-full"
                          dangerouslySetInnerHTML={{ __html: sticker.src }}
                        />
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* GIPHY stickers tab */}
            {stickerType === 'giphy' && (
              <div>
                {/* Search input */}
                <div className="relative mb-3">
                  <input
                    type="text"
                    value={stickerGiphySearch}
                    onChange={(e) => setStickerGiphySearch(e.target.value)}
                    placeholder="Search animated stickers..."
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 transition-shadow"
                  />
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                {/* Quick categories */}
                <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-hide mb-3">
                  {['love', 'happy', 'thumbs up', 'excited', 'cool', 'celebrate', 'wow', 'fire'].map((term) => (
                    <button
                      key={term}
                      type="button"
                      onClick={() => setStickerGiphySearch(term)}
                      className={`px-2.5 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors capitalize ${stickerGiphySearch === term
                        ? 'bg-slate-100 text-slate-700 border border-slate-200'
                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                        }`}
                    >
                      {term}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto pr-1 custom-scrollbar min-h-[100px]">
                  {stickerGiphyLoading ? (
                    <div className="col-span-3 flex items-center justify-center py-8 text-slate-400">
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Loading...
                    </div>
                  ) : stickerGiphyResults.length > 0 ? (
                    stickerGiphyResults.map((gif) => (
                      <button
                        key={gif.id}
                        type="button"
                        onClick={() => {
                          applyContentUpdate({
                            src: gif.url,
                            alt: gif.alt,
                            stickerName: gif.alt,
                            stickerType: 'giphy',
                          });
                        }}
                        className="relative w-full aspect-square rounded-xl overflow-hidden border-2 border-transparent hover:border-slate-400 hover:shadow-md focus:outline-none group transition-all bg-slate-50"
                        title={gif.alt}
                      >
                        <img
                          src={gif.url}
                          alt={gif.alt}
                          loading="lazy"
                          className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                        />
                      </button>
                    ))
                  ) : (
                    <div className="col-span-3 py-8 text-center text-slate-400 text-xs">
                      {stickerGiphySearch ? 'No stickers found' : 'Search for animated stickers'}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Badges tab */}
            {stickerType === 'badge' && (
              <div className="grid grid-cols-2 gap-2.5 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
                {allBuiltinStickers
                  .filter((s: { category: string }) => s.category === 'badges')
                  .map((sticker: { id: string; name: string; src: string }) => (
                    <button
                      key={sticker.id}
                      type="button"
                      onClick={() => {
                        applyContentUpdate({
                          src: sticker.src,
                          stickerName: sticker.name,
                          alt: sticker.name,
                          stickerType: 'badge',
                        });
                      }}
                      className={`relative rounded-xl border-2 transition-all p-3 flex items-center justify-center bg-white hover:shadow-md h-16 ${block.content.stickerName === sticker.name
                        ? 'border-slate-400 shadow-md ring-2 ring-slate-100'
                        : 'border-slate-100 hover:border-slate-300'
                        }`}
                      title={sticker.name}
                    >
                      <div
                        className="w-full h-full flex items-center justify-center"
                        dangerouslySetInnerHTML={{ __html: sticker.src }}
                      />
                    </button>
                  ))}
              </div>
            )}

            {/* Selected sticker preview */}
            {stickerSrc && (
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                <p className="text-xs font-medium text-slate-500 mb-2">Preview</p>
                <div className="flex items-center justify-center bg-white rounded-lg border border-slate-100 p-4 min-h-[100px]">
                  {isSvgSticker ? (
                    <div
                      style={{ width: `${stickerWidth}px`, height: 'auto' }}
                      dangerouslySetInnerHTML={{ __html: stickerSrc }}
                    />
                  ) : (
                    <img
                      src={stickerSrc}
                      alt={block.content.alt || 'Sticker'}
                      style={{ width: `${stickerWidth}px`, height: 'auto' }}
                    />
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Size: {stickerWidth}px
              </label>
              <input
                type="range"
                min={40}
                max={400}
                value={stickerWidth}
                onChange={(e) => handleContentChange('width', `${e.target.value}px`)}
                className="v2-slider w-full h-1"
                style={{ background: `linear-gradient(90deg, #0F172A ${((stickerWidth - 40) / 360) * 100}%, #E2E8F0 ${((stickerWidth - 40) / 360) * 100}%)` }}
              />
            </div>

            {/* Alignment */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Alignment</label>
              <div className="flex items-center gap-1.5 mt-1.5">
                {(['left', 'center', 'right'] as const).map((align) => (
                  <button
                    key={align}
                    type="button"
                    onClick={() => handleStyleChange('textAlign', align)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                      (block.styles.textAlign || 'center') === align
                        ? 'bg-slate-100 text-slate-900 shadow-sm'
                        : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                    }`}
                    title={align.charAt(0).toUpperCase() + align.slice(1)}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {align === 'left' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h13M4 12h9M4 18h13" />}
                      {align === 'center' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 6h10M4 12h16M7 18h10" />}
                      {align === 'right' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 6h13M11 12h9M7 18h13" />}
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      }

      case 'giphy': {
        const handleSearch = (term: string) => {
          setGiphySearchTerm(term);
          if (term.length > 0) {
            setShowGiphyResults(true);
          }
        };

        const selectGif = (url: string, alt: string) => {
          applyContentUpdate({ src: url, alt });
          setShowGiphyResults(false);
          setGiphySearchTerm('');
        };

        const gifWidthRaw = block.content.width || '100%';
        const gifWidthPercent = gifWidthRaw.toString().includes('%')
          ? parseInt(gifWidthRaw.replace('%', ''), 10) || 100
          : 100;

        return (
          <div className="space-y-4">
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-3 text-slate-700">
                <GifIcon className="w-5 h-5" />
                <span className="text-sm font-semibold">GIPHY Search</span>
                {!process.env.REACT_APP_GIPHY_API_KEY && (
                  <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded border border-red-200 ml-auto">
                    Missing API Key
                  </span>
                )}
              </div>

              {/* Search Input */}
              <div className="relative mb-3">
                <input
                  type="text"
                  value={giphySearchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => setShowGiphyResults(true)}
                  placeholder="Search for GIFs..."
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-shadow"
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* Categories */}
              {showGiphyResults && (
                <div className="mb-3 flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                  {['all', 'trending', 'reactions', 'memes'].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setGiphyCategory(cat as any)}
                      className={`px-3 py-1 text-xs font-medium rounded-full capitalize whitespace-nowrap transition-colors ${giphyCategory === cat
                        ? 'bg-pink-100 text-pink-700 border border-pink-200'
                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                        }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}

              {/* Results Grid */}
              {showGiphyResults && (
                <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1 custom-scrollbar min-h-[100px]">
                  {giphyLoading ? (
                    <div className="col-span-3 flex items-center justify-center py-8 text-slate-400">
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Loading...
                    </div>
                  ) : giphyGifs.length > 0 ? (
                    giphyGifs.map((gif) => (
                      <button
                        key={gif.id}
                        onClick={() => selectGif(gif.url, gif.alt)}
                        className="relative w-full aspect-square rounded overflow-hidden border border-transparent hover:border-pink-500 hover:shadow-sm focus:outline-none group transition-all bg-slate-100"
                        title={gif.alt}
                      >
                        <img
                          src={gif.url}
                          alt={gif.alt}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </button>
                    ))
                  ) : (
                    <div className="col-span-3 py-8 text-center text-slate-400 text-xs">
                      {process.env.REACT_APP_GIPHY_API_KEY ? 'No GIFs found' : 'Please configure API Key'}
                    </div>
                  )}
                </div>
              )}

              {/* Selected Preview */}
              {!showGiphyResults && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-slate-500 mb-2">Selected GIF</p>
                  <div className="w-full rounded-lg overflow-hidden border border-slate-200 bg-white min-h-[150px] flex items-center justify-center relative group p-2">
                    {block.content.src ? (
                      <>
                        <img src={block.content.src} alt="Selected" className="max-w-full h-auto max-h-[200px]" />
                        <button
                          onClick={() => setShowGiphyResults(true)}
                          className="absolute inset-0 bg-black/0 group-hover:bg-black/10 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                        >
                          <span className="bg-white/90 text-slate-700 px-3 py-1.5 rounded-full text-xs font-medium shadow-sm">
                            Change GIF
                          </span>
                        </button>
                      </>
                    ) : (
                      <div className="text-center p-4">
                        <span className="text-xs text-slate-400 block mb-2">No GIF selected</span>
                        <button
                          onClick={() => setShowGiphyResults(true)}
                          className="text-pink-600 text-xs font-medium hover:underline"
                        >
                          Browse GIFs
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Width: {gifWidthPercent}%
              </label>
              <input
                type="range"
                min={25}
                max={100}
                value={gifWidthPercent}
                onChange={(e) => handleContentChange('width', `${e.target.value}%`)}
                className="v2-slider w-full h-1"
                style={{
                  background: `linear-gradient(90deg, #0F172A ${((gifWidthPercent - 25) / 75) * 100}%, #E2E8F0 ${((gifWidthPercent - 25) / 75) * 100}%)`,
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Alt Text</label>
              <input
                type="text"
                value={block.content.alt || ''}
                onChange={(e) => handleContentChange('alt', e.target.value)}
                placeholder="Describe the GIF"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-slate-500"
              />
            </div>
          </div>
        );
      }





      case 'poll': {
        const options = (block.content.options as any[]) || [];

        const handleOptionChange = (index: number, field: string, value: string) => {
          const newOptions = [...options];
          newOptions[index] = { ...newOptions[index], [field]: value };
          handleContentChange('options', newOptions);
        };

        const addOption = () => {
          handleContentChange('options', [...options, { label: 'New Option', url: '#' }]);
        };

        const removeOption = (index: number) => {
          const newOptions = options.filter((_, i) => i !== index);
          handleContentChange('options', newOptions);
        };

        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Question</label>
              <input
                type="text"
                value={block.content.question || ''}
                onChange={(e) => handleContentChange('question', e.target.value)}
                placeholder="Ask a question..."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Options</label>
              <div className="space-y-3">
                {options.map((option, index) => (
                  <div key={index} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs font-medium text-slate-500">Option {index + 1}</span>
                      <button
                        onClick={() => removeOption(index)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={option.label}
                        onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                        placeholder="Label"
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                      />
                      <input
                        type="text"
                        value={option.url}
                        onChange={(e) => handleOptionChange(index, 'url', e.target.value)}
                        placeholder="URL (e.g., mailto: or https://)"
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs text-slate-600 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={addOption}
                className="mt-3 w-full py-2 px-3 flex items-center justify-center gap-1.5 text-sm font-medium text-slate-800 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Add Option
              </button>
            </div>
          </div>
        );
      }

      case 'caption':
      case 'preheader':
      case 'quote':
      case 'list':
      case 'text': {
        return (
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700 mb-1">Text Content</label>
                <div className="relative" ref={mergeTagTriggerRef}>
                  <button
                    type="button"
                    onClick={() => setShowMergeTagDropdown((prev) => !prev)}
                    className="text-xs font-medium text-slate-800 hover:text-slate-700 hover:underline"
                  >
                    Merge tags
                  </button>
                  {showMergeTagDropdown && (
                    <div
                      ref={mergeTagDropdownRef}
                      className="absolute right-0 mt-2 w-60 bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto z-50"
                    >
                      {mergeTags.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-slate-500">No merge tags available</div>
                      ) : (
                        mergeTags.map((tag) => (
                          <button
                            key={tag.mergeTag}
                            type="button"
                            onClick={() => insertRichTextMergeTag(tag.mergeTag)}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50"
                          >
                            <div className="text-sm font-medium text-slate-700">{tag.label}</div>
                            <div className="text-xs text-slate-500">{tag.mergeTag}</div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
              <textarea
                ref={setRichTextInputRef}
                value={block.content.text || ''}
                onChange={(e) => handleContentChange('text', e.target.value)}
                onSelect={(e) => {
                  const el = e.currentTarget;
                  richTextSelectionRef.current = {
                    start: el.selectionStart ?? 0,
                    end: el.selectionEnd ?? 0,
                  };
                }}
                onMouseUp={(e) => {
                  const el = e.currentTarget;
                  richTextSelectionRef.current = {
                    start: el.selectionStart ?? 0,
                    end: el.selectionEnd ?? 0,
                  };
                }}
                onKeyUp={(e) => {
                  const el = e.currentTarget;
                  richTextSelectionRef.current = {
                    start: el.selectionStart ?? 0,
                    end: el.selectionEnd ?? 0,
                  };
                }}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 bg-slate-50/50 hover:bg-white transition-all"
                rows={4}
              />
            </div>

            <CollapsibleSection title="Typography" defaultOpen={true}>
              {renderTypographyContent()}
            </CollapsibleSection>

            <CollapsibleSection title="Style" defaultOpen={true}>
              {renderStyleContent()}
            </CollapsibleSection>

            <CollapsibleSection title="Layout" defaultOpen={true}>
              {renderSpacingContent()}
            </CollapsibleSection>

            <CollapsibleSection title="Mobile responsive" defaultOpen={true}>
              {renderMobileContent()}
            </CollapsibleSection>
          </div>
        );
      }

      case 'title':
      case 'subtitle':
      case 'heading': {
        return (
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-700">Text Content</label>
                <div className="relative" ref={mergeTagTriggerRef}>
                  <button
                    type="button"
                    onClick={() => setShowMergeTagDropdown((prev) => !prev)}
                    className="text-xs font-medium text-slate-800 hover:text-slate-700 hover:underline"
                  >
                    Merge tags
                  </button>
                  {showMergeTagDropdown && (
                    <div
                      ref={mergeTagDropdownRef}
                      className="absolute right-0 mt-2 w-60 bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto z-50"
                    >
                      {mergeTags.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-slate-500">No merge tags available</div>
                      ) : (
                        mergeTags.map((tag) => (
                          <button
                            key={tag.mergeTag}
                            type="button"
                            onClick={() => insertRichTextMergeTag(tag.mergeTag)}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50"
                          >
                            <div className="text-sm font-medium text-slate-700">{tag.label}</div>
                            <div className="text-xs text-slate-500">{tag.mergeTag}</div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
              <input
                ref={setRichTextInputRef}
                type="text"
                value={block.content.text || ''}
                onChange={(e) => handleContentChange('text', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 bg-slate-50/50 hover:bg-white transition-all font-semibold"
              />
            </div>

            {block.type === 'heading' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Heading Type</label>
                <div className="grid grid-cols-4 gap-2">
                  {[1, 2, 3, 4].map((level) => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => {
                        handleContentChange('level', level);
                        const headingSizeMap: Record<number, number> = { 1: 40, 2: 32, 3: 24, 4: 20 };
                        handleStyleChange('fontSize', `${headingSizeMap[level]}px`);
                      }}
                      className={`py-2 px-3 text-sm font-bold border rounded-lg transition-all ${
                        (block.content.level || 1) === level
                          ? 'bg-slate-900 text-white border-slate-900 shadow-md scale-105'
                          : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      H{level}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <CollapsibleSection title="Typography" defaultOpen={true}>
              {renderTypographyContent()}
            </CollapsibleSection>

            <CollapsibleSection title="Style" defaultOpen={true}>
              {renderStyleContent()}
            </CollapsibleSection>

            <CollapsibleSection title="Layout" defaultOpen={true}>
              {renderSpacingContent()}
            </CollapsibleSection>

            <CollapsibleSection title="Mobile responsive" defaultOpen={true}>
              {renderMobileContent()}
            </CollapsibleSection>
          </div>
        );
      }

      case 'image':
        return renderImageProperties();

      case 'logo':
        return renderLogoProperties();

      case 'button': {
        const buttonStyle = block.styles.buttonStyle || {};
        const widthRaw = buttonStyle.width;
        const isButtonAutoWidth = !widthRaw || widthRaw === 'auto';
        const buttonWidthValue = (() => {
          if (isButtonAutoWidth) return 100;
          const numericValue = parseInt(
            widthRaw.toString().replace('%', '').replace('px', '') || '100',
            10
          );
          return isNaN(numericValue) ? 100 : Math.max(20, Math.min(100, numericValue));
        })();
        const borderRadiusValue = (() => {
          const borderRadius = buttonStyle.borderRadius || '6px';
          const numericValue = parseInt(
            borderRadius.toString().replace('px', '').replace('%', '') || '6',
            10
          );
          return isNaN(numericValue) ? 6 : Math.max(0, Math.min(50, numericValue));
        })();
        const fontFamilyValue = buttonStyle.fontFamily || block.styles.fontFamily || 'Arial';

        const buttonActionType = (block.content.actionType as 'open-url' | 'download-file') || 'open-url';
        const buttonFileUrl = block.content.downloadFileUrl || '';
        const downloadFileSizeLabel = block.content.downloadFileSize ? formatFileSize(block.content.downloadFileSize) : null;

        return (
          <div className="space-y-6">
            {/* Content Section */}
            <div className="space-y-4">
              <div>
                <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Content & Action</label>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Button Text</label>
                    <input
                      type="text"
                      value={block.content.text || ''}
                      onChange={(e) => handleContentChange('text', e.target.value)}
                      placeholder="e.g., Shop Now"
                      className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Action Type</label>
                    <select
                      value={buttonActionType}
                      onChange={(e) => setButtonActionType(e.target.value as 'open-url' | 'download-file')}
                      className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                    >
                      <option value="open-url">Open Website</option>
                      <option value="download-file">Download File</option>
                    </select>
                  </div>
                  {buttonActionType === 'open-url' ? (
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">Destination URL</label>
                      <input
                        type="text"
                        value={block.content.url || ''}
                        onChange={(e) => handleContentChange('url', e.target.value)}
                        placeholder="https://example.com"
                        className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                      />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => buttonFileInputRef.current?.click()}
                          disabled={isUploadingButtonFile || !currentUser}
                          className="px-4 py-2 text-sm font-semibold text-white bg-slate-900 rounded-lg hover:bg-slate-800 disabled:opacity-50 transition-all shadow-sm"
                        >
                          {isUploadingButtonFile ? 'Uploading...' : buttonFileUrl ? 'Change File' : 'Upload File'}
                        </button>
                        {buttonFileUrl && (
                          <button
                            type="button"
                            onClick={handleButtonFileRemove}
                            className="text-xs font-semibold text-rose-500 hover:text-rose-600 px-2 py-1"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <input
                        ref={buttonFileInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleButtonFileUpload}
                      />
                      {buttonFileUrl && (
                        <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-between">
                          <div className="truncate">
                            <p className="text-sm font-semibold text-slate-900 truncate">{block.content.downloadFileName || 'Attached File'}</p>
                            {downloadFileSizeLabel && <p className="text-[10px] text-slate-400 font-medium">{downloadFileSizeLabel}</p>}
                          </div>
                          <a href={buttonFileUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-slate-900 hover:underline ml-2">View</a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Visual Design Section */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Visual Design</label>
              
              <div className="space-y-6">
                {/* Alignment */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-3">Alignment</label>
                  <div className="flex gap-1.5">
                    {(['left', 'center', 'right'] as const).map((align) => (
                      <button
                        key={align}
                        type="button"
                        onClick={() => handleStyleChange('textAlign', align)}
                        className={`flex-1 py-1.5 flex justify-center items-center rounded-lg border transition-all ${
                          (block.styles.textAlign || 'center') === align
                            ? 'bg-slate-900 border-slate-900 text-white shadow-md shadow-slate-200'
                            : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {align === 'left' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h16" />}
                          {align === 'center' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M7 12h10M4 18h16" />}
                          {align === 'right' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M10 12h10M4 18h16" />}
                        </svg>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Button style variant */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-2">Button style</label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {(
                      [
                        { id: 'solid', label: 'Solid' },
                        { id: 'outline', label: 'Outline' },
                        { id: 'ghost', label: 'Ghost' },
                        { id: 'gradient', label: 'Gradient' },
                      ] as const
                    ).map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => handleButtonStyleChange('variant', opt.id)}
                        className={`py-1.5 text-xs font-semibold rounded-lg border transition-all ${
                          (buttonStyle.variant || 'solid') === opt.id
                            ? 'bg-slate-900 border-slate-900 text-white'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Colors */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-2">Background</label>
                    <div className="relative">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'buttonBg' ? null : 'buttonBg')}
                        className="w-full h-10 px-2 rounded-xl border border-slate-200 flex items-center gap-3 bg-white hover:bg-slate-50 transition-all shadow-sm"
                      >
                        <div className="w-6 h-6 rounded-lg shadow-inner border border-black/5" style={{ backgroundColor: buttonStyle.backgroundColor || '#111827' }} />
                        <span className="text-xs font-mono text-slate-400 uppercase">{(buttonStyle.backgroundColor || '#111827').replace('#','')}</span>
                      </button>
                      {showColorPicker === 'buttonBg' && (
                        <div className="absolute left-0 mt-2 z-50 animate-in fade-in zoom-in-95 duration-100">
                          <CustomColorPicker
                            currentColor={buttonStyle.backgroundColor || '#111827'}
                            onChange={(color) => handleButtonStyleChange('backgroundColor', color)}
                            pickerType="solid"
                            emailColors={getEmailBackgroundColors()}
                            brandColors={brandColors}
                            showAdvancedPicker={showAdvancedPicker}
                            setShowAdvancedPicker={setShowAdvancedPicker}
                            setAdvancedPickerPosition={setAdvancedPickerPosition}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-2">Text Color</label>
                    <div className="relative">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'buttonText' ? null : 'buttonText')}
                        className="w-full h-10 px-2 rounded-xl border border-slate-200 flex items-center gap-3 bg-white hover:bg-slate-50 transition-all shadow-sm"
                      >
                        <div className="w-6 h-6 rounded-lg shadow-inner border border-black/5" style={{ backgroundColor: buttonStyle.color || '#FFFFFF' }} />
                        <span className="text-xs font-mono text-slate-400 uppercase">{(buttonStyle.color || '#FFFFFF').replace('#','')}</span>
                      </button>
                      {showColorPicker === 'buttonText' && (
                        <div className="absolute right-0 mt-2 z-50 animate-in fade-in zoom-in-95 duration-100">
                          <CustomColorPicker
                            currentColor={buttonStyle.color || '#FFFFFF'}
                            onChange={(color) => handleButtonStyleChange('color', color)}
                            pickerType="solid"
                            emailColors={getEmailBackgroundColors()}
                            brandColors={brandColors}
                            showAdvancedPicker={showAdvancedPicker}
                            setShowAdvancedPicker={setShowAdvancedPicker}
                            setAdvancedPickerPosition={setAdvancedPickerPosition}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Font Choices */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-2">Typography</label>
                    <div className="grid grid-cols-2 gap-3">
                      <select
                        value={fontFamilyValue}
                        onChange={(e) => handleButtonStyleChange('fontFamily', e.target.value)}
                        className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all cursor-pointer"
                        style={{ fontFamily: fontFamilyValue }}
                      >
                        {FONT_FAMILIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                      </select>
                      <select
                        value={buttonStyle.fontWeight || '400'}
                        onChange={(e) => handleButtonStyleChange('fontWeight', e.target.value)}
                        className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all cursor-pointer"
                      >
                        <option value="400">Regular</option>
                        <option value="500">Medium</option>
                        <option value="600">Semibold</option>
                        <option value="700">Bold</option>
                        <option value="800">Extra Bold</option>
                      </select>
                    </div>
                  </div>

                  {/* Size & Formatting */}
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1 pr-4">
                        <label className="text-xs font-semibold text-slate-700 font-mono">SIZE</label>
                        <span className="text-[10px] font-bold text-slate-400">{parseInt(buttonStyle.fontSize) || 16}px</span>
                      </div>
                      <input
                        type="range"
                        min="12"
                        max="48"
                        value={parseInt(buttonStyle.fontSize) || 16}
                        onChange={(e) => handleButtonStyleChange('fontSize', `${e.target.value}px`)}
                        className="v2-spacing-slider w-[90%]"
                        style={{ background: `linear-gradient(90deg, #64748b ${(( (parseInt(buttonStyle.fontSize) || 16) - 12) / 36) * 100}%, #f1f5f9 ${(( (parseInt(buttonStyle.fontSize) || 16) - 12) / 36) * 100}%)` }}
                      />
                    </div>
                    <div className="flex-1 border-l border-slate-100 pl-4">
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-xs font-semibold text-slate-700 font-mono text-[10px]">ROUNDING</label>
                        <span className="text-[10px] font-bold text-slate-400">{borderRadiusValue}px</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="32"
                        value={borderRadiusValue}
                        onChange={(e) => handleButtonStyleChange('borderRadius', `${e.target.value}px`)}
                        className="v2-spacing-slider w-full"
                        style={{ background: `linear-gradient(90deg, #64748b ${(borderRadiusValue / 32) * 100}%, #f1f5f9 ${(borderRadiusValue / 32) * 100}%)` }}
                      />
                    </div>
                  </div>
                  <div className="pt-4 border-t border-slate-50">
                    <PaddingControl
                      label="Button Padding"
                      value={buttonStyle.padding || '12px 24px'}
                      onChange={(val) => handleButtonStyleChange('padding', val)}
                    />
                  </div>
                </div>

                {/* Width Override */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-xs font-semibold text-slate-700">Button Width</label>
                    <button
                      type="button"
                      onClick={() => handleButtonStyleChange('width', isButtonAutoWidth ? '100%' : 'auto')}
                      className={`text-[10px] font-bold px-2 py-0.5 rounded transition-all ${isButtonAutoWidth ? 'bg-slate-100 text-slate-900 border border-slate-200' : 'bg-slate-50 text-slate-400 hover:text-slate-600'}`}
                    >
                      {isButtonAutoWidth ? 'AUTO (FIT CONTENT)' : 'CUSTOM'}
                    </button>
                  </div>
                  {!isButtonAutoWidth && (
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min="20"
                        max="100"
                        value={buttonWidthValue}
                        onChange={(e) => handleButtonStyleChange('width', `${e.target.value}%`)}
                        className="v2-spacing-slider flex-1"
                        style={{ background: `linear-gradient(90deg, #64748b ${((buttonWidthValue - 20) / 80) * 100}%, #f1f5f9 ${((buttonWidthValue - 20) / 80) * 100}%)` }}
                      />
                      <span className="text-xs font-bold text-slate-500 w-8 text-right font-mono">{buttonWidthValue}%</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <PaddingControl
                label="Block padding"
                value={block.styles.padding || '20px'}
                onChange={(val) => handleStyleChange('padding', val)}
              />
            </div>
          </div>
        );
      }

      case 'profile-card': {
        const template = block.content.template || 'standard';
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
              <input
                type="text"
                value={block.content.name || ''}
                onChange={(e) => handleContentChange('name', e.target.value)}
                className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-500/10 transition-all duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
              <input
                type="text"
                value={block.content.role || ''}
                onChange={(e) => handleContentChange('role', e.target.value)}
                className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-500/10 transition-all duration-200"
              />
            </div>
            {/* ... other profile inputs ... */}
          </div>
        );
      }

      case 'header':
        return <HeaderPropertiesPanel ctx={editorialPropertyCtx} />;

      case 'menu':
        return <MenuPropertiesPanel ctx={editorialPropertyCtx} />;

      case 'qr-code': {
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <h5 className="text-sm font-semibold text-slate-800">QR Content</h5>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">URL or Text</label>
                <input
                  type="text"
                  value={block.content.url || ''}
                  onChange={(e) => handleContentChange('url', e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-500/10 transition-all duration-200"
                />
                <p className="mt-2 text-xs text-slate-500 leading-relaxed">
                  This value is encoded directly into the QR image in the sent email (same for every recipient). Use a tracking link or landing page if you need per-contact behavior.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h5 className="text-sm font-semibold text-slate-800">Appearance</h5>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Key Color</label>
                  <div className="relative">
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'qrFg' ? null : 'qrFg')}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg flex items-center gap-2 bg-white"
                    >
                      <div
                        className="w-5 h-5 rounded-full border border-slate-200"
                        style={{ backgroundColor: block.content.foregroundColor || '#000000' }}
                      />
                      <span className="text-sm text-slate-600 truncate">
                        {block.content.foregroundColor || '#000000'}
                      </span>
                    </button>
                    {showColorPicker === 'qrFg' && (
                      <div ref={colorPickerRef} className="absolute z-10 top-full left-0 mt-1">
                        <CustomColorPicker
                          currentColor={block.content.foregroundColor || '#000000'}
                          onChange={(color) => handleContentChange('foregroundColor', color)}
                          pickerType="solid"
                          emailColors={getEmailBackgroundColors()}
                          brandColors={brandColors}
                          showAdvancedPicker={showAdvancedPicker}
                          setShowAdvancedPicker={setShowAdvancedPicker}
                          setAdvancedPickerPosition={setAdvancedPickerPosition}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Background</label>
                  <div className="relative">
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'qrBg' ? null : 'qrBg')}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg flex items-center gap-2 bg-white"
                    >
                      <div
                        className="w-5 h-5 rounded-full border border-slate-200"
                        style={{ backgroundColor: block.content.backgroundColor || '#FFFFFF' }}
                      />
                      <span className="text-sm text-slate-600 truncate">
                        {block.content.backgroundColor || '#FFFFFF'}
                      </span>
                    </button>
                    {showColorPicker === 'qrBg' && (
                      <div ref={colorPickerRef} className="absolute z-10 top-full right-0 mt-1">
                        <CustomColorPicker
                          currentColor={block.content.backgroundColor || '#FFFFFF'}
                          onChange={(color) => handleContentChange('backgroundColor', color)}
                          pickerType="solid"
                          emailColors={getEmailBackgroundColors()}
                          brandColors={brandColors}
                          showAdvancedPicker={showAdvancedPicker}
                          setShowAdvancedPicker={setShowAdvancedPicker}
                          setAdvancedPickerPosition={setAdvancedPickerPosition}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Size: {block.content.width || 150}px
                </label>
                <input
                  type="range"
                  min="50"
                  max="400"
                  value={block.content.width || 150}
                  onChange={(e) => handleContentChange('width', parseInt(e.target.value))}
                  className="v2-slider w-full h-1"
                  style={{ background: `linear-gradient(90deg, #0F172A ${(((block.content.width || 150) - 50) / 350) * 100}%, #E2E8F0 ${(((block.content.width || 150) - 50) / 350) * 100}%)` }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Alignment</label>
                <div className="flex items-center gap-1.5 mt-1.5">
                  {['left', 'center', 'right'].map((align) => (
                    <button
                      key={align}
                      type="button"
                      onClick={() => handleContentChange('alignment', align)}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                        (block.content.alignment || 'center') === align
                          ? 'bg-slate-100 text-slate-900 shadow-sm'
                          : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                      }`}
                      title={align.charAt(0).toUpperCase() + align.slice(1)}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {align === 'left' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h13M4 12h9M4 18h13" />}
                        {align === 'center' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 6h10M4 12h16M7 18h10" />}
                        {align === 'right' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 6h13M11 12h9M7 18h13" />}
                      </svg>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'star-rating': {
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <h5 className="text-sm font-semibold text-slate-800">Rating Config</h5>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Rating: {block.content.rating || 5} / {block.content.maxRating || 5}
                </label>
                <input
                  type="range"
                  min="0"
                  max={block.content.maxRating || 5}
                  step="0.5"
                  value={block.content.rating || 5}
                  onChange={(e) => handleContentChange('rating', parseFloat(e.target.value))}
                  className="v2-slider w-full h-1"
                  style={{ background: `linear-gradient(90deg, #0F172A ${((block.content.rating || 5) / (block.content.maxRating || 5)) * 100}%, #E2E8F0 ${((block.content.rating || 5) / (block.content.maxRating || 5)) * 100}%)` }}
                />
              </div>
            </div>

            <div className="space-y-4">
              <h5 className="text-sm font-semibold text-slate-800">Appearance</h5>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Star Color</label>
                  <div className="relative">
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'starFg' ? null : 'starFg')}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg flex items-center gap-2 bg-white"
                    >
                      <div
                        className="w-5 h-5 rounded-full border border-slate-200"
                        style={{ backgroundColor: block.content.color || '#FBBF24' }}
                      />
                      <span className="text-sm text-slate-600 truncate">
                        {block.content.color || '#FBBF24'}
                      </span>
                    </button>
                    {showColorPicker === 'starFg' && (
                      <div ref={colorPickerRef} className="absolute z-10 top-full left-0 mt-1">
                        <CustomColorPicker
                          currentColor={block.content.color || '#FBBF24'}
                          onChange={(color) => handleContentChange('color', color)}
                          pickerType="solid"
                          emailColors={getEmailBackgroundColors()}
                          brandColors={brandColors}
                          showAdvancedPicker={showAdvancedPicker}
                          setShowAdvancedPicker={setShowAdvancedPicker}
                          setAdvancedPickerPosition={setAdvancedPickerPosition}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Empty Color</label>
                  <div className="relative">
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'starBg' ? null : 'starBg')}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg flex items-center gap-2 bg-white"
                    >
                      <div
                        className="w-5 h-5 rounded-full border border-slate-200"
                        style={{ backgroundColor: block.content.emptyColor || '#E5E7EB' }}
                      />
                      <span className="text-sm text-slate-600 truncate">
                        {block.content.emptyColor || '#E5E7EB'}
                      </span>
                    </button>
                    {showColorPicker === 'starBg' && (
                      <div ref={colorPickerRef} className="absolute z-10 top-full right-0 mt-1">
                        <CustomColorPicker
                          currentColor={block.content.emptyColor || '#E5E7EB'}
                          onChange={(color) => handleContentChange('emptyColor', color)}
                          pickerType="solid"
                          emailColors={getEmailBackgroundColors()}
                          brandColors={brandColors}
                          showAdvancedPicker={showAdvancedPicker}
                          setShowAdvancedPicker={setShowAdvancedPicker}
                          setAdvancedPickerPosition={setAdvancedPickerPosition}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Size: {block.content.size || 24}px
                </label>
                <input
                  type="range"
                  min="12"
                  max="64"
                  value={block.content.size || 24}
                  onChange={(e) => handleContentChange('size', parseInt(e.target.value))}
                  className="v2-slider w-full h-1"
                  style={{ background: `linear-gradient(90deg, #0F172A ${(((block.content.size || 24) - 12) / 52) * 100}%, #E2E8F0 ${(((block.content.size || 24) - 12) / 52) * 100}%)` }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Alignment</label>
                <div className="flex items-center gap-1.5 mt-1.5">
                  {['left', 'center', 'right'].map((align) => (
                    <button
                      key={align}
                      type="button"
                      onClick={() => handleContentChange('alignment', align)}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                        (block.content.alignment || 'center') === align
                          ? 'bg-slate-100 text-slate-900 shadow-sm'
                          : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                      }`}
                      title={align.charAt(0).toUpperCase() + align.slice(1)}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {align === 'left' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h13M4 12h9M4 18h13" />}
                        {align === 'center' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 6h10M4 12h16M7 18h10" />}
                        {align === 'right' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 6h13M11 12h9M7 18h13" />}
                      </svg>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'hero':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
              <input
                type="text"
                value={block.content.title || ''}
                onChange={(e) => handleContentChange('title', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Subtitle</label>
              <input
                type="text"
                value={block.content.subtitle || ''}
                onChange={(e) => handleContentChange('subtitle', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Button Text</label>
              <input
                type="text"
                value={block.content.buttonText || ''}
                onChange={(e) => handleContentChange('buttonText', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Button URL</label>
              <input
                type="text"
                value={block.content.buttonUrl || ''}
                onChange={(e) => handleContentChange('buttonUrl', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Background Image URL</label>
              <input
                type="text"
                value={block.content.backgroundImage || ''}
                onChange={(e) => handleContentChange('backgroundImage', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
                placeholder="https://example.com/bg.jpg"
              />
            </div>
          </>
        );

      case 'video': {
        const videoThumbnailPreview = resolveVideoThumbnailUrl(block.content);

        const syncThumbnailUrl = (url: string) => {
          applyContentUpdate({ thumbnailUrl: url, thumbnail: url });
        };

        return (
          <div className="space-y-4">
            <div className="space-y-3 border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <h5 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <PlayIcon className="w-4 h-4 text-slate-500" />
                  Video Settings
                </h5>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Video URL</label>
                <div className="relative">
                  <input
                    type="text"
                    value={block.content.videoUrl || ''}
                    onChange={(e) => handleContentChange('videoUrl', e.target.value)}
                    placeholder="https://youtube.com/..."
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                  />
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <PlayIcon className="w-4 h-4" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Add a YouTube or Vimeo URL.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Thumbnail URL</label>
                <input
                  type="text"
                  value={block.content.thumbnailUrl || block.content.thumbnail || ''}
                  onChange={(e) => syncThumbnailUrl(e.target.value)}
                  placeholder="Auto-filled from video URL, or paste custom"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-slate-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Leave empty to auto-fetch from YouTube/Vimeo when possible.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Play Button Style</label>
                <div className="flex items-center gap-1.5 mt-1.5">
                  {['circle', 'square', 'none'].map((style) => (
                    <button
                      key={style}
                      type="button"
                      onClick={() => handleContentChange('playButtonStyle', style)}
                      className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${
                        (block.content.playButtonStyle || 'circle') === style
                          ? 'bg-slate-100 text-slate-900 shadow-sm'
                          : 'text-slate-400 hover:bg-slate-50'
                      }`}
                    >
                      {style}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-2">
                <p className="text-xs font-medium text-slate-500 mb-2">Preview</p>
                <div className="w-full aspect-video bg-slate-100 rounded-lg overflow-hidden relative group border border-slate-200">
                  {videoThumbnailPreview ? (
                    <img src={videoThumbnailPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">No thumbnail</div>
                  )}

                  {block.content.playButtonStyle !== 'none' && (
                    <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-black/70 flex items-center justify-center border-2 border-white ${block.content.playButtonStyle === 'circle' ? 'rounded-full' : 'rounded-lg'
                      }`}>
                      <PlayIcon className="w-6 h-6 text-white ml-0.5" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'spacer':
        return (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Height (px)</label>
            <input
              type="number"
              value={parseInt(block.content.height) || 40}
              onChange={(e) => handleContentChange('height', `${e.target.value}px`)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
          </div>
        );

      case 'columns':
        return (
          <div>
            <p className="text-sm text-slate-500 italic">
              Column layout is fixed for this block.
            </p>
          </div>
        );

      case 'footer':
        return <FooterPropertiesPanel ctx={editorialPropertyCtx} />;
      case 'social': {
        const platforms = block.content.platforms || [];
        const defaults = {
          display: 'icon-only',
          iconStyle: 'solid',
          layout: 'horizontal',
          iconColor: '#000000',
          alignment: 'center',
          gap: 12,
          size: 24,
        };
        const iconSettings = { ...defaults, ...(block.content.iconSettings || {}) };

        const updatePlatform = (index: number, updates: Partial<{ name: string; url: string; icon: string; style: string }>) => {
          const newPlatforms = [...platforms];
          newPlatforms[index] = { ...newPlatforms[index], ...updates };
          handleContentChange('platforms', newPlatforms);
        };

        const removePlatform = (index: number) => {
          const newPlatforms = platforms.filter((_: any, i: number) => i !== index);
          handleContentChange('platforms', newPlatforms);
        };

        const addPlatform = (platformValue: string) => {
          const platform = SOCIAL_PLATFORMS.find(p => p.value === platformValue);
          if (!platform) return;

          const newPlatform = {
            name: platform.label,
            url: platform.defaultUrl,
            icon: platform.value,
          };
          handleContentChange('platforms', [...platforms, newPlatform]);
          setShowPlatformPicker(null);
        };

        const movePlatform = (fromIndex: number, toIndex: number) => {
          const newPlatforms = [...platforms];
          const [moved] = newPlatforms.splice(fromIndex, 1);
          newPlatforms.splice(toIndex, 0, moved);
          handleContentChange('platforms', newPlatforms);
        };

        const updateIconSettings = (updates: Partial<typeof iconSettings>) => {
          handleContentChange('iconSettings', { ...iconSettings, ...updates });
        };

        const renderPlatformPicker = (editingIndex: number | 'new') => (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <h3 className="text-lg font-bold text-slate-800">Select Platform</h3>
                <button 
                  onClick={() => setShowPlatformPicker(null)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-4 overflow-y-auto grid grid-cols-4 gap-3">
                {SOCIAL_PLATFORMS.map((p) => {
                  const isAlreadyAdded = platforms.some((existing: any) => existing.icon === p.value);
                  return (
                    <button
                      key={p.value}
                      onClick={() => {
                        if (editingIndex === 'new') {
                          addPlatform(p.value);
                        } else {
                          updatePlatform(editingIndex, {
                            icon: p.value,
                            name: p.label,
                            url: p.defaultUrl,
                          });
                          setShowPlatformPicker(null);
                        }
                      }}
                      className={`flex flex-col items-center gap-2 p-3 border rounded-xl transition-all group ${
                        isAlreadyAdded && editingIndex === 'new'
                          ? 'bg-slate-50 border-slate-100 opacity-60 grayscale cursor-not-allowed'
                          : 'bg-white border-slate-200 hover:border-slate-400 hover:bg-slate-50 active:scale-95'
                      }`}
                      disabled={isAlreadyAdded && editingIndex === 'new'}
                    >
                      <SocialMediaIcon platform={p.value} className="w-6 h-6" />
                      <span className="text-[10px] font-bold text-slate-600 uppercase tracking-tight text-center truncate w-full">
                        {p.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );

        const renderContentTab = () => (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-4 px-1">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">Social Media Links</label>
                <span className="text-[10px] font-bold text-slate-800 bg-slate-50 px-2 py-0.5 rounded-full uppercase">
                  {platforms.length} Icons
                </span>
              </div>
              
              <div className="space-y-3">
                {platforms.map((platform: any, index: number) => (
                  <div
                    key={index}
                    className="bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md mb-3 animate-in fade-in slide-in-from-bottom-2 duration-500 overflow-hidden group/card"
                  >

                    <div className="flex items-center justify-between px-4 py-3 bg-white relative">
                      <button
                        type="button"
                        onClick={() => setShowPlatformPicker(index)}
                        className="flex items-center gap-3 group text-left"
                      >
                        <div className="w-12 h-12 bg-white border border-slate-50 rounded-xl shadow-md shadow-slate-950/5 flex items-center justify-center transition-all duration-500 group-hover:scale-105 active:scale-95 ring-1 ring-slate-900/5 relative overflow-hidden">
                          <SocialMediaIcon 
                            platform={platform.icon || 'facebook'} 
                            className="w-6 h-6 relative z-10" 
                            styleOverride={platform.style || iconSettings.iconStyle}
                            colorOverride={
                              (platform.style || iconSettings.iconStyle).includes('brand') 
                                ? undefined 
                                : (iconSettings.iconColor || '#000000')
                            }
                          />
                        </div>
                        <div className="flex flex-col justify-center">
                          <span className="text-sm font-bold text-slate-800 tracking-tight leading-tight">
                            {escapeHtml(platform.name || (SOCIAL_PLATFORMS.find(p => p.value === platform.icon)?.label) || platform.icon || 'Social')}
                          </span>
                        </div>
                      </button>
                      <div className="flex items-center gap-1">
                        <div className="flex items-center opacity-0 group-hover/card:opacity-100 transition-all duration-500 translate-x-1 group-hover/card:translate-x-0">
                          <button
                            type="button"
                            onClick={() => index > 0 && movePlatform(index, index - 1)}
                            disabled={index === 0}
                            className="p-1 text-slate-300 hover:text-slate-600 rounded-lg transition-all disabled:opacity-20 hover:bg-slate-50"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => index < platforms.length - 1 && movePlatform(index, index + 1)}
                            disabled={index === platforms.length - 1}
                            className="p-1 text-slate-300 hover:text-slate-600 rounded-lg transition-all disabled:opacity-20 hover:bg-slate-50"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => removePlatform(index)}
                          className="p-1.5 text-slate-300 hover:text-red-500 rounded-lg transition-all ml-0.5 opacity-50 hover:opacity-100 hover:bg-red-50"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="px-4 pb-4 space-y-4">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 px-1">Link URL</label>
                        <div className="relative group/input">
                          <input
                            type="url"
                            value={platform.url || ''}
                            onChange={(e) => updatePlatform(index, { url: e.target.value })}
                            placeholder="#"
                            className="w-full px-3 py-2 text-xs border border-slate-100 rounded-lg bg-slate-50/30 focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-500/5 focus:border-slate-300 transition-all duration-500 font-mono font-bold text-slate-700 shadow-inner"
                          />
                        </div>
                      </div>

                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setShowPlatformPicker(-1)} // Use -1 as indicator for new platform
                className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-3.5 text-sm font-bold text-slate-800 bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl hover:bg-slate-100 hover:border-slate-400 hover:text-slate-700 transition-all active:scale-[0.98]"
              >
                <PlusIcon className="w-5 h-5" />
                Add Social Media Link
              </button>
            </div>

            {/* Layout & Style Section */}
            <div className="pt-10 border-t border-slate-100 space-y-8">
              <div className="flex flex-col">
                <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] px-1 mb-1">Layout & Design</h4>
                <div className="h-1 w-8 bg-slate-500 rounded-full ml-1"></div>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3 px-0.5">Alignment</label>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    {[
                      { value: 'left', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h10M4 18h16" /></svg> },
                      { value: 'center', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M7 12h10M4 18h16" /></svg> },
                      { value: 'right', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M10 12h10M4 18h16" /></svg> },
                    ].map((align) => {
                      const isActive = (iconSettings.alignment || 'center') === align.value;
                      return (
                        <button
                          key={align.value}
                          type="button"
                          onClick={() => updateIconSettings({ alignment: align.value })}
                          className={`flex-1 flex items-center justify-center p-2 rounded-lg transition-all ${
                            isActive
                              ? 'bg-slate-100 text-slate-900 shadow-sm'
                              : 'text-slate-400 hover:bg-slate-50'
                          }`}
                        >
                          {align.icon}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3 px-0.5">Flow Direction</label>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    {[
                      { value: 'horizontal', label: 'Row' },
                      { value: 'vertical', label: 'Col' },
                    ].map((mode) => {
                      const isActive = (iconSettings.layout || 'horizontal') === mode.value;
                      return (
                        <button
                          key={mode.value}
                          type="button"
                          onClick={() => updateIconSettings({ layout: mode.value })}
                          className={`flex-1 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${
                            isActive
                              ? 'bg-slate-100 text-slate-900 shadow-sm'
                              : 'text-slate-400 hover:bg-slate-50'
                          }`}
                        >
                          {mode.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6 items-end">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3 block px-0.5 flex items-center justify-between">
                    Icon Spacing
                    <span className="text-[10px] font-black text-slate-800 bg-slate-50 px-2 py-0.5 rounded-lg border border-slate-100">
                      {iconSettings.gap || 12}PX
                    </span>
                  </label>
                  <div className="px-1 py-1">
                    <input
                      type="range"
                      min="0"
                      max="48"
                      step="4"
                      value={iconSettings.gap || 12}
                      onChange={(e) => updateIconSettings({ gap: parseInt(e.target.value) })}
                      className="v2-slider w-full"
                    />
                  </div>
                </div>

                <div className="col-span-2">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-4 px-0.5">Icon Style & Shape</label>
                  <div className="grid grid-cols-4 gap-4">
                    {[
                      { value: 'brand', label: 'Brand' },
                      { value: 'solid', label: 'Solid' },
                      { value: 'outline', label: 'Outline' },
                      { value: 'circle-brand', label: 'Circle' },
                      { value: 'circle-solid', label: 'C-Solid' },
                      { value: 'circle-outline', label: 'C-Out' },
                      { value: 'square-solid', label: 'Square' },
                      { value: 'square-brand', label: 'S-Brand' },
                    ].map((style) => {
                      const isActive = (iconSettings.iconStyle || 'solid') === style.value;
                      return (
                        <button
                          key={style.value}
                          type="button"
                          onClick={() => updateIconSettings({ iconStyle: style.value as any })}
                          className={`flex flex-col items-center justify-center p-3 border rounded-2xl transition-all ${
                            isActive
                              ? 'border-slate-500 bg-slate-50 ring-2 ring-slate-100 shadow-sm'
                              : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                          }`}
                        >
                          <div className="mb-2 h-7 flex items-center justify-center">
                            <SocialMediaIcon 
                              platform="sample" 
                              className="w-5 h-5" 
                              styleOverride={style.value}
                              colorOverride={style.value.includes('brand') ? undefined : iconSettings.iconColor}
                            />
                          </div>
                          <span className={`text-[9px] font-bold uppercase tracking-tight text-center w-full truncate ${isActive ? 'text-slate-800' : 'text-slate-400'}`}>
                            {style.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3 px-0.5 text-center">Icon Size</label>
                  <div className="flex bg-slate-50 p-1.5 rounded-2xl border border-slate-200 shadow-inner max-w-xs mx-auto">
                    {[16, 24, 32].map((s) => {
                      const isActive = (iconSettings.size || 24) === s;
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => updateIconSettings({ size: s })}
                          className={`flex-1 py-2.5 rounded-xl transition-all flex items-center justify-center ${
                            isActive
                              ? 'bg-white text-slate-800 shadow-md ring-1 ring-slate-200'
                              : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200/50'
                          }`}
                        >
                          <span className={`text-[10px] font-black ${isActive ? 'scale-110' : 'scale-90 opacity-60'}`}>{s}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Picker Modal Portal */}
            {showPlatformPicker !== null && renderPlatformPicker(showPlatformPicker === -1 ? 'new' : showPlatformPicker)}
          </div>
        );

        return renderContentTab();
      }

      case 'countdown': {
        const targetDate = block.content.targetDate || block.content.endDate || '';
        const targetDateValue = targetDate ? new Date(targetDate).toISOString().slice(0, 16) : '';

        return (
          <div className="space-y-4">
            <div className="space-y-3 border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <h5 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Timer Settings
                </h5>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Target Date & Time</label>
                <input
                  type="datetime-local"
                  value={targetDateValue}
                  onChange={(e) => {
                    if (e.target.value) {
                      const date = new Date(e.target.value);
                      handleContentChange('targetDate', date.toISOString());
                    }
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Set the date and time when the countdown should end
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title (Optional)</label>
                <input
                  type="text"
                  value={block.content.title || ''}
                  onChange={(e) => handleContentChange('title', e.target.value)}
                  placeholder="e.g., Limited Time Offer"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Layout</label>
                <select
                  value={block.content.layout || 'horizontal'}
                  onChange={(e) => handleContentChange('layout', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <option value="horizontal">Horizontal</option>
                  <option value="vertical">Vertical</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`countdown-show-labels-${block.id}`}
                  checked={block.content.showLabels !== false}
                  onChange={(e) => handleContentChange('showLabels', e.target.checked)}
                  className="w-4 h-4 text-slate-500 border-slate-300 rounded focus:ring-slate-500"
                />
                <label htmlFor={`countdown-show-labels-${block.id}`} className="text-sm text-slate-700">
                  Show labels (Days, Hours, Minutes, Seconds)
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Expired Message</label>
                <input
                  type="text"
                  value={block.content.expiredMessage || 'The countdown has ended'}
                  onChange={(e) => handleContentChange('expiredMessage', e.target.value)}
                  placeholder="Message to show when countdown ends"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
                />
              </div>
            </div>

            <div className="space-y-3 border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <h5 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                  Appearance
                </h5>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Timer Color</label>
                <div className="relative">
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'countdownColor' ? null : 'countdownColor')}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 flex items-center gap-2"
                  >
                    <div
                      className="w-6 h-6 rounded-full border border-slate-300"
                      style={{
                        backgroundColor: block.styles.color || '#111827',
                      }}
                    />
                    <span className="text-sm text-slate-600">
                      {block.styles.color || '#111827'}
                    </span>
                  </button>
                  {showColorPicker === 'countdownColor' && (
                    <div ref={colorPickerRef} className="absolute z-10 mt-1 mb-4">
                      <CustomColorPicker
                        currentColor={block.styles.color || '#111827'}
                        onChange={(color) => handleStyleChange('color', color)}
                        pickerType="countdownColor"
                        emailColors={getEmailBackgroundColors()}
                        brandColors={brandColors}
                        showAdvancedPicker={showAdvancedPicker}
                        setShowAdvancedPicker={setShowAdvancedPicker}
                        setAdvancedPickerPosition={setAdvancedPickerPosition}
                      />
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Color of the countdown numbers and title
                </p>
              </div>
            </div>
          </div>
        );
      }

      case 'hero-content': {
        const template = block.content.template || 'standard';

        if (shouldUseHeroContentPropertiesPanel(template)) {
          return <HeroContentPropertiesPanel ctx={editorialPropertyCtx} />;
        }

        if (template === 'image-overlay') {
          return (
            <div className="space-y-6">
              {/* Content Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4 px-1">
                  <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Content & Visuals</label>
                  <div className="h-0.5 flex-1 bg-slate-100 ml-4"></div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2 relative" ref={imageSourceDropRef}>
                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">Add image from...</label>
                    <div className="relative">
                      <button
                        onClick={() => setIsImageSourceDropOpen(!isImageSourceDropOpen)}
                        className="w-full flex items-center justify-between px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                      >
                        <div className="flex items-center gap-2">
                          <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                          <span>My computer</span>
                        </div>
                        <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isImageSourceDropOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {isImageSourceDropOpen && (
                        <div className="absolute top-full left-0 right-0 mt-2 py-1 bg-white border border-slate-100 rounded-xl shadow-xl z-[100] animate-in fade-in zoom-in-95 duration-100">
                          <button
                            onClick={() => {
                              imageTargetFieldRef.current = 'image';
                              setIsImageSourceDropOpen(false);
                              if (isUploadingImage) return;
                              fileInputRef.current?.click();
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">My computer</span>
                          </button>
                          <button
                            onClick={() => {
                              imageTargetFieldRef.current = 'image';
                              setIsImageSourceDropOpen(false);
                              setIsStockImageModalOpen(true);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <PhotoIcon className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">Stock image</span>
                          </button>
                          <button
                            onClick={() => {
                              imageTargetFieldRef.current = 'image';
                              setIsImageSourceDropOpen(false);
                              setContentManagerTab('image');
                              setIsContentManagerOpen(true);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <QueueListIcon className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">Library</span>
                          </button>
                          <ImageSourceFromCanvaRow onClose={() => setIsImageSourceDropOpen(false)} />
                          {block.content.image && (
                            <>
                              <div className="h-px bg-slate-100 my-1 mx-2" />
                              <button
                                onClick={() => {
                                  handleContentChange('image', '');
                                  setIsImageSourceDropOpen(false);
                                }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                              >
                                <TrashIcon className="w-4 h-4" />
                                <span className="font-semibold">Remove Image</span>
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">Heading Text</label>
                    <textarea
                      value={block.content.heading || ''}
                      onChange={(e) => handleContentChange('heading', e.target.value)}
                      placeholder="COMPLETE YOUR ORDER"
                      rows={2}
                      className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 transition-all duration-200"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">Supporting Text</label>
                    <textarea
                      value={block.content.text || ''}
                      onChange={(e) => handleContentChange('text', e.target.value)}
                      placeholder="Supporting subtitle text"
                      rows={2}
                      className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 transition-all duration-200"
                    />
                  </div>
                </div>
              </div>

              {/* Call to Action Section */}
              <div className="space-y-4 pt-2 border-t border-slate-100">
                <div className="flex items-center justify-between mb-4 px-1">
                  <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Call to Action</label>
                  <div className="h-0.5 flex-1 bg-slate-100 ml-4"></div>
                </div>

                <div className="flex items-center justify-between py-1 px-1">
                  <label className="text-sm font-medium text-slate-700">Show Button</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={block.content.showButton !== false}
                      onChange={(e) => handleContentChange('showButton', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-slate-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-500"></div>
                  </label>
                </div>

                <PaddingControl
                  label="Button Padding"
                  value={block.content.buttonPadding || '12px 32px'}
                  onChange={(val) => handleContentChange('buttonPadding', val)}
                />

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">Button Text</label>
                    <input
                      type="text"
                      value={block.content.buttonText || ''}
                      onChange={(e) => handleContentChange('buttonText', e.target.value)}
                      placeholder="Shop Now"
                      className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">Button URL</label>
                    <input
                      type="text"
                      value={block.content.buttonUrl || ''}
                      onChange={(e) => handleContentChange('buttonUrl', e.target.value)}
                      placeholder="#"
                      className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">Button Color</label>
                    <div className="relative">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'heroOverlayButtonBg' ? null : 'heroOverlayButtonBg')}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg flex items-center gap-2 bg-white transition-all shadow-sm"
                      >
                        <div
                          className="w-5 h-5 rounded-full border border-slate-200"
                          style={{ backgroundColor: block.content.buttonColor || block.styles.color || '#FFFFFF' }}
                        />
                        <span className="text-xs font-mono text-slate-500 uppercase">
                          {(block.content.buttonColor || block.styles.color || '#FFFFFF').replace('#', '')}
                        </span>
                      </button>
                      {showColorPicker === 'heroOverlayButtonBg' && (
                        <div ref={colorPickerRef} className="absolute z-[101] top-full left-0 mt-1">
                          <CustomColorPicker
                            currentColor={block.content.buttonColor || block.styles.color || '#FFFFFF'}
                            onChange={(color) => handleContentChange('buttonColor', color)}
                            pickerType="solid"
                            emailColors={getEmailBackgroundColors()}
                            brandColors={brandColors}
                            showAdvancedPicker={showAdvancedPicker}
                            setShowAdvancedPicker={setShowAdvancedPicker}
                            setAdvancedPickerPosition={setAdvancedPickerPosition}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">Button Text Color</label>
                    <div className="relative">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'heroOverlayButtonText' ? null : 'heroOverlayButtonText')}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg flex items-center gap-2 bg-white transition-all shadow-sm"
                      >
                        <div
                          className="w-5 h-5 rounded-full border border-slate-200"
                          style={{ backgroundColor: block.content.buttonTextColor || '#000000' }}
                        />
                        <span className="text-xs font-mono text-slate-500 uppercase">
                          {(block.content.buttonTextColor || '#000000').replace('#', '')}
                        </span>
                      </button>
                      {showColorPicker === 'heroOverlayButtonText' && (
                        <div ref={colorPickerRef} className="absolute z-[101] top-full left-0 mt-1">
                          <CustomColorPicker
                            currentColor={block.content.buttonTextColor || '#000000'}
                            onChange={(color) => handleContentChange('buttonTextColor', color)}
                            pickerType="solid"
                            emailColors={getEmailBackgroundColors()}
                            brandColors={brandColors}
                            showAdvancedPicker={showAdvancedPicker}
                            setShowAdvancedPicker={setShowAdvancedPicker}
                            setAdvancedPickerPosition={setAdvancedPickerPosition}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <CollapsibleSection title="Typography" defaultOpen={true}>
                {renderHeroOverlayTypography()}
                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <label className="block text-xs font-semibold text-slate-700">Text Shadow</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={block.styles.textShadow === '0 2px 10px rgba(0,0,0,0.5)'}
                      onChange={(e) => handleStyleChange('textShadow', e.target.checked ? '0 2px 10px rgba(0,0,0,0.5)' : 'none')}
                      className="w-4 h-4 rounded border-slate-300 text-slate-800 focus:ring-slate-500/20"
                    />
                  </div>
                </div>
              </CollapsibleSection>

              {/* Layout Section */}
              <div className="space-y-4 pt-2 border-t border-slate-100">
                <div className="flex items-center justify-between mb-4 px-1">
                  <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Layout & Position</label>
                  <div className="h-0.5 flex-1 bg-slate-100 ml-4"></div>
                </div>

                <PaddingControl
                  label="Content padding"
                  value={
                    block.styles.padding && !isEffectivelyZeroPadding(block.styles.padding)
                      ? block.styles.padding
                      : '60px 40px'
                  }
                  onChange={(val) => handleStyleChange('padding', val)}
                />

                <div className="space-y-2">
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-xs font-semibold text-slate-700">Block Height</label>
                    <span className="text-xs font-bold text-slate-400">{block.styles.height || '600px'}</span>
                  </div>
                  <input
                    type="range"
                    min="300"
                    max="1000"
                    step="10"
                    value={parseInt(block.styles.height) || 600}
                    onChange={(e) => handleStyleChange('height', `${e.target.value}px`)}
                    className="v2-spacing-slider w-full"
                    style={{ background: `linear-gradient(90deg, #64748b ${((parseInt(block.styles.height) || 600) - 300) / 700 * 100}%, #f1f5f9 ${((parseInt(block.styles.height) || 600) - 300) / 700 * 100}%)` }}
                  />
                </div>

                <div className="grid grid-cols-2 gap-6 pt-2">
                  <div>
                    <label className="block text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2.5">Vertical</label>
                    <div className="flex gap-1">
                      {[
                        { id: 'flex-start', label: 'Top' },
                        { id: 'center', label: 'Mid' },
                        { id: 'flex-end', label: 'Btm' }
                      ].map((align) => (
                        <button
                          key={align.id}
                          onClick={() => handleStyleChange('justifyContent', align.id)}
                          className={`flex-1 py-2 text-[10px] font-black uppercase tracking-tight rounded-lg transition-all border ${
                            (block.styles.justifyContent || 'flex-end') === align.id
                              ? 'bg-slate-900 border-slate-900 text-white shadow-sm'
                              : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                          }`}
                        >
                          {align.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2.5">Horizontal</label>
                    <div className="flex gap-1">
                      {[
                        { id: 'left', label: 'Left' },
                        { id: 'center', label: 'Ctr' },
                        { id: 'right', label: 'Rgt' }
                      ].map((align) => (
                        <button
                          key={align.id}
                          onClick={() => handleStyleChange('textAlign', align.id)}
                          className={`flex-1 py-2 text-[10px] font-black uppercase tracking-tight rounded-lg transition-all border ${
                            (block.styles.textAlign || 'left') === align.id
                              ? 'bg-slate-900 border-slate-900 text-white shadow-sm'
                              : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                          }`}
                        >
                          {align.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }

        if (template === 'hero-card') {
          return (
            <div className="space-y-6">
              {/* Content Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4 px-1">
                  <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Content & Visuals</label>
                  <div className="h-0.5 flex-1 bg-slate-100 ml-4"></div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2 relative" ref={imageSourceDropRef}>
                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">Add image from...</label>
                    <div className="relative">
                      <button
                        onClick={() => setIsImageSourceDropOpen(!isImageSourceDropOpen)}
                        className="w-full flex items-center justify-between px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                      >
                        <div className="flex items-center gap-2">
                          <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                          <span>My computer</span>
                        </div>
                        <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isImageSourceDropOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {isImageSourceDropOpen && (
                        <div className="absolute top-full left-0 right-0 mt-2 py-1 bg-white border border-slate-100 rounded-xl shadow-xl z-[100] animate-in fade-in zoom-in-95 duration-100">
                          <button
                            onClick={() => {
                              imageTargetFieldRef.current = 'image';
                              setIsImageSourceDropOpen(false);
                              if (isUploadingImage) return;
                              fileInputRef.current?.click();
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">My computer</span>
                          </button>
                          <button
                            onClick={() => {
                              imageTargetFieldRef.current = 'image';
                              setIsImageSourceDropOpen(false);
                              setIsStockImageModalOpen(true);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <PhotoIcon className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">Stock image</span>
                          </button>
                          <button
                            onClick={() => {
                              imageTargetFieldRef.current = 'image';
                              setIsImageSourceDropOpen(false);
                              setContentManagerTab('image');
                              setIsContentManagerOpen(true);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <QueueListIcon className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">Library</span>
                          </button>
                          <ImageSourceFromCanvaRow onClose={() => setIsImageSourceDropOpen(false)} />
                          {block.content.image && (
                            <>
                              <div className="h-px bg-slate-100 my-1 mx-2" />
                              <button
                                onClick={() => {
                                  handleContentChange('image', '');
                                  setIsImageSourceDropOpen(false);
                                }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                              >
                                <TrashIcon className="w-4 h-4" />
                                <span className="font-semibold">Remove Image</span>
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center mb-1">
                      <label className="block text-xs font-semibold text-slate-700">Card Overlap Offset</label>
                      <span className="text-[10px] font-bold text-slate-400">{block.content.cardOffset || '-80px'}</span>
                    </div>
                    <input
                      type="range"
                      min="-200"
                      max="-20"
                      step="5"
                      value={parseInt(block.content.cardOffset) || -80}
                      onChange={(e) => handleContentChange('cardOffset', `${e.target.value}px`)}
                      className="v2-spacing-slider w-full"
                      style={{ background: `linear-gradient(90deg, #64748b ${(((parseInt(block.content.cardOffset) || -80) - -200) / 180) * 100}%, #f1f5f9 ${(((parseInt(block.content.cardOffset) || -80) - -200) / 180) * 100}%)` }}
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center mb-1">
                      <label className="block text-xs font-semibold text-slate-700">Image Maximum Width</label>
                      <span className="text-[10px] font-bold text-slate-400">{block.content.imageWidth || '480px'}</span>
                    </div>
                    <input
                      type="range"
                      min="200"
                      max="600"
                      step="10"
                      value={parseInt(block.content.imageWidth) || 480}
                      onChange={(e) => handleContentChange('imageWidth', `${e.target.value}px`)}
                      className="v2-spacing-slider w-full"
                      style={{ background: `linear-gradient(90deg, #64748b ${(( (parseInt(block.content.imageWidth) || 480) - 200) / 400) * 100}%, #f1f5f9 ${(( (parseInt(block.content.imageWidth) || 480) - 200) / 400) * 100}%)` }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-slate-700">Subtitle</label>
                  <input
                    type="text"
                    value={block.content.subtitle || ''}
                    onChange={(e) => handleContentChange('subtitle', e.target.value)}
                    placeholder="SHOCKINGLY SOFT TEES"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-slate-700">Heading</label>
                  <textarea
                    value={block.content.heading || ''}
                    onChange={(e) => handleContentChange('heading', e.target.value)}
                    placeholder="We're Bringing Basic Back"
                    rows={2}
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-slate-700">Body Text</label>
                  <textarea
                    value={block.content.text || ''}
                    onChange={(e) => handleContentChange('text', e.target.value)}
                    placeholder="Supporting subtitle text"
                    rows={3}
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>
              </div>

              {/* Call to Action Section */}
              <div className="space-y-4 pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between mb-4 px-1">
                  <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Call to Action</label>
                  <div className="h-0.5 flex-1 bg-slate-100 ml-4"></div>
                </div>

                <div className="flex items-center justify-between py-1 px-1">
                  <label className="text-xs font-semibold text-slate-700">Show Button</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={block.content.showButton !== false}
                      onChange={(e) => handleContentChange('showButton', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-slate-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-500"></div>
                  </label>
                </div>

                <PaddingControl
                  label="Button Padding"
                  value={block.content.buttonPadding || '12px 32px'}
                  onChange={(val) => handleContentChange('buttonPadding', val)}
                />

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-slate-700">Button Text</label>
                    <input
                      type="text"
                      value={block.content.buttonText || ''}
                      onChange={(e) => handleContentChange('buttonText', e.target.value)}
                      placeholder="Shop Now"
                      className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-slate-700">Button URL</label>
                    <input
                      type="text"
                      value={block.content.buttonUrl || ''}
                      onChange={(e) => handleContentChange('buttonUrl', e.target.value)}
                      placeholder="#"
                      className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-slate-700 mb-2">Button Color</label>
                    <div className="relative">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'heroCardButtonBg' ? null : 'heroCardButtonBg')}
                        className="w-full h-10 px-2 rounded-xl border border-slate-200 flex items-center gap-3 bg-white hover:bg-slate-50 transition-all shadow-sm"
                      >
                        <div className="w-6 h-6 rounded-lg shadow-inner border border-black/5" style={{ backgroundColor: block.content.buttonColor || '#000000' }} />
                        <span className="text-xs font-mono text-slate-400 uppercase">{(block.content.buttonColor || '#000000').replace('#', '')}</span>
                      </button>
                      {showColorPicker === 'heroCardButtonBg' && (
                        <div ref={colorPickerRef} className="absolute left-0 mt-2 z-[101]">
                          <CustomColorPicker
                            currentColor={block.content.buttonColor || '#000000'}
                            onChange={(color) => handleContentChange('buttonColor', color)}
                            pickerType="solid"
                            emailColors={getEmailBackgroundColors()}
                            brandColors={brandColors}
                            showAdvancedPicker={showAdvancedPicker}
                            setShowAdvancedPicker={setShowAdvancedPicker}
                            setAdvancedPickerPosition={setAdvancedPickerPosition}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-slate-700 mb-2">Card Color</label>
                    <div className="relative">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'heroCardBg' ? null : 'heroCardBg')}
                        className="w-full h-10 px-2 rounded-xl border border-slate-200 flex items-center gap-3 bg-white hover:bg-slate-50 transition-all shadow-sm"
                      >
                        <div className="w-6 h-6 rounded-lg shadow-inner border border-black/5" style={{ backgroundColor: block.content.cardBackgroundColor || '#FFFFFF' }} />
                        <span className="text-xs font-mono text-slate-400 uppercase">{(block.content.cardBackgroundColor || '#FFFFFF').replace('#', '')}</span>
                      </button>
                      {showColorPicker === 'heroCardBg' && (
                        <div ref={colorPickerRef} className="absolute right-0 mt-2 z-[101]">
                          <CustomColorPicker
                            currentColor={block.content.cardBackgroundColor || '#FFFFFF'}
                            onChange={(color) => handleContentChange('cardBackgroundColor', color)}
                            pickerType="solid"
                            emailColors={getEmailBackgroundColors()}
                            brandColors={brandColors}
                            showAdvancedPicker={showAdvancedPicker}
                            setShowAdvancedPicker={setShowAdvancedPicker}
                            setAdvancedPickerPosition={setAdvancedPickerPosition}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Layout Section */}
              <div className="space-y-4 pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between mb-4 px-1">
                  <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Layout</label>
                  <div className="h-0.5 flex-1 bg-slate-100 ml-4"></div>
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-slate-700 mb-2">Block Background Color</label>
                  <div className="relative">
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'heroCardBlockBg' ? null : 'heroCardBlockBg')}
                      className="w-full h-10 px-2 rounded-xl border border-slate-200 flex items-center gap-3 bg-white hover:bg-slate-50 transition-all shadow-sm"
                    >
                      <div className="w-6 h-6 rounded-lg shadow-inner border border-black/5" style={{ backgroundColor: block.styles.backgroundColor || '#F3F4F6' }} />
                      <span className="text-xs font-mono text-slate-400 uppercase">{(block.styles.backgroundColor || '#F3F4F6').replace('#', '')}</span>
                    </button>
                    {showColorPicker === 'heroCardBlockBg' && (
                      <div ref={colorPickerRef} className="absolute left-0 mt-2 z-[101]">
                        <CustomColorPicker
                          currentColor={block.styles.backgroundColor || '#F3F4F6'}
                          onChange={(color) => handleStyleChange('backgroundColor', color)}
                          pickerType="solid"
                          emailColors={getEmailBackgroundColors()}
                          brandColors={brandColors}
                          showAdvancedPicker={showAdvancedPicker}
                          setShowAdvancedPicker={setShowAdvancedPicker}
                          setAdvancedPickerPosition={setAdvancedPickerPosition}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <HeroCardExtraColorFields ctx={editorialPropertyCtx} />
            </div>
          );
        }

        if (template === 'editorial') {
          return <HeroEditorialPropertiesPanel ctx={editorialPropertyCtx} />;
        }

        if (template === 'quote') {
          return <HeroQuotePropertiesPanel ctx={editorialPropertyCtx} />;
        }

        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2 relative" ref={imageSourceDropRef}>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">Add image from...</label>
                <div className="relative">
                  <button
                    onClick={() => setIsImageSourceDropOpen(!isImageSourceDropOpen)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                  >
                    <div className="flex items-center gap-2">
                      <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                      <span>Upload</span>
                    </div>
                    <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isImageSourceDropOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isImageSourceDropOpen && (
                    <div className="absolute top-full left-0 right-0 mt-2 py-1 bg-white border border-slate-100 rounded-xl shadow-xl z-[100] animate-in fade-in zoom-in-95 duration-100">
                      <button
                        onClick={() => {
                          imageTargetFieldRef.current = 'image';
                          setIsImageSourceDropOpen(false);
                          if (isUploadingImage) return;
                          fileInputRef.current?.click();
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Upload</span>
                      </button>
                      <button
                        onClick={() => {
                          imageTargetFieldRef.current = 'image';
                          setIsImageSourceDropOpen(false);
                          setIsStockImageModalOpen(true);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <PhotoIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Stock image</span>
                      </button>
                      <button
                        onClick={() => {
                          imageTargetFieldRef.current = 'image';
                          setIsImageSourceDropOpen(false);
                          setContentManagerTab('image');
                          setIsContentManagerOpen(true);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <QueueListIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Library</span>
                      </button>
                      <ImageSourceFromCanvaRow onClose={() => setIsImageSourceDropOpen(false)} />
                      {block.content.image && (
                        <>
                          <div className="h-px bg-slate-100 my-1 mx-2" />
                          <button
                            onClick={() => {
                              handleContentChange('image', '');
                              setIsImageSourceDropOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                            <span className="font-semibold">Remove Image</span>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-slate-100">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Content</label>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Heading</label>
                <input
                  type="text"
                  value={block.content.heading || ''}
                  onChange={(e) => handleContentChange('heading', e.target.value)}
                  placeholder="Heading text"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Text</label>
                <textarea
                  value={block.content.text || ''}
                  onChange={(e) => handleContentChange('text', e.target.value)}
                  placeholder="Description text"
                  rows={4}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>
            </div>
            
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Call to Action</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Button Text</label>
                  <input
                    type="text"
                    value={block.content.buttonText || ''}
                    onChange={(e) => handleContentChange('buttonText', e.target.value)}
                    placeholder="Button text"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Button URL</label>
                  <input
                    type="url"
                    value={block.content.buttonUrl || ''}
                    onChange={(e) => handleContentChange('buttonUrl', e.target.value)}
                    placeholder="https://example.com"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between py-2">
                <label className="text-xs font-semibold text-slate-700">Show Button</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={block.content.showButton !== false}
                    onChange={(e) => handleContentChange('showButton', e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-slate-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-500"></div>
                </label>
              </div>

              <PaddingControl
                label="Button Padding"
                value={block.content.buttonPadding || '11px 24px'}
                onChange={(val) => handleContentChange('buttonPadding', val)}
              />
            </div>
          </div>
        );
      }

      case 'image-content': {
        const template = block.content.template || 'standard';
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Image</label>
                <div className="space-y-2 relative" ref={imageSourceDropRef}>
                  <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">Add image from...</label>
                  <div className="relative">
                    <button
                      onClick={() => setIsImageSourceDropOpen(!isImageSourceDropOpen)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                    >
                      <div className="flex items-center gap-2">
                        <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                        <span>Upload</span>
                      </div>
                      <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isImageSourceDropOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isImageSourceDropOpen && (
                      <div className="absolute top-full left-0 right-0 mt-2 py-1 bg-white border border-slate-100 rounded-xl shadow-xl z-[100] animate-in fade-in zoom-in-95 duration-100">
                        <button
                          onClick={() => {
                            imageTargetFieldRef.current = 'image';
                            setIsImageSourceDropOpen(false);
                            if (isUploadingImage) return;
                            fileInputRef.current?.click();
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">Upload</span>
                        </button>
                        <button
                          onClick={() => {
                            imageTargetFieldRef.current = 'image';
                            setIsImageSourceDropOpen(false);
                            setIsStockImageModalOpen(true);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <PhotoIcon className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">Stock image</span>
                        </button>
                        <button
                          onClick={() => {
                            imageTargetFieldRef.current = 'image';
                            setIsImageSourceDropOpen(false);
                            setContentManagerTab('image');
                            setIsContentManagerOpen(true);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <QueueListIcon className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">Library</span>
                        </button>
                        <ImageSourceFromCanvaRow onClose={() => setIsImageSourceDropOpen(false)} />
                        {block.content.image && (
                          <>
                            <div className="h-px bg-slate-100 my-1 mx-2" />
                            <button
                              onClick={() => {
                                handleContentChange('image', '');
                                setIsImageSourceDropOpen(false);
                              }}
                              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                              <span className="font-semibold">Remove Image</span>
                            </button>
                          </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

              </div>

              <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Subtitle</label>
              <input
                type="text"
                value={block.content.subtitle || ''}
                onChange={(e) => handleContentChange('subtitle', e.target.value)}
                placeholder="Subtitle text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Heading</label>
              <input
                type="text"
                value={block.content.heading || ''}
                onChange={(e) => handleContentChange('heading', e.target.value)}
                placeholder="Heading text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Text</label>
              <textarea
                value={block.content.text || ''}
                onChange={(e) => handleContentChange('text', e.target.value)}
                placeholder="Description text"
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Button Text</label>
              <input
                type="text"
                value={block.content.buttonText || ''}
                onChange={(e) => handleContentChange('buttonText', e.target.value)}
                placeholder="Button text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Button URL</label>
              <input
                type="url"
                value={block.content.buttonUrl || ''}
                onChange={(e) => handleContentChange('buttonUrl', e.target.value)}
                placeholder="https://example.com"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>
            {template === 'editorial' && <ImageContentEditorialExtras ctx={editorialPropertyCtx} />}
          </div>
        );
      }

      case 'feature-showcase': {
        const template = block.content.template || 'standard';
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2 relative" ref={imageSourceDropRef}>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">Add image from...</label>
                <div className="relative">
                  <button
                    onClick={() => setIsImageSourceDropOpen(!isImageSourceDropOpen)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                  >
                    <div className="flex items-center gap-2">
                      <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                      <span>Upload</span>
                    </div>
                    <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isImageSourceDropOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isImageSourceDropOpen && (
                    <div className="absolute top-full left-0 right-0 mt-2 py-1 bg-white border border-slate-100 rounded-xl shadow-xl z-[100] animate-in fade-in zoom-in-95 duration-100">
                      <button
                        onClick={() => {
                          imageTargetFieldRef.current = 'image';
                          setIsImageSourceDropOpen(false);
                          if (isUploadingImage) return;
                          fileInputRef.current?.click();
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Upload</span>
                      </button>
                      <button
                        onClick={() => {
                          imageTargetFieldRef.current = 'image';
                          setIsImageSourceDropOpen(false);
                          setIsStockImageModalOpen(true);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <PhotoIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Stock image</span>
                      </button>
                      <button
                        onClick={() => {
                          imageTargetFieldRef.current = 'image';
                          setIsImageSourceDropOpen(false);
                          setContentManagerTab('image');
                          setIsContentManagerOpen(true);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <QueueListIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Library</span>
                      </button>
                      <ImageSourceFromCanvaRow onClose={() => setIsImageSourceDropOpen(false)} />
                      {block.content.image && (
                        <>
                          <div className="h-px bg-slate-100 my-1 mx-2" />
                          <button
                            onClick={() => {
                              handleContentChange('image', '');
                              setIsImageSourceDropOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                            <span className="font-semibold">Remove Image</span>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-slate-100">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Content</label>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Heading</label>
                <input
                  type="text"
                  value={block.content.heading || ''}
                  onChange={(e) => handleContentChange('heading', e.target.value)}
                  placeholder="Feature heading"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Description</label>
                <textarea
                  value={block.content.text || ''}
                  onChange={(e) => handleContentChange('text', e.target.value)}
                  placeholder="Feature description"
                  rows={4}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-slate-100">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Call to Action</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Button Text</label>
                  <input
                    type="text"
                    value={block.content.buttonText || ''}
                    onChange={(e) => handleContentChange('buttonText', e.target.value)}
                    placeholder="Button text"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Button URL</label>
                  <input
                    type="url"
                    value={block.content.buttonUrl || ''}
                    onChange={(e) => handleContentChange('buttonUrl', e.target.value)}
                    placeholder="https://example.com"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between py-2">
                <label className="text-xs font-semibold text-slate-700">Show Button</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={block.content.showButton !== false}
                    onChange={(e) => handleContentChange('showButton', e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-slate-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-500"></div>
                </label>
              </div>

              <div>
              <PaddingControl
                label="Button Padding"
                value={block.content.buttonPadding || '11px 24px'}
                onChange={(val) => handleContentChange('buttonPadding', val)}
              />
              </div>
            </div>
          </div>
        );
      }

      case 'testimonial': {
        const template = block.content.template || 'standard';
        return (
          <div className="space-y-4">
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Content</label>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Quote</label>
                <textarea
                  value={block.content.quote || ''}
                  onChange={(e) => handleContentChange('quote', e.target.value)}
                  placeholder="Customer testimonial quote"
                  rows={4}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Author Name</label>
                  <input
                    type="text"
                    value={block.content.author || ''}
                    onChange={(e) => handleContentChange('author', e.target.value)}
                    placeholder="Author name"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Author Role</label>
                  <input
                    type="text"
                    value={block.content.role || ''}
                    onChange={(e) => handleContentChange('role', e.target.value)}
                    placeholder="e.g. CEO, Founder"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'cta-banner': {
        const template = block.content.template || 'standard';
        return (
          <div className="space-y-4">
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Content & Messaging</label>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Heading</label>
                <input
                  type="text"
                  value={block.content.heading || ''}
                  onChange={(e) => handleContentChange('heading', e.target.value)}
                  placeholder="CTA heading"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Text</label>
                <textarea
                  value={block.content.text || ''}
                  onChange={(e) => handleContentChange('text', e.target.value)}
                  placeholder="CTA description"
                  rows={3}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>
            </div>
            
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Call to Action</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Button Text</label>
                  <input
                    type="text"
                    value={block.content.buttonText || ''}
                    onChange={(e) => handleContentChange('buttonText', e.target.value)}
                    placeholder="Button text"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Button URL</label>
                  <input
                    type="url"
                    value={block.content.buttonUrl || ''}
                    onChange={(e) => handleContentChange('buttonUrl', e.target.value)}
                    placeholder="https://example.com"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'profile-card': {
        const template = block.content.template || 'standard';
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2 relative" ref={imageSourceDropRef}>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">Add profile image from...</label>
                <div className="relative">
                  <button
                    onClick={() => setIsImageSourceDropOpen(!isImageSourceDropOpen)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                  >
                    <div className="flex items-center gap-2">
                      <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                      <span>Upload</span>
                    </div>
                    <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isImageSourceDropOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isImageSourceDropOpen && (
                    <div className="absolute top-full left-0 right-0 mt-2 py-1 bg-white border border-slate-100 rounded-xl shadow-xl z-[100] animate-in fade-in zoom-in-95 duration-100">
                      <button
                        onClick={() => {
                          imageTargetFieldRef.current = 'profileImage';
                          setIsImageSourceDropOpen(false);
                          if (isUploadingImage) return;
                          fileInputRef.current?.click();
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Upload</span>
                      </button>
                      <button
                        onClick={() => {
                          imageTargetFieldRef.current = 'profileImage';
                          setIsImageSourceDropOpen(false);
                          setIsStockImageModalOpen(true);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <PhotoIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Stock image</span>
                      </button>
                      <button
                        onClick={() => {
                          imageTargetFieldRef.current = 'profileImage';
                          setIsImageSourceDropOpen(false);
                          setContentManagerTab('image');
                          setIsContentManagerOpen(true);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <QueueListIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Library</span>
                      </button>
                      <ImageSourceFromCanvaRow onClose={() => setIsImageSourceDropOpen(false)} />
                      {block.content.profileImage && (
                        <>
                          <div className="h-px bg-slate-100 my-1 mx-2" />
                          <button
                            onClick={() => {
                              handleContentChange('profileImage', '');
                              setIsImageSourceDropOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                            <span className="font-semibold">Remove Image</span>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Profile Details</label>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Name</label>
                <input
                  type="text"
                  value={block.content.name || ''}
                  onChange={(e) => handleContentChange('name', e.target.value)}
                  placeholder="Full name"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Role</label>
                <input
                  type="text"
                  value={block.content.role || ''}
                  onChange={(e) => handleContentChange('role', e.target.value)}
                  placeholder="Job title or role"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-slate-100">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Event Details</label>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Event Label</label>
                <input
                  type="text"
                  value={block.content.eventLabel || ''}
                  onChange={(e) => handleContentChange('eventLabel', e.target.value)}
                  placeholder="e.g. EVENT"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Event Title</label>
                <input
                  type="text"
                  value={block.content.eventTitle || ''}
                  onChange={(e) => handleContentChange('eventTitle', e.target.value)}
                  placeholder="Event name"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Event Description</label>
                <textarea
                  value={block.content.eventDescription || ''}
                  onChange={(e) => handleContentChange('eventDescription', e.target.value)}
                  placeholder="Event description"
                  rows={3}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-slate-100">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Call to Action</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Button Text</label>
                  <input
                    type="text"
                    value={block.content.buttonText || ''}
                    onChange={(e) => handleContentChange('buttonText', e.target.value)}
                    placeholder="Button text"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Button URL</label>
                  <input
                    type="url"
                    value={block.content.buttonUrl || ''}
                    onChange={(e) => handleContentChange('buttonUrl', e.target.value)}
                    placeholder="https://example.com"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'product': {
        const productTemplate = block.content.template || 'standard';
        if (productTemplate === 'editorial') {
          return <ProductEditorialPropertiesPanel ctx={editorialPropertyCtx} />;
        }
        const buttonStyle = block.styles.buttonStyle || {};
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2 relative" ref={imageSourceDropRef}>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">Add product image from...</label>
                <div className="relative">
                  <button
                    onClick={() => setIsImageSourceDropOpen(!isImageSourceDropOpen)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                  >
                    <div className="flex items-center gap-2">
                      <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                      <span>Upload</span>
                    </div>
                    <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isImageSourceDropOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isImageSourceDropOpen && (
                    <div className="absolute top-full left-0 right-0 mt-2 py-1 bg-white border border-slate-100 rounded-xl shadow-xl z-[100] animate-in fade-in zoom-in-95 duration-100">
                      <button
                        onClick={() => {
                          imageTargetFieldRef.current = 'image';
                          setIsImageSourceDropOpen(false);
                          if (isUploadingImage) return;
                          fileInputRef.current?.click();
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Upload</span>
                      </button>
                      <button
                        onClick={() => {
                          imageTargetFieldRef.current = 'image';
                          setIsImageSourceDropOpen(false);
                          setIsStockImageModalOpen(true);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <PhotoIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Stock image</span>
                      </button>
                      <button
                        onClick={() => {
                          imageTargetFieldRef.current = 'image';
                          setIsImageSourceDropOpen(false);
                          setContentManagerTab('image');
                          setIsContentManagerOpen(true);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <QueueListIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Library</span>
                      </button>
                      <ImageSourceFromCanvaRow onClose={() => setIsImageSourceDropOpen(false)} />
                      {block.content.image && (
                        <>
                          <div className="h-px bg-slate-100 my-1 mx-2" />
                          <button
                            onClick={() => {
                              handleContentChange('image', '');
                              setIsImageSourceDropOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                            <span className="font-semibold">Remove Image</span>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-slate-100">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Product Details</label>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Product Name</label>
                <input
                  type="text"
                  value={block.content.name || ''}
                  onChange={(e) => handleContentChange('name', e.target.value)}
                  placeholder="Product name"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Description</label>
                <textarea
                  value={block.content.description || ''}
                  onChange={(e) => handleContentChange('description', e.target.value)}
                  placeholder="Product description"
                  rows={3}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Price</label>
                  <input
                    type="text"
                    value={block.content.price || ''}
                    onChange={(e) => handleContentChange('price', e.target.value)}
                    placeholder="$99.99"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Old Price (Optional)</label>
                  <input
                    type="text"
                    value={block.content.oldPrice || ''}
                    onChange={(e) => handleContentChange('oldPrice', e.target.value)}
                    placeholder="$129.99"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-slate-100">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Call to Action</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Button Text</label>
                  <input
                    type="text"
                    value={block.content.buttonText || ''}
                    onChange={(e) => handleContentChange('buttonText', e.target.value)}
                    placeholder="Buy Now"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Product URL</label>
                  <input
                    type="url"
                    value={block.content.url || ''}
                    onChange={(e) => handleContentChange('url', e.target.value)}
                    placeholder="https://example.com/product"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>
              </div>
            </div>

            {/* Button Style Section (v2) */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Button Appearance</label>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Background Color */}
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Background</label>
                  <div className="relative">
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'productButtonBg' ? null : 'productButtonBg')}
                      className="w-full flex items-center justify-between px-3 py-2 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors bg-white shadow-sm"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-5 h-5 rounded shadow-sm border border-slate-200/50"
                          style={{ backgroundColor: buttonStyle.backgroundColor || '#10B981' }}
                        />
                        <span className="text-xs font-mono text-slate-600 uppercase">
                          {buttonStyle.backgroundColor || '#10B981'}
                        </span>
                      </div>
                      <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform ${showColorPicker === 'productButtonBg' ? 'rotate-180' : ''}`} />
                    </button>
                    {showColorPicker === 'productButtonBg' && (
                      <div className="absolute top-full left-0 mt-2 z-[100]">
                        <CustomColorPicker
                          currentColor={buttonStyle.backgroundColor || '#10B981'}
                          onChange={(color) => handleButtonStyleChange('backgroundColor', color)}
                          pickerType="productButtonBg"
                          emailColors={getEmailBackgroundColors()}
                          brandColors={brandColors}
                          showAdvancedPicker={showAdvancedPicker}
                          setShowAdvancedPicker={setShowAdvancedPicker}
                          setAdvancedPickerPosition={setAdvancedPickerPosition}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Text Color */}
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Text Color</label>
                  <div className="relative">
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'productButtonText' ? null : 'productButtonText')}
                      className="w-full flex items-center justify-between px-3 py-2 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors bg-white shadow-sm"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-5 h-5 rounded shadow-sm border border-slate-200/50"
                          style={{ backgroundColor: buttonStyle.color || '#FFFFFF' }}
                        />
                        <span className="text-xs font-mono text-slate-600 uppercase">
                          {buttonStyle.color || '#FFFFFF'}
                        </span>
                      </div>
                      <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform ${showColorPicker === 'productButtonText' ? 'rotate-180' : ''}`} />
                    </button>
                    {showColorPicker === 'productButtonText' && (
                      <div className="absolute top-full right-0 mt-2 z-[100]">
                        <CustomColorPicker
                          currentColor={buttonStyle.color || '#FFFFFF'}
                          onChange={(color) => handleButtonStyleChange('color', color)}
                          pickerType="productButtonText"
                          emailColors={getEmailBackgroundColors()}
                          brandColors={brandColors}
                          showAdvancedPicker={showAdvancedPicker}
                          setShowAdvancedPicker={setShowAdvancedPicker}
                          setAdvancedPickerPosition={setAdvancedPickerPosition}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Sliders for padding & radius */}
              <div className="space-y-4 mt-6 pt-4 border-t border-slate-50">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-xs font-semibold text-slate-700">Button Size</label>
                    <span className="text-[10px] font-bold text-slate-400">{buttonStyle.padding || '12px 32px'}</span>
                  </div>
                  <input
                    type="range"
                    min="4"
                    max="48"
                    value={parseInt(buttonStyle.padding?.split(' ')[0] || '12')}
                    onChange={(e) => handleButtonStyleChange('padding', `${e.target.value}px ${Math.round(parseInt(e.target.value) * 2.18)}px`)}
                    className="v2-spacing-slider w-full"
                    style={{ background: `linear-gradient(90deg, #64748b ${(((parseInt(buttonStyle.padding?.split(' ')[0] || '12') - 4) / 44) * 100)}%, #f1f5f9 ${(((parseInt(buttonStyle.padding?.split(' ')[0] || '12') - 4) / 44) * 100)}%)` }}
                  />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-xs font-semibold text-slate-700">Corner Radius</label>
                    <span className="text-[10px] font-bold text-slate-400">{parseInt(buttonStyle.borderRadius || '6')}px</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    value={parseInt((buttonStyle.borderRadius || '6px').toString().replace('px', '')) || 6}
                    onChange={(e) => handleButtonStyleChange('borderRadius', `${e.target.value}px`)}
                    className="v2-spacing-slider w-full"
                    style={{ background: `linear-gradient(90deg, #64748b ${((parseInt((buttonStyle.borderRadius || '6px').toString().replace('px', '')) || 6) / 50) * 100}%, #f1f5f9 ${((parseInt((buttonStyle.borderRadius || '6px').toString().replace('px', '')) || 6) / 50) * 100}%)` }}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'product-rec': {
        const products: Array<{
          name?: string;
          price?: string;
          oldPrice?: string;
          image?: string;
          url?: string;
          buttonText?: string;
        }> = Array.isArray(block.content.products) && block.content.products.length > 0
            ? block.content.products
            : [{}];

        const productsPerRow = block.content.productsPerRow || 3;

        const handleProductChange = (index: number, field: string, value: string) => {
          const next = [...products];
          if (!next[index]) next[index] = {};
          (next[index] as any)[field] = value;
          onUpdate({
            content: {
              ...block.content,
              products: next,
            },
          });
        };

        const addProduct = () => {
          const next = [...products, {
            name: 'New Product',
            price: '$49.99',
            oldPrice: '',
            image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=400&q=80',
            url: '#',
            buttonText: 'SHOP NOW',
          }];
          onUpdate({
            content: {
              ...block.content,
              products: next,
            },
          });
        };

        const removeProduct = (index: number) => {
          if (products.length <= 1) return;
          const next = products.filter((_, i) => i !== index);
          onUpdate({
            content: {
              ...block.content,
              products: next,
            },
          });
        };

        return (
          <>
            <CollapsibleSection title="Settings" defaultOpen={false}>
              <div className="space-y-3 pt-2">
                <div className="flex flex-col gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-700">Products Per Row</span>
                    <div className="flex items-center gap-1.5 p-1 bg-white border border-slate-200 rounded-lg">
                      {[1, 2, 3].map((num) => (
                        <button
                          key={num}
                          type="button"
                          onClick={() => onUpdate({
                            content: {
                              ...block.content,
                              productsPerRow: num
                            }
                          })}
                          className={`w-8 h-8 flex items-center justify-center rounded-md text-xs font-bold transition-all ${
                            productsPerRow === num
                              ? 'bg-slate-900 text-white shadow-sm'
                              : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between border-t border-slate-100 pt-2 mt-1">
                    <span className="text-xs font-semibold text-slate-700">Rounded Corners</span>
                    <button
                      type="button"
                      onClick={() => onUpdate({
                        content: {
                          ...block.content,
                          showRoundedCorners: !block.content.showRoundedCorners
                        }
                      })}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        block.content.showRoundedCorners ? 'bg-slate-900' : 'bg-slate-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          block.content.showRoundedCorners ? 'translate-x-4' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </CollapsibleSection>


            <CollapsibleSection title="Typography" defaultOpen={false}>
              <div className="space-y-8">
                <div>
                  <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Product Name</label>
                  {renderProductTypographyContent()}
                </div>
                <div className="pt-8 border-t border-slate-100">
                  <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Product Price</label>
                  {renderProductPriceTypographyContent()}
                </div>
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Product List" defaultOpen={true}>
              <div className="space-y-6">
                <div className="flex items-center justify-between pb-2">
                  <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Manage Items</span>
                  <button
                    type="button"
                    onClick={addProduct}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors shadow-sm rounded-lg"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add Product
                  </button>
                </div>
                
                <div className="space-y-4">
                  {products.map((product, index) => (
                    <div key={index} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 shadow-[0_1px_2px_rgba(0,0,0,0.02)] space-y-4 relative group">
                      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                        <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
                          Product {index + 1}
                        </span>
                        {products.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeProduct(index)}
                            className="p-1.5 text-rose-500 hover:bg-rose-50 hover:text-rose-600 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            title="Remove product"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      <div className="space-y-4">
                        <div className="space-y-4">
                          <div>
                            <p className="text-[11px] font-bold text-slate-900 uppercase tracking-tight">Image source</p>
                            <div className="flex items-center gap-1.5">
                              <p className="text-[10px] text-slate-500 line-clamp-1">Upload a file or paste a direct link</p>
                              <span className="shrink-0 px-1.5 py-0.5 bg-slate-100 text-[#6366f1] text-[9px] font-bold rounded uppercase tracking-tight">600 x 600px Recommended</span>
                            </div>
                          </div>

                          <div className="space-y-2 relative" ref={openProductImageIndex === index ? productImageDropRef : null}>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Add image from...</label>
                            <div className="relative">
                              <button
                                onClick={() => setOpenProductImageIndex(openProductImageIndex === index ? null : index)}
                                className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50/30 transition-all group shadow-sm"
                              >
                                <div className="flex items-center gap-2">
                                  <PhotoIcon className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-500" />
                                  <span>{product.image ? 'Change Image Assets' : 'Select Image Assets'}</span>
                                </div>
                                <ChevronDownIcon className={`w-3.5 h-3.5 text-slate-400 transition-transform ${openProductImageIndex === index ? 'rotate-180' : ''}`} />
                              </button>

                              {openProductImageIndex === index && (
                                <div className="absolute top-full left-0 right-0 z-[60] mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                                  <button
                                    onClick={() => {
                                      if (isUploadingImage) return;
                                      imageTargetFieldRef.current = `products.${index}.image`;
                                      fileInputRef.current?.click();
                                      setOpenProductImageIndex(null);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                  >
                                    <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                                    <span className="font-medium">Upload from computer</span>
                                  </button>
                                  <button
                                    onClick={() => {
                                      imageTargetFieldRef.current = `products.${index}.image`;
                                      setIsStockImageModalOpen(true);
                                      setOpenProductImageIndex(null);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                  >
                                    <SparklesIcon className="w-4 h-4 text-slate-400" />
                                    <span className="font-medium">Stock image</span>
                                  </button>
                                  <button
                                    onClick={() => {
                                      imageTargetFieldRef.current = `products.${index}.image`;
                                      setIsContentManagerOpen(true);
                                      setContentManagerTab('image');
                                      setOpenProductImageIndex(null);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                  >
                                    <QueueListIcon className="w-4 h-4 text-slate-400" />
                                    <span className="font-medium">Library</span>
                                  </button>
                                  <ImageSourceFromCanvaRow onClose={() => setIsImageSourceDropOpen(false)} />
                                </div>
                              )}
                            </div>
                          </div>

                          {product.image && (
                            <div className="flex items-start gap-3 rounded-lg border border-slate-200 p-2.5 bg-slate-50/50">
                                <div className="w-10 h-10 rounded-md overflow-hidden border border-slate-100 bg-white flex items-center justify-center shrink-0 shadow-sm">
                                  <img
                                    src={product.image}
                                    alt={product.name || 'Selected image'}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="flex-1 min-w-0 flex items-center justify-between">
                                  <div className="truncate">
                                    <p className="text-[10px] font-semibold text-slate-900 truncate">
                                      {deriveFileName(product.image) || 'Selected image'}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => handleProductChange(index, 'image', '')}
                                    className="text-[10px] font-bold text-rose-600 hover:text-rose-700 transition-colors ml-2"
                                  >
                                    Remove
                                  </button>
                                </div>
                            </div>
                          )}

                          <div>
                            <input
                              type="url"
                              value={product.image || ''}
                              onChange={(e) => handleProductChange(index, 'image', e.target.value)}
                              placeholder="Paste a direct link"
                              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                            />
                          </div>
                        </div>

                        <div className="space-y-3 pt-3 border-t border-slate-100">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Product Name</label>
                            <input
                              type="text"
                              value={product.name || ''}
                              onChange={(e) => handleProductChange(index, 'name', e.target.value)}
                              placeholder="Product name"
                              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Price</label>
                              <input
                                type="text"
                                value={product.price || ''}
                                onChange={(e) => handleProductChange(index, 'price', e.target.value)}
                                placeholder="$99.99"
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Old Price</label>
                              <input
                                type="text"
                                value={product.oldPrice || ''}
                                onChange={(e) => handleProductChange(index, 'oldPrice', e.target.value)}
                                placeholder="$129.99"
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Button Text</label>
                              <input
                                type="text"
                                value={product.buttonText || 'SHOP NOW'}
                                onChange={(e) => handleProductChange(index, 'buttonText', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">URL</label>
                              <input
                                type="text"
                                value={product.url || ''}
                                onChange={(e) => handleProductChange(index, 'url', e.target.value)}
                                placeholder="https://..."
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CollapsibleSection>
          </>
        );
      }

      case 'music-launch': {
        const layout = block.content.layout || 'dark';
        const tracks: { title?: string; duration?: string; featured?: string }[] =
          Array.isArray(block.content.tracks) && block.content.tracks.length > 0
            ? block.content.tracks
            : [{}, {}, {}];

        const handleTrackChange = (index: number, field: keyof (typeof tracks)[number], value: string) => {
          const next = [...tracks];
          if (!next[index]) next[index] = {};
          (next[index] as any)[field] = value;
          onUpdate({
            content: {
              ...block.content,
              tracks: next,
            },
          });
        };

        const handlePlatformsChange = (field: string, value: string) => {
          onUpdate({
            content: {
              ...block.content,
              platforms: {
                ...(block.content.platforms || {}),
                [field]: value,
              },
            },
          });
        };

        const handleContentBoolChange = (field: string, value: boolean) => {
          onUpdate({
            content: {
              ...block.content,
              [field]: value,
            },
          });
        };

        return (
          <div className="space-y-6">
            {/* View / Layout Settings */}
            <div className="space-y-4">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Appearance</label>
              
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Theme</label>
                <div className="grid grid-cols-3 gap-2 p-1 bg-slate-100/50 rounded-xl">
                  {[
                    { id: 'dark', label: 'Dark' },
                    { id: 'light', label: 'Spotlight' },
                    { id: 'tour', label: 'Tour' },
                  ].map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleContentChange('layout', option.id)}
                      className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${
                        layout === option.id
                          ? 'bg-white border-slate-200 text-slate-800 shadow-sm'
                          : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

               <div className="grid grid-cols-2 gap-4 mt-4">
                {/* Background Color */}
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Background Color</label>
                  <div className="relative">
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'platformBg' ? null : 'platformBg')}
                      className="w-full flex items-center justify-between px-3 py-2 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors bg-white shadow-sm"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-5 h-5 rounded shadow-sm border border-slate-200/50"
                          style={{ backgroundColor: block.styles.backgroundColor || (layout === 'dark' ? '#020617' : layout === 'tour' ? '#F8FAFC' : '#FFFFFF') }}
                        />
                        <span className="text-xs font-mono text-slate-600 uppercase">
                          {block.styles.backgroundColor || (layout === 'dark' ? '#020617' : layout === 'tour' ? '#F8FAFC' : '#FFFFFF')}
                        </span>
                      </div>
                      <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform ${showColorPicker === 'platformBg' ? 'rotate-180' : ''}`} />
                    </button>
                    {showColorPicker === 'platformBg' && (
                      <div className="absolute top-full left-0 mt-2 z-[100]">
                        <CustomColorPicker
                          currentColor={block.styles.backgroundColor || (layout === 'dark' ? '#020617' : layout === 'tour' ? '#F8FAFC' : '#FFFFFF')}
                          onChange={(color) => handleStyleChange('backgroundColor', color)}
                          pickerType="platformBg"
                          emailColors={getEmailBackgroundColors()}
                          brandColors={brandColors}
                          showAdvancedPicker={showAdvancedPicker}
                          setShowAdvancedPicker={setShowAdvancedPicker}
                          setAdvancedPickerPosition={setAdvancedPickerPosition}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Text Color */}
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Text Color</label>
                  <div className="relative">
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()} onClick={() => setShowColorPicker(showColorPicker === 'platformText' ? null : 'platformText')}
                      className="w-full flex items-center justify-between px-3 py-2 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors bg-white shadow-sm"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-5 h-5 rounded shadow-sm border border-slate-200/50"
                          style={{ backgroundColor: block.styles.color || (layout === 'dark' ? '#FFFFFF' : '#111827') }}
                        />
                        <span className="text-xs font-mono text-slate-600 uppercase">
                          {block.styles.color || (layout === 'dark' ? '#FFFFFF' : '#111827')}
                        </span>
                      </div>
                      <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform ${showColorPicker === 'platformText' ? 'rotate-180' : ''}`} />
                    </button>
                    {showColorPicker === 'platformText' && (
                      <div className="absolute top-full right-0 mt-2 z-[100]">
                        <CustomColorPicker
                          currentColor={block.styles.color || (layout === 'dark' ? '#FFFFFF' : '#111827')}
                          onChange={(color) => handleStyleChange('color', color)}
                          pickerType="platformText"
                          emailColors={getEmailBackgroundColors()}
                          brandColors={brandColors}
                          showAdvancedPicker={showAdvancedPicker}
                          setShowAdvancedPicker={setShowAdvancedPicker}
                          setAdvancedPickerPosition={setAdvancedPickerPosition}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Content Segment */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Release Metadata</label>
              
               <div className="space-y-2 relative" ref={imageSourceDropRef}>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Cover Image</label>
                <div className="relative">
                  <button
                    onClick={() => setIsImageSourceDropOpen(!isImageSourceDropOpen)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                  >
                    <div className="flex items-center gap-2">
                      <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                      <span>Upload cover artwork</span>
                    </div>
                    <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isImageSourceDropOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isImageSourceDropOpen && (
                    <div className="absolute top-full left-0 right-0 mt-2 py-1 bg-white border border-slate-100 rounded-xl shadow-xl z-[100] animate-in fade-in zoom-in-95 duration-100">
                      <button
                         onClick={() => {
                          imageTargetFieldRef.current = 'coverImage';
                          setIsImageSourceDropOpen(false);
                          if (isUploadingImage) return;
                          fileInputRef.current?.click();
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Upload</span>
                      </button>
                      <button
                        onClick={() => {
                          imageTargetFieldRef.current = 'coverImage';
                          setIsImageSourceDropOpen(false);
                          setContentManagerTab('image');
                          setIsContentManagerOpen(true);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                         <QueueListIcon className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">Library</span>
                      </button>
                      <ImageSourceFromCanvaRow onClose={() => setIsImageSourceDropOpen(false)} />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Release Title</label>
                <input
                  type="text"
                  value={block.content.title || ''}
                  onChange={(e) => handleContentChange('title', e.target.value)}
                  placeholder="New single out now"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>

               <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Artist Name</label>
                <input
                  type="text"
                  value={block.content.artist || ''}
                  onChange={(e) => handleContentChange('artist', e.target.value)}
                  placeholder="Artist name"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Release Type</label>
                  <div className="relative">
                    <select
                      value={block.content.releaseType || 'Single'}
                      onChange={(e) => handleContentChange('releaseType', e.target.value)}
                      className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm appearance-none pr-10"
                    >
                      <option value="Single">Single</option>
                      <option value="EP">EP</option>
                      <option value="Album">Album</option>
                      <option value="Mixtape">Mixtape</option>
                    </select>
                    <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Release Date</label>
                  <input
                    type="text"
                    value={block.content.releaseDate || ''}
                    onChange={(e) => handleContentChange('releaseDate', e.target.value)}
                    placeholder="Aug 12, 2024"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                  />
                </div>
              </div>

              <div className="pt-2">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-slate-700">Description</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={block.content.showDescription !== false}
                      onChange={(e) => handleContentBoolChange('showDescription', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-7 h-4 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-slate-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-slate-800"></div>
                  </label>
                </div>
                 <textarea
                  value={block.content.description || ''}
                  onChange={(e) => handleContentChange('description', e.target.value)}
                  placeholder="Short paragraph introducing the release."
                  rows={3}
                  className={`w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all shadow-sm ${block.content.showDescription === false ? 'bg-slate-50 text-slate-400 opacity-50 cursor-not-allowed' : 'bg-white'}`}
                  disabled={block.content.showDescription === false}
                />
              </div>
            </div>

            {/* CTAs */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
               <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Call To Action</label>
               <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Primary Button</label>
                    <input
                      type="text"
                      value={block.content.primaryButtonText || ''}
                      onChange={(e) => handleContentChange('primaryButtonText', e.target.value)}
                      placeholder="Listen now"
                      className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">URL</label>
                    <input
                      type="url"
                      value={block.content.primaryButtonUrl || ''}
                      onChange={(e) => handleContentChange('primaryButtonUrl', e.target.value)}
                      placeholder="https://open.spotify.com/..."
                      className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                    />
                  </div>
               </div>

                <div className="p-3 border border-slate-200 rounded-xl bg-slate-50/50 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-800">Secondary Action</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!block.content.showSecondaryButton}
                        onChange={(e) => handleContentBoolChange('showSecondaryButton', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-7 h-4 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-slate-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-slate-800"></div>
                    </label>
                  </div>
                  {block.content.showSecondaryButton && (
                    <div className="grid grid-cols-2 gap-4 pt-1">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Text</label>
                        <input
                          type="text"
                          value={block.content.secondaryButtonText || ''}
                          onChange={(e) => handleContentChange('secondaryButtonText', e.target.value)}
                          placeholder="View tour dates"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">URL</label>
                        <input
                          type="url"
                          value={block.content.secondaryButtonUrl || ''}
                          onChange={(e) => handleContentChange('secondaryButtonUrl', e.target.value)}
                          placeholder="https://example.com/tour"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
            </div>

            {/* Tracklist array */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
               <div className="flex items-center justify-between">
                <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Tracklist</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={block.content.showTracklist !== false}
                    onChange={(e) => handleContentBoolChange('showTracklist', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-7 h-4 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-slate-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-slate-800"></div>
                </label>
              </div>

               {block.content.showTracklist !== false && (
                <div className="p-1 space-y-2">
                  {tracks.map((track, index) => (
                    <div key={index} className="flex gap-2 items-center bg-white p-2 border border-slate-200 rounded-xl shadow-sm">
                      <span className="text-[10px] font-bold text-slate-400 w-4 text-center">{index + 1}</span>
                      <div className="flex-1 grid grid-cols-6 gap-2">
                         <div className="col-span-3 lg:col-span-3">
                           <input
                             type="text"
                             value={track.title || ''}
                             onChange={(e) => handleTrackChange(index, 'title', e.target.value)}
                             placeholder={`Track Title`}
                             className="w-full px-2 py-1.5 border border-transparent hover:border-slate-200 rounded focus:border-slate-300 focus:outline-none text-xs transition-colors bg-transparent hover:bg-slate-50 focus:bg-white"
                           />
                         </div>
                         <div className="col-span-2 text-right">
                           <input
                             type="text"
                             value={track.featured || ''}
                             onChange={(e) => handleTrackChange(index, 'featured', e.target.value)}
                             placeholder="Feat."
                             className="w-full px-2 py-1.5 border border-transparent hover:border-slate-200 rounded focus:border-slate-300 focus:outline-none text-xs transition-colors bg-transparent hover:bg-slate-50 focus:bg-white text-right"
                           />
                         </div>
                         <div className="col-span-1 text-right">
                           <input
                             type="text"
                             value={track.duration || ''}
                             onChange={(e) => handleTrackChange(index, 'duration', e.target.value)}
                             placeholder="3:21"
                             className="w-full px-2 py-1.5 border border-transparent hover:border-slate-200 rounded focus:border-slate-300 focus:outline-none text-xs font-mono transition-colors bg-transparent hover:bg-slate-50 focus:bg-white text-right"
                           />
                         </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                        onUpdate({
                            content: {
                                ...block.content,
                                tracks: [...tracks, {}]
                            }
                        })
                    }}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50 border border-slate-200 border-dashed hover:border-slate-300 hover:text-slate-700 transition-colors rounded-xl mt-2"
                  >
                    <PlusIcon className="w-3.5 h-3.5" />
                    Add Track
                  </button>
                </div>
              )}
            </div>

            {/* Platforms segment */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Platform Links</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={block.content.showPlatforms !== false}
                    onChange={(e) => handleContentBoolChange('showPlatforms', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-7 h-4 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-slate-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-slate-800"></div>
                </label>
              </div>

               {block.content.showPlatforms !== false && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Spotify</label>
                    <input
                      type="url"
                      value={block.content.platforms?.spotifyUrl || ''}
                      onChange={(e) => handlePlatformsChange('spotifyUrl', e.target.value)}
                      placeholder="https://open.spotify.com/..."
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white placeholder:text-slate-400 shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Apple Music</label>
                    <input
                      type="url"
                      value={block.content.platforms?.appleMusicUrl || ''}
                      onChange={(e) => handlePlatformsChange('appleMusicUrl', e.target.value)}
                      placeholder="https://music.apple.com/..."
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white placeholder:text-slate-400 shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">YouTube</label>
                    <input
                      type="url"
                      value={block.content.platforms?.youtubeUrl || ''}
                      onChange={(e) => handlePlatformsChange('youtubeUrl', e.target.value)}
                      placeholder="https://youtube.com/..."
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white placeholder:text-slate-400 shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">SoundCloud</label>
                    <input
                      type="url"
                      value={block.content.platforms?.soundcloudUrl || ''}
                      onChange={(e) => handlePlatformsChange('soundcloudUrl', e.target.value)}
                      placeholder="https://soundcloud.com/..."
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white placeholder:text-slate-400 shadow-sm"
                    />
                  </div>
                   <div className="col-span-2">
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Bandcamp</label>
                    <input
                      type="url"
                      value={block.content.platforms?.bandcampUrl || ''}
                      onChange={(e) => handlePlatformsChange('bandcampUrl', e.target.value)}
                      placeholder="https://bandcamp.com/..."
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white placeholder:text-slate-400 shadow-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }

      case 'newsletter': {
        const layout = block.content.layout || 'classic';
        const sections: { title?: string; summary?: string; enabled?: boolean }[] =
          Array.isArray(block.content.sections) && block.content.sections.length > 0
            ? block.content.sections
            : [
              { title: 'Story 1', summary: '', enabled: true },
              { title: 'Story 2', summary: '', enabled: true },
              { title: 'Story 3', summary: '', enabled: true },
            ];

        const handleSectionChange = (
          index: number,
          field: keyof (typeof sections)[number],
          value: string | boolean
        ) => {
          const next = [...sections];
          if (!next[index]) next[index] = { enabled: true };
          (next[index] as any)[field] = value;
          onUpdate({
            content: {
              ...block.content,
              sections: next,
            },
          });
        };

        const handleContentBoolChange = (field: string, value: boolean) => {
          onUpdate({
            content: {
              ...block.content,
              [field]: value,
            },
          });
        };

        return (
          <div className="space-y-6">
            {/* View / Layout Settings */}
            <div className="space-y-4">
              <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Appearance</label>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Theme</label>
                <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100/50 rounded-xl">
                  {[
                    { id: 'classic', label: 'Classic' },
                    { id: 'single-column', label: 'Single column' },
                  ].map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleContentChange('layout', option.id)}
                      className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${
                        layout === option.id
                          ? 'bg-white border-slate-200 text-slate-800 shadow-sm'
                          : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Content Segment */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
               <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Header Content</label>

              <div className="space-y-2 relative" ref={imageSourceDropRef}>
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Intro Image</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={block.content.showImage !== false}
                        onChange={(e) => handleContentBoolChange('showImage', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-7 h-4 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-slate-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-slate-800"></div>
                    </label>
                  </div>
                  {block.content.showImage !== false && (
                    <div className="relative">
                      <button
                        onClick={() => setIsImageSourceDropOpen(!isImageSourceDropOpen)}
                        className="w-full flex items-center justify-between px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                      >
                        <div className="flex items-center gap-2">
                          <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                          <span>Upload hero image</span>
                        </div>
                        <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isImageSourceDropOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {isImageSourceDropOpen && (
                        <div className="absolute top-full left-0 right-0 mt-2 py-1 bg-white border border-slate-100 rounded-xl shadow-xl z-[100] animate-in fade-in zoom-in-95 duration-100">
                          <button
                             onClick={() => {
                              imageTargetFieldRef.current = 'image';
                              setIsImageSourceDropOpen(false);
                              if (isUploadingImage) return;
                              fileInputRef.current?.click();
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">Upload</span>
                          </button>
                          <button
                            onClick={() => {
                              imageTargetFieldRef.current = 'image';
                              setIsImageSourceDropOpen(false);
                              setContentManagerTab('image');
                              setIsContentManagerOpen(true);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                             <QueueListIcon className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">Library</span>
                          </button>
                          <ImageSourceFromCanvaRow onClose={() => setIsImageSourceDropOpen(false)} />
                        </div>
                      )}
                    </div>
                  )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Newsletter Title</label>
                <input
                  type="text"
                  value={block.content.title || ''}
                  onChange={(e) => handleContentChange('title', e.target.value)}
                  placeholder="This week at your brand"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>

               <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Intro Text</label>
                <textarea
                  value={block.content.intro || ''}
                  onChange={(e) => handleContentChange('intro', e.target.value)}
                  placeholder="Short paragraph welcoming subscribers and setting expectations."
                  rows={3}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                />
              </div>
            </div>

            {/* Call To Action */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between">
                    <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Intro Button</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!block.content.showButton}
                        onChange={(e) => handleContentBoolChange('showButton', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-7 h-4 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-slate-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-slate-800"></div>
                    </label>
                </div>
              {block.content.showButton && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">Button Text</label>
                      <input
                        type="text"
                        value={block.content.buttonText || ''}
                        onChange={(e) => handleContentChange('buttonText', e.target.value)}
                        placeholder="Read the full update"
                         className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">Button URL</label>
                      <input
                        type="url"
                        value={block.content.buttonUrl || ''}
                        onChange={(e) => handleContentChange('buttonUrl', e.target.value)}
                        placeholder="https://example.com/blog"
                        className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-white shadow-sm"
                      />
                    </div>
                  </div>
              )}
            </div>

            {/* Sections */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
               <div className="flex items-center justify-between">
                <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Sections</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={block.content.showSections !== false}
                    onChange={(e) => handleContentBoolChange('showSections', e.target.checked)}
                     className="sr-only peer"
                  />
                  <div className="w-7 h-4 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-slate-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-slate-800"></div>
                </label>
              </div>

              {block.content.showSections !== false && (
                <div className="space-y-3">
                  {sections.map((section, index) => (
                    <div
                      key={index}
                      className="p-3 bg-white border border-slate-200 rounded-xl shadow-sm relative overflow-hidden group"
                    >
                      <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-100">
                         <div className="flex items-center gap-2">
                            <DocumentTextIcon className="w-4 h-4 text-slate-400" />
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                              Section {index + 1}
                            </span>
                         </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                             checked={section.enabled !== false}
                            onChange={(e) => handleSectionChange(index, 'enabled', e.target.checked)}
                             className="sr-only peer"
                          />
                          <div className="w-6 h-3 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-slate-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-2 after:w-2 after:transition-all peer-checked:bg-slate-800"></div>
                        </label>
                      </div>

                       <div className="space-y-3">
                         <div>
                           <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Title</label>
                            <input
                              type="text"
                              value={section.title || ''}
                              onChange={(e) => handleSectionChange(index, 'title', e.target.value)}
                              placeholder={`Story ${index + 1} title`}
                              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-slate-50 hover:bg-white focus:bg-white"
                            />
                         </div>
                         <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Summary</label>
                            <textarea
                              value={section.summary || ''}
                              onChange={(e) => handleSectionChange(index, 'summary', e.target.value)}
                              placeholder="2–3 lines of summary or description."
                              rows={2}
                              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 transition-all bg-slate-50 hover:bg-white focus:bg-white"
                            />
                         </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      }

      case 'event-announcement': {
        const layout = block.content.layout || 'standard';
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                Layout Style
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'standard', label: 'Standard' },
                  { id: 'webinar', label: 'Webinar' },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleContentChange('layout', option.id)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium border ${layout === option.id
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Hero Image</label>
              <div className="space-y-2">
                <input
                  type="url"
                  value={block.content.heroImage || ''}
                  onChange={(e) => handleContentChange('heroImage', e.target.value)}
                  placeholder="Event image URL"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                />
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      imageTargetFieldRef.current = 'heroImage';
                      setContentManagerTab('image');
                      setIsContentManagerOpen(true);
                    }}
                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    Upload image
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      imageTargetFieldRef.current = 'heroImage';
                      setIsStockImageModalOpen(true);
                    }}
                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    Browse stock images
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
              <input
                type="text"
                value={block.content.title || ''}
                onChange={(e) => handleContentChange('title', e.target.value)}
                placeholder="Event title"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
              <textarea
                value={block.content.description || ''}
                onChange={(e) => handleContentChange('description', e.target.value)}
                placeholder="Event description"
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Button Text</label>
                <input
                  type="text"
                  value={block.content.buttonText || ''}
                  onChange={(e) => handleContentChange('buttonText', e.target.value)}
                  placeholder="Register Now"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Button URL</label>
                <input
                  type="url"
                  value={block.content.buttonUrl || ''}
                  onChange={(e) => handleContentChange('buttonUrl', e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                />
              </div>
            </div>
            <div className="pt-3 border-t border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-slate-700">Event Details</label>
                <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    checked={block.content.showDetails !== false}
                    onChange={(e) => handleContentChange('showDetails', e.target.checked)}
                  />
                  Show details
                </label>
              </div>
              {block.content.showDetails !== false && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                    <input
                      type="text"
                      value={block.content.eventDate || ''}
                      onChange={(e) => handleContentChange('eventDate', e.target.value)}
                      placeholder="Friday, October 15, 2024"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Time</label>
                    <input
                      type="text"
                      value={block.content.eventTime || ''}
                      onChange={(e) => handleContentChange('eventTime', e.target.value)}
                      placeholder="6:00 PM - 9:00 PM"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                    <input
                      type="text"
                      value={block.content.eventLocation || ''}
                      onChange={(e) => handleContentChange('eventLocation', e.target.value)}
                      placeholder="Grand Ballroom, Downtown"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Additional Info</label>
                    <textarea
                      value={block.content.additionalInfo || ''}
                      onChange={(e) => handleContentChange('additionalInfo', e.target.value)}
                      placeholder="Additional event information"
                      rows={2}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
            {layout === 'webinar' && (
              <div className="pt-3 border-t border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-slate-700">Speaker Info</label>
                  <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={!!block.content.showSpeaker}
                      onChange={(e) => handleContentChange('showSpeaker', e.target.checked)}
                    />
                    Show speaker
                  </label>
                </div>
                {block.content.showSpeaker && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Speaker Image</label>
                      <div className="space-y-2">
                        <input
                          type="url"
                          value={block.content.speakerImage || ''}
                          onChange={(e) => handleContentChange('speakerImage', e.target.value)}
                          placeholder="Speaker photo URL"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                        />
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              imageTargetFieldRef.current = 'speakerImage';
                              setContentManagerTab('image');
                              setIsContentManagerOpen(true);
                            }}
                            className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                          >
                            Upload image
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              imageTargetFieldRef.current = 'speakerImage';
                              setIsStockImageModalOpen(true);
                            }}
                            className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                          >
                            Browse stock images
                          </button>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Speaker Name</label>
                      <input
                        type="text"
                        value={block.content.speakerName || ''}
                        onChange={(e) => handleContentChange('speakerName', e.target.value)}
                        placeholder="Dr. Sarah Johnson"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Speaker Role</label>
                      <input
                        type="text"
                        value={block.content.speakerRole || ''}
                        onChange={(e) => handleContentChange('speakerRole', e.target.value)}
                        placeholder="Industry Expert & Author"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Speaker Bio</label>
                      <textarea
                        value={block.content.speakerBio || ''}
                        onChange={(e) => handleContentChange('speakerBio', e.target.value)}
                        placeholder="Speaker biography"
                        rows={3}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      }

      case 'testimonial': {
        const layout = block.content.layout || 'single';
        const testimonials: { quote?: string; author?: string; role?: string; image?: string; rating?: number; enabled?: boolean }[] =
          Array.isArray(block.content.testimonials) && block.content.testimonials.length > 0
            ? block.content.testimonials
            : [{}, {}, {}];

        const handleTestimonialChange = (index: number, field: string, value: any) => {
          const next = [...testimonials];
          if (!next[index]) next[index] = {};
          (next[index] as any)[field] = value;
          onUpdate({
            content: {
              ...block.content,
              testimonials: next,
            },
          });
        };

        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Section Title</label>
              <input
                type="text"
                value={block.content.sectionTitle || ''}
                onChange={(e) => handleContentChange('sectionTitle', e.target.value)}
                placeholder="What Our Customers Say"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                Layout Style
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'single', label: 'Single' },
                  { id: 'grid', label: 'Grid' },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleContentChange('layout', option.id)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium border ${layout === option.id
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="pt-3 border-t border-slate-200">
              <h4 className="text-sm font-semibold text-slate-800 mb-3">Testimonials</h4>
              <div className="space-y-4">
                {testimonials.slice(0, layout === 'single' ? 1 : 3).map((testimonial, index) => (
                  <div key={index} className="space-y-3 p-3 border border-slate-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-600">Testimonial {index + 1}</span>
                      <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          checked={testimonial.enabled !== false}
                          onChange={(e) => handleTestimonialChange(index, 'enabled', e.target.checked)}
                        />
                        Show
                      </label>
                    </div>
                    {testimonial.enabled !== false && (
                      <>
                        {layout === 'single' && (
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Photo</label>
                            <div className="space-y-2">
                              <input
                                type="url"
                                value={testimonial.image || ''}
                                onChange={(e) => handleTestimonialChange(index, 'image', e.target.value)}
                                placeholder="Photo URL"
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                              />
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    imageTargetFieldRef.current = `testimonial-${index}-image`;
                                    setContentManagerTab('image');
                                    setIsContentManagerOpen(true);
                                  }}
                                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                                >
                                  Upload
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    imageTargetFieldRef.current = `testimonial-${index}-image`;
                                    setIsStockImageModalOpen(true);
                                  }}
                                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                                >
                                  Stock
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Quote</label>
                          <textarea
                            value={testimonial.quote || ''}
                            onChange={(e) => handleTestimonialChange(index, 'quote', e.target.value)}
                            placeholder="Customer testimonial"
                            rows={2}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Author</label>
                            <input
                              type="text"
                              value={testimonial.author || ''}
                              onChange={(e) => handleTestimonialChange(index, 'author', e.target.value)}
                              placeholder="Jane Smith"
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                            <input
                              type="text"
                              value={testimonial.role || ''}
                              onChange={(e) => handleTestimonialChange(index, 'role', e.target.value)}
                              placeholder="CEO"
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Rating</label>
                          <select
                            value={testimonial.rating || 5}
                            onChange={(e) => handleTestimonialChange(index, 'rating', parseInt(e.target.value, 10))}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm bg-white"
                          >
                            {[1, 2, 3, 4, 5].map((r) => (
                              <option key={r} value={r}>
                                {r} Star{r > 1 ? 's' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }

      case 'pricing-table': {
        const plans: {
          name?: string;
          price?: string;
          period?: string;
          description?: string;
          features?: string[];
          buttonText?: string;
          buttonUrl?: string;
          highlighted?: boolean;
          enabled?: boolean;
        }[] =
          Array.isArray(block.content.plans) && block.content.plans.length > 0
            ? block.content.plans
            : [{}, {}, {}];

        const handlePlanChange = (index: number, field: string, value: any) => {
          const next = [...plans];
          if (!next[index]) next[index] = {};
          (next[index] as any)[field] = value;
          onUpdate({
            content: {
              ...block.content,
              plans: next,
            },
          });
        };

        const handleFeatureChange = (planIndex: number, featureIndex: number, value: string) => {
          const next = [...plans];
          if (!next[planIndex]) next[planIndex] = {};
          if (!Array.isArray(next[planIndex].features)) next[planIndex].features = [];
          const features = [...(next[planIndex].features || [])];
          features[featureIndex] = value;
          next[planIndex].features = features;
          onUpdate({
            content: {
              ...block.content,
              plans: next,
            },
          });
        };

        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Section Title</label>
              <input
                type="text"
                value={block.content.sectionTitle || ''}
                onChange={(e) => handleContentChange('sectionTitle', e.target.value)}
                placeholder="Choose Your Plan"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>
            <div className="pt-3 border-t border-slate-200">
              <h4 className="text-sm font-semibold text-slate-800 mb-3">Pricing Plans</h4>
              <div className="space-y-4">
                {plans.map((plan, index) => (
                  <div key={index} className="space-y-3 p-3 border border-slate-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-600">Plan {index + 1}</span>
                      <div className="flex items-center gap-3">
                        <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={plan.highlighted === true}
                            onChange={(e) => handlePlanChange(index, 'highlighted', e.target.checked)}
                          />
                          Highlighted
                        </label>
                        <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={plan.enabled !== false}
                            onChange={(e) => handlePlanChange(index, 'enabled', e.target.checked)}
                          />
                          Show
                        </label>
                      </div>
                    </div>
                    {plan.enabled !== false && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Plan Name</label>
                          <input
                            type="text"
                            value={plan.name || ''}
                            onChange={(e) => handlePlanChange(index, 'name', e.target.value)}
                            placeholder="Starter"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Price</label>
                            <input
                              type="text"
                              value={plan.price || ''}
                              onChange={(e) => handlePlanChange(index, 'price', e.target.value)}
                              placeholder="$29"
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Period</label>
                            <input
                              type="text"
                              value={plan.period || ''}
                              onChange={(e) => handlePlanChange(index, 'period', e.target.value)}
                              placeholder="month"
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                            <input
                              type="text"
                              value={plan.description || ''}
                              onChange={(e) => handlePlanChange(index, 'description', e.target.value)}
                              placeholder="Perfect for..."
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Features</label>
                          <div className="space-y-2">
                            {Array.from({ length: Math.max((plan.features?.length || 0), 3) }, (_, i) => (
                              <input
                                key={i}
                                type="text"
                                value={plan.features?.[i] || ''}
                                onChange={(e) => handleFeatureChange(index, i, e.target.value)}
                                placeholder={`Feature ${i + 1}`}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                              />
                            ))}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Button Text</label>
                            <input
                              type="text"
                              value={plan.buttonText || ''}
                              onChange={(e) => handlePlanChange(index, 'buttonText', e.target.value)}
                              placeholder="Get Started"
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Button URL</label>
                            <input
                              type="url"
                              value={plan.buttonUrl || ''}
                              onChange={(e) => handlePlanChange(index, 'buttonUrl', e.target.value)}
                              placeholder="https://example.com"
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }

      case 'features-grid': {
        const gridContent = normalizeFeaturesGridContent(block.content);
        const layoutVariant = resolveFeaturesGridVariant(gridContent);
        const usesPackIcons = usesPackIconLayout(layoutVariant);
        const features: {
          icon?: string;
          iconUrl?: string;
          title?: string;
          description?: string;
          enabled?: boolean;
        }[] =
          Array.isArray(gridContent.features) && gridContent.features.length > 0
            ? gridContent.features
            : getDefaultFeaturesForLayout(layoutVariant).features;

        const handleFeatureChange = (index: number, field: string, value: any) => {
          const next = [...features];
          if (!next[index]) next[index] = {};
          (next[index] as any)[field] = value;
          applyContentUpdate({ features: next });
        };

        const handleFeatureIconChange = (
          index: number,
          updates: { icon?: string; iconUrl?: string }
        ) => {
          const next = [...features];
          if (!next[index]) next[index] = {};
          next[index] = { ...next[index], ...updates };
          applyContentUpdate({ features: next });
        };

        const setFeatures = (next: typeof features) => {
          applyContentUpdate({ features: next });
        };

        const addFeature = () => {
          setFeatures([
            ...features,
            {
              icon: usesPackIcons ? 'mail' : '✨',
              title: 'New feature',
              description: 'Describe this feature.',
              enabled: true,
            },
          ]);
        };

        const removeFeature = (index: number) => {
          if (features.length <= 1) return;
          setFeatures(features.filter((_, i) => i !== index));
        };

        return (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                Layout style
              </label>
              <div className="grid grid-cols-1 gap-2">
                {FEATURES_GRID_LAYOUT_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      const nextVariant = option.id;
                      const current = resolveFeaturesGridVariant(block.content);
                      if (current === nextVariant) return;
                      const defaults = getDefaultFeaturesForLayout(nextVariant);
                      applyContentUpdate({
                        layoutVariant: nextVariant,
                        features: defaults.features,
                        sectionTitle: defaults.sectionTitle,
                      });
                    }}
                    className={`px-3 py-2.5 rounded-lg text-sm font-medium border text-left ${
                      layoutVariant === option.id
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Section Title</label>
              <input
                type="text"
                value={gridContent.sectionTitle || ''}
                onChange={(e) => handleContentChange('sectionTitle', e.target.value)}
                placeholder={getDefaultFeaturesForLayout(layoutVariant).sectionTitle}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>

            <div className="pt-3 border-t border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-slate-800">Features</h4>
                <button
                  type="button"
                  onClick={addFeature}
                  className="text-xs font-semibold text-slate-700 hover:text-slate-900 px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50"
                >
                  + Add feature
                </button>
              </div>
              <div className="space-y-3">
                {features.map((feature, index) => (
                  <div key={index} className="space-y-2 p-3 border border-slate-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-600">Feature {index + 1}</span>
                      <div className="flex items-center gap-3">
                        <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={feature.enabled !== false}
                            onChange={(e) => handleFeatureChange(index, 'enabled', e.target.checked)}
                            className="accent-slate-900"
                          />
                          Show
                        </label>
                        {features.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeFeature(index)}
                            className="text-xs text-red-500 hover:text-red-700 font-medium"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                    {feature.enabled !== false && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Icon</label>
                          <FeatureIconPicker
                            feature={feature}
                            onChange={(updates) => handleFeatureIconChange(index, updates)}
                            allowEmoji={!usesPackIcons}
                            allowClearIcon={layoutVariant === 'numbered'}
                            currentUserId={currentUser?.uid}
                            workspaceId={currentWorkspace?.id}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                          <input
                            type="text"
                            value={feature.title || ''}
                            onChange={(e) => handleFeatureChange(index, 'title', e.target.value)}
                            placeholder={
                              getDefaultFeaturesForLayout(layoutVariant).features[0]?.title ||
                              'Feature title'
                            }
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                          <textarea
                            value={feature.description || ''}
                            onChange={(e) => handleFeatureChange(index, 'description', e.target.value)}
                            placeholder="Feature description"
                            rows={2}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                          />
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }

      case 'promo-banner': {
        const layout = block.content.layout || 'with-countdown';
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                Layout Style
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'with-countdown', label: 'With Countdown' },
                  { id: 'simple', label: 'Simple' },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleContentChange('layout', option.id)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium border ${layout === option.id
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Hero Image</label>
              <div className="space-y-2">
                <input
                  type="url"
                  value={block.content.heroImage || ''}
                  onChange={(e) => handleContentChange('heroImage', e.target.value)}
                  placeholder="Promotional image URL"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                />
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      imageTargetFieldRef.current = 'heroImage';
                      setContentManagerTab('image');
                      setIsContentManagerOpen(true);
                    }}
                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    Upload image
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      imageTargetFieldRef.current = 'heroImage';
                      setIsStockImageModalOpen(true);
                    }}
                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    Browse stock images
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
              <input
                type="text"
                value={block.content.title || ''}
                onChange={(e) => handleContentChange('title', e.target.value)}
                placeholder="Limited Time Offer"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
              <textarea
                value={block.content.description || ''}
                onChange={(e) => handleContentChange('description', e.target.value)}
                placeholder="Promotional description"
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Button Text</label>
                <input
                  type="text"
                  value={block.content.buttonText || ''}
                  onChange={(e) => handleContentChange('buttonText', e.target.value)}
                  placeholder="Shop Now"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Button URL</label>
                <input
                  type="url"
                  value={block.content.buttonUrl || ''}
                  onChange={(e) => handleContentChange('buttonUrl', e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                />
              </div>
            </div>
            {layout === 'with-countdown' && (
              <div className="pt-3 border-t border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-slate-700">Countdown</label>
                  <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={!!block.content.showCountdown}
                      onChange={(e) => handleContentChange('showCountdown', e.target.checked)}
                    />
                    Show countdown
                  </label>
                </div>
                {block.content.showCountdown && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Countdown Title</label>
                      <input
                        type="text"
                        value={block.content.countdownTitle || ''}
                        onChange={(e) => handleContentChange('countdownTitle', e.target.value)}
                        placeholder="Offer Ends In"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Countdown Date</label>
                      <input
                        type="datetime-local"
                        value={block.content.countdownDate ? new Date(block.content.countdownDate).toISOString().slice(0, 16) : ''}
                        onChange={(e) => handleContentChange('countdownDate', new Date(e.target.value).toISOString())}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      }

      case 'blog-preview': {
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-slate-700">Image</label>
              <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={block.content.showImage !== false}
                  onChange={(e) => handleContentChange('showImage', e.target.checked)}
                />
                Show image
              </label>
            </div>
            {block.content.showImage !== false && (
              <div className="space-y-2">
                <input
                  type="url"
                  value={block.content.image || ''}
                  onChange={(e) => handleContentChange('image', e.target.value)}
                  placeholder="Blog post image URL"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                />
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      imageTargetFieldRef.current = 'image';
                      setContentManagerTab('image');
                      setIsContentManagerOpen(true);
                    }}
                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    Upload image
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      imageTargetFieldRef.current = 'image';
                      setIsStockImageModalOpen(true);
                    }}
                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    Browse stock images
                  </button>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-slate-700">Category</label>
              <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={block.content.showCategory !== false}
                  onChange={(e) => handleContentChange('showCategory', e.target.checked)}
                />
                Show category
              </label>
            </div>
            {block.content.showCategory !== false && (
              <div>
                <input
                  type="text"
                  value={block.content.category || ''}
                  onChange={(e) => handleContentChange('category', e.target.value)}
                  placeholder="Latest Blog Post"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
              <input
                type="text"
                value={block.content.title || ''}
                onChange={(e) => handleContentChange('title', e.target.value)}
                placeholder="Blog post title"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Excerpt</label>
              <textarea
                value={block.content.excerpt || ''}
                onChange={(e) => handleContentChange('excerpt', e.target.value)}
                placeholder="Blog post excerpt"
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Button Text</label>
                <input
                  type="text"
                  value={block.content.buttonText || ''}
                  onChange={(e) => handleContentChange('buttonText', e.target.value)}
                  placeholder="Read More"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Button URL</label>
                <input
                  type="url"
                  value={block.content.buttonUrl || ''}
                  onChange={(e) => handleContentChange('buttonUrl', e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                />
              </div>
            </div>
          </div>
        );
      }

      case 'faq-section': {
        const faqs: { question?: string; answer?: string; enabled?: boolean }[] =
          Array.isArray(block.content.faqs) && block.content.faqs.length > 0
            ? block.content.faqs
            : [{}, {}, {}];

        const handleFaqChange = (index: number, field: string, value: string | boolean) => {
          const next = [...faqs];
          if (!next[index]) next[index] = {};
          (next[index] as any)[field] = value;
          onUpdate({
            content: {
              ...block.content,
              faqs: next,
            },
          });
        };

        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Section Title</label>
              <input
                type="text"
                value={block.content.sectionTitle || ''}
                onChange={(e) => handleContentChange('sectionTitle', e.target.value)}
                placeholder="Frequently Asked Questions"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>
            <div className="pt-3 border-t border-slate-200">
              <h4 className="text-sm font-semibold text-slate-800 mb-3">FAQs</h4>
              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <div key={index} className="space-y-2 p-3 border border-slate-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-600">FAQ {index + 1}</span>
                      <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          checked={faq.enabled !== false}
                          onChange={(e) => handleFaqChange(index, 'enabled', e.target.checked)}
                        />
                        Show
                      </label>
                    </div>
                    {faq.enabled !== false && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Question</label>
                          <input
                            type="text"
                            value={faq.question || ''}
                            onChange={(e) => handleFaqChange(index, 'question', e.target.value)}
                            placeholder="How do I get started?"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Answer</label>
                          <textarea
                            value={faq.answer || ''}
                            onChange={(e) => handleFaqChange(index, 'answer', e.target.value)}
                            placeholder="Answer to the question"
                            rows={2}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                          />
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }

      case 'team-grid': {
        const members: { name?: string; role?: string; image?: string; enabled?: boolean }[] =
          Array.isArray(block.content.members) && block.content.members.length > 0
            ? block.content.members
            : [{}, {}, {}];

        const handleMemberChange = (index: number, field: string, value: any) => {
          const next = [...members];
          if (!next[index]) next[index] = {};
          (next[index] as any)[field] = value;
          onUpdate({
            content: {
              ...block.content,
              members: next,
            },
          });
        };

        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Section Title</label>
              <input
                type="text"
                value={block.content.sectionTitle || ''}
                onChange={(e) => handleContentChange('sectionTitle', e.target.value)}
                placeholder="Meet Our Team"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
            </div>
            <div className="pt-3 border-t border-slate-200">
              <h4 className="text-sm font-semibold text-slate-800 mb-3">Team Members</h4>
              <div className="space-y-3">
                {members.map((member, index) => (
                  <div key={index} className="space-y-2 p-3 border border-slate-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-600">Member {index + 1}</span>
                      <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          checked={member.enabled !== false}
                          onChange={(e) => handleMemberChange(index, 'enabled', e.target.checked)}
                        />
                        Show
                      </label>
                    </div>
                    {member.enabled !== false && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Photo</label>
                          <div className="space-y-2">
                            <input
                              type="url"
                              value={member.image || ''}
                              onChange={(e) => handleMemberChange(index, 'image', e.target.value)}
                              placeholder="Member photo URL"
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                            />
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  imageTargetFieldRef.current = `member-${index}-image`;
                                  setContentManagerTab('image');
                                  setIsContentManagerOpen(true);
                                }}
                                className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                              >
                                Upload
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  imageTargetFieldRef.current = `member-${index}-image`;
                                  setIsStockImageModalOpen(true);
                                }}
                                className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                              >
                                Stock
                              </button>
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                          <input
                            type="text"
                            value={member.name || ''}
                            onChange={(e) => handleMemberChange(index, 'name', e.target.value)}
                            placeholder="John Doe"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                          <input
                            type="text"
                            value={member.role || ''}
                            onChange={(e) => handleMemberChange(index, 'role', e.target.value)}
                            placeholder="CEO & Founder"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                          />
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  const contentFields = renderContentFields();







  const bodyContent = (isImageBlock || isLogoBlock) ? (
    <div className="space-y-4">{contentFields}</div>
  ) : isTextBlock ? (
    <div className="space-y-6">
      {contentFields}
    </div>
  ) : (
    <div className="space-y-3">
      {/* Content Section */}
      {normalizedBlockType === 'product-rec' ? (
        contentFields
      ) : (
        <CollapsibleSection title="Content" defaultOpen={true}>
          <div className="space-y-6 pt-2">
            {contentFields}
          </div>
        </CollapsibleSection>
      )}

      {block.type === 'footer' && (
        <CollapsibleSection title="Typography" defaultOpen={true}>
          <div className="space-y-6 pt-2">{renderTypographyContent()}</div>
        </CollapsibleSection>
      )}

      {/* Style Section */}
      <CollapsibleSection title="Style" defaultOpen={false}>
        <div className="space-y-6 pt-2">
          {renderStyleContent()}
        </div>
      </CollapsibleSection>

      {/* Layout Section */}
      <CollapsibleSection title="Layout" defaultOpen={false}>
        <div className="space-y-6 pt-2">
          {renderLayoutContent()}
        </div>
      </CollapsibleSection>

      {/* Mobile Responsive Section */}
      <CollapsibleSection title="Mobile Responsive" defaultOpen={false}>
        <div className="space-y-6 pt-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] text-slate-400 font-normal uppercase tracking-wider">Settings for screens &lt; 600px</span>
            {mobileHasOverrides && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  resetMobileStyles();
                }}
                className="text-[10px] text-red-500 hover:text-red-700 font-bold uppercase tracking-wider px-2 py-1 hover:bg-red-50 rounded transition-colors"
              >
                Reset
              </button>
            )}
          </div>
          {renderMobileContent()}
        </div>
      </CollapsibleSection>
    </div>
  );

  return (
    <div className="px-4 pt-4 pb-28">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.1em]">Properties</h3>
          <span className="inline-block bg-black/[0.05] text-slate-600 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded mt-1">
            {block.type}
          </span>
        </div>
        {onDone && (
          <button
            onClick={onDone}
            className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 transition-colors px-2 py-1 hover:bg-black/[0.04] rounded"
          >
            Done
          </button>
        )}
      </div>

      {bodyContent}
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={async (event) => {
          const file = event.target.files?.[0];
          if (!file || !currentUser) {
            event.target.value = '';
            imageTargetFieldRef.current = null;
            return;
          }
          setIsUploadingImage(true);
          try {
            // Get image dimensions before upload
            const imageDimensions = await new Promise<{ width: number; height: number }>((resolve) => {
              const img = new Image();
              const objectUrl = URL.createObjectURL(file);
              img.onload = () => {
                resolve({ width: img.width, height: img.height });
                URL.revokeObjectURL(objectUrl);
              };
              img.onerror = () => {
                resolve({ width: 0, height: 0 });
                URL.revokeObjectURL(objectUrl);
              };
              img.src = objectUrl;
            });

            const uploadedFile = await storageService.uploadFile(
              file,
              currentUser.uid,
              currentWorkspace?.id
            );
            const isHeader = block.type === 'header';
            const logoField = imageTargetFieldRef.current || (isHeader ? 'logoUrl' : 'src');
            
            applyContentUpdate({
              [logoField]: uploadedFile.url,
              alt: uploadedFile.originalName || '',
              autoWidth: !isHeader, // Header doesn't use the standard autoWidth logic
              fileId: uploadedFile.id || getExistingFileId(),
              fileSize: uploadedFile.size,
              imageName: uploadedFile.originalName || uploadedFile.name,
              imageMimeType: uploadedFile.mimeType,
              metadata: {
                ...block.content.metadata,
                width: imageDimensions.width || uploadedFile.metadata?.width,
                height: imageDimensions.height || uploadedFile.metadata?.height,
              },
            });
          } catch (error) {
            console.error('File upload failed:', error);
          } finally {
            setIsUploadingImage(false);
            imageTargetFieldRef.current = null;
            event.target.value = '';
          }
        }}
      />
      <ContentManagementModal
        isOpen={isContentManagerOpen}
        onClose={() => setIsContentManagerOpen(false)}
        onSelectFiles={handleImageSelection}
        selectionMode="single"
        initialTab={contentManagerTab}
      />
      <StockImageModal
        isOpen={isStockImageModalOpen}
        onClose={() => setIsStockImageModalOpen(false)}
        onSelect={handleStockImageSelection}
      />
      <KonvaImageEditor
        isOpen={isImageEditorOpen && Boolean(imageEditorInitialSrc)}
        initialImage={imageEditorInitialSrc || undefined}
        onClose={handleCloseImageEditor}
        onSave={handleImageEditorSave}
        isSaving={isSavingEditedImage}
      />
      <ImageLibraryModal
        isOpen={isImageLibraryOpen}
        onClose={() => setIsImageLibraryOpen(false)}
        onSelectImage={(url) => {
          applyContentUpdate({
            src: url,
            autoWidth: true,
          });
          setIsImageLibraryOpen(false);
        }}
      />
    </div>
  );
};

